/**
 * DOM interactions and browser-specific code
 *
 * API-first architecture:
 * - Public API payloads use the browser HTTP cache
 * - Base row shells cached once per type/freshness
 * - Visible rank/change/trend cells hydrated after Clusterize render
 * - Order-only view cache plus small assembled-view LRU
 * - Mutex locks prevent concurrent operations
 */

import { TEMPLATE_BOOTSTRAP } from "./bootstrap.js";
import { buildCategorySearchIndex, getCategorySearchTreeState } from "./category-search.js";
import {
  getCategoryDisplayMode,
  getColumnsForType,
  getDefaultDirection as getSchemaDefaultDirection,
  getDefaultSortForType as getSchemaDefaultSortForType,
  getSortableColumnsForType,
  setColumnFeatureFlags,
} from "./columns.js";
import { buildCacheKey, clearSortCache, escapeHtml, getSortedIndices, log, matchesSearch, normalizeSearchValue } from "./core.js";
import { buildCsv, buildCsvFilename, escapeCsvCell, formatCsvMedals } from "./csv-export.js";
import {
  buildGatedContextKey,
  buildGatedEnvelope,
  formatCategoryChurnMetrics,
  formatCategoryChurnMetricsHtml,
  getGatedSessionStorageKey,
  isCategoryChurnMetricsValid,
  isGatedCacheEnvelopeValid,
  normalizeEngagementItems,
  normalizeGatedSearchFields,
  normalizeRenderableGatedMetrics,
} from "./gated-metrics-client.js";
import {
  buildEngagementCacheKey,
  clearAllTypePayloads,
  readBaseRowsPayload,
  readEngagementPayload,
  readTypePayload,
  writeBaseRowsPayload,
  writeEngagementPayload,
  writeTypePayload,
} from "./indexeddb-cache.js";
import { renderAllRows, renderCategoryTooltipHtml, renderContextCellsForContext, renderRowRecords } from "./render.js";
import { itemMatchesSearchCommands, parseSearchCommands } from "./search-commands.js";
import { initSparkline } from "./sparklines.js";
import { buildNoResultsRow as buildNoResultsRowForType, mountTableStructure } from "./table-structure.js";

// ============================================
// STATE
// ============================================

// Unified state
const state = {
  // View state
  type: "template", // "template" | "component" | "expert" | "creator"
  tab: "all", // "all", "free", "paid"
  rankingMode: "popular", // "popular", "best30", "best90"
  category: null, // category ID or null
  expertType: null, // marketplace expert type: studio, freelancer, or pro
  search: "", // search query

  // Search field filters
  searchFields: {
    searchRank: true,
    searchChange: true,
    searchPrice: true,
    searchName: true,
    searchAuthor: true,
  },

  // Sort state
  sortColumn: "rank",
  sortDirection: "asc", // "asc" or "desc"

  // Data
  allItems: [], // Raw items from JSON
  marketplaceSources: {}, // Snapshot linkage + totals for source-level delta reconstruction
  allRows: [], // Base row shells: [{html, itemId, categoryCellHtml, index}, ...]
  filteredRows: [], // Currently filtered rows (subset of allRows)
  sortedIndices: [], // Sorted indices into filteredRows

  // Engagement data (likes/comments) loaded lazily per type
  engagementData: null, // Map<string, {likes: number, comments: number}> or null
  engagementLoading: false, // true while engagement fetching is in progress

  // Virtualizer instance
  clusterize: null,
  thumbnailHydrationTimer: null,
  thumbnailHydrationToken: 0,

  // Category metadata
  templateCategoryMeta: {},
  templateCategoryTree: [],
  componentCategoryMeta: {},
  componentCategoryTree: [],
  pluginCategoryMeta: {},
  pluginCategoryTree: [],
  vectorCategoryMeta: {},
  vectorCategoryTree: [],
  siteCategoryMeta: {},
  siteCategoryTree: [],
  tutorialCategoryMeta: {},
  tutorialCategoryTree: [],
  expertCountryMeta: {},
  expertCountryTree: [],
  expertTypeMeta: {},
  expertTypeTree: [],
  expertCountryMetaByType: {},
  expertCountryTreeByType: {},
  // Legacy aliases (template categories)
  categoryMeta: {},
  categoryTree: [],
  footerSummary: null,
  api: {
    enabled: false,
    cache: new Map(),
    baseRowsCache: new Map(),
    viewCache: new Map(),
    assembledViewCache: new Map(),
    categoryTreeCache: new Map(),
    contextCellCache: new Map(),
    contextPopulationCache: new Map(),
    activeTypeKey: null,
    lastUpdated: null,
    lastUpdateMs: null,
    staticFiles: {},
    inFlightByTypeKey: new Map(),
    typeLastUpdated: new Map(),
  },
  gated: {
    cache: new Map(),
    inFlightByContextKey: new Map(),
    lastUpdated: null,
    currentMetrics: null,
  },
  uiFlags: {},
  columnFeatures: {
    topPercentColumnEnabled: true,
  },
  rowLookup: new Map(),
};

const boundDocumentListenerKeys = new Set();

function bindDocumentListener(key, type, listener, options) {
  if (boundDocumentListenerKeys.has(key)) return;
  boundDocumentListenerKeys.add(key);
  document.addEventListener(type, listener, options);
}

// Mutex locks for operations
const locks = {
  sorting: false,
  filtering: false,
  rendering: false,
};

const perfState = {
  enabled: false,
  history: [],
  measures: [],
  maxHistory: 200,
  maxMeasures: 500,
};

const filterCache = new Map();
const FILTER_CACHE_MAX_SIZE = 20;
const BASE_ROWS_CACHE_VERSION = "base-rows-v5";
const VIEW_CACHE_VERSION = "view-v2";
const CATEGORY_TREE_CACHE_VERSION = "category-tree-v2";
const VIEW_CACHE_MAX_SIZE = 10;
const ASSEMBLED_VIEW_CACHE_MAX_SIZE = 3;
const PROGRESSIVE_INITIAL_ROW_COUNT = 500;
function getProgressiveInitialRowCount() {
  if (typeof window === "undefined") return PROGRESSIVE_INITIAL_ROW_COUNT;
  const requested = Number(new URLSearchParams(window.location.search).get("progressiveInitial"));
  return Number.isFinite(requested) && requested >= 1 && requested <= 1000
    ? Math.floor(requested)
    : PROGRESSIVE_INITIAL_ROW_COUNT;
}
// Keep each background batch small enough that scrolling and input remain
// responsive while the full list is hydrated.
// Keep each idle task small enough for input/paint, but avoid thousands of
// tiny tasks for a 10k-row payload. The previous value (5) made full
// hydration take ~40s even though the page remained responsive.
const PROGRESSIVE_CHUNK_SIZE = 20;
const ENGAGEMENT_MEMORY_CACHE_MAX_SIZE = 20;
const engagementMemoryCache = new Map();
const engagementInFlightByKey = new Map();
const GATED_CACHE_MAX_ENTRIES = 100;
let clusterizeLayoutRefreshToken = 0;
const progressiveRenderState = {
  generation: 0,
  active: false,
  nextIndex: 0,
  timer: null,
  typeKey: null,
  lastUpdated: null,
  payloadState: null,
  authRefreshPending: false,
  commitPending: false,
  bootstrapUpdatePending: false,
  renderVersion: 0,
  virtualizerVersion: -1,
  commitTimer: null,
};
const categorySearchState = {
  index: [],
  results: [],
  activeIndex: -1,
  lastQuery: "",
  started: false,
};
const MANAGED_URL_KEYS = new Set(["type", "price", "category", "expertType", "sorting", "direction", "search"]);
const PATH_STATE_QUERY_OVERRIDE_KEYS = new Set(["type", "price", "category", "expertType", "ranking"]);
const MANAGED_QUERY_ONLY_KEYS = new Set(["sorting", "direction", "search"]);
const ITEM_TYPE_OPTIONS = Object.freeze([
  { type: "template", label: "Templates", buttonId: "templates-button" },
  { type: "plugin", label: "Plugins", buttonId: "plugins-button" },
  { type: "component", label: "Components", buttonId: "components-button" },
  { type: "vector", label: "Vectors", buttonId: "vectors-button" },
  { type: "site", label: "Sites", buttonId: "sites-button" },
  { type: "creator", label: "Creators", buttonId: "creators-button" },
  { type: "expert", label: "Experts", buttonId: "experts-button" },
]);
const PRICING_OPTIONS = Object.freeze([
  { tab: "all", label: "Mixed Price" },
  { tab: "free", label: "Free" },
  { tab: "paid", label: "Paid" },
]);
const RANKING_MODE_OPTIONS = Object.freeze([
  { mode: "popular", label: "Trending" },
  { mode: "best30", label: "Last Month" },
  { mode: "best90", label: "Last Quarter" },
]);
const CURATED_TEMPLATE_CATEGORY_NAV = Object.freeze([
  {
    name: "Portfolio",
    id: "portfolio",
    children: [
      ["Artists", "artists"],
      ["Designers", "designers"],
      ["Photography", "photography"],
      ["Resume", "resume"],
    ],
  },
  {
    name: "Software",
    id: "software",
    children: [
      ["AI", "ai"],
      ["Apps", "apps"],
      ["Fintech", "fintech"],
      ["SaaS", "saas"],
    ],
  },
  {
    name: "Agency",
    id: "agency",
    children: [
      ["Architecture", "architecture"],
      ["Design", "design"],
      ["Marketing", "marketing"],
      ["Web", "web-design"],
    ],
  },
  {
    name: "Ecommerce",
    id: "ecommerce",
    children: [
      ["Beauty", "beauty"],
      ["Digital", "digital"],
      ["Fashion", "fashion"],
      ["Furniture", "furniture"],
    ],
  },
  {
    name: "Services",
    id: "professional-services",
    children: [
      ["Consulting", "consulting"],
      ["Education", "education"],
      ["Home", "home-services"],
      ["Legal", "legal"],
    ],
  },
  {
    name: "Health",
    id: "health",
    children: [
      ["Fitness", "fitness"],
      ["Medical", "medical"],
      ["Therapy", "therapy"],
      ["Wellness", "wellness"],
    ],
  },
  {
    name: "Publishing",
    id: "publishing",
    children: [
      ["Blogs", "blogs"],
      ["Entertainment", "entertainment"],
      ["Magazines", "magazines"],
      ["News", "news"],
    ],
  },
  {
    name: "Real Estate",
    id: "real-estate",
    children: [
      ["Agents", "agents"],
      ["Construction", "construction"],
      ["Listings", "listings"],
      ["Management", "management"],
    ],
  },
  {
    name: "Hospitality",
    id: "hospitality",
    children: [
      ["Food", "food"],
      ["Hotels", "hotels"],
      ["Restaurants", "restaurants"],
      ["Travel", "travel"],
    ],
  },
  {
    name: "Events",
    id: "events",
    children: [
      ["Conferences", "conferences"],
      ["Music", "music"],
      ["Sports", "sports"],
      ["Weddings", "weddings"],
    ],
  },
]);
const RANKING_MODE_TYPES = new Set(["template", "component", "plugin", "vector"]);
const THEME_STORAGE_KEY = "ranks_theme";
const THEME_CHOICES = new Set(["dark", "light"]);
const THEME_CYCLE_ORDER = ["dark", "light"];
const DEFAULT_THEME_CHOICE = "dark";
const FALLBACK_EFFECTIVE_THEME = "dark";
const NON_CREATOR_TYPES = new Set(["template", "component", "plugin", "vector", "site", "tutorial", "expert"]);
const TYPE_CAPABILITIES = {
  template: {
    supportsPricing: true,
    categoryLabel: "Template Categories",
    typeLabel: "Templates",
  },
  component: {
    supportsPricing: true,
    categoryLabel: "Component Categories",
    typeLabel: "Components",
  },
  plugin: { supportsPricing: true, categoryLabel: "Plugin Categories", typeLabel: "Plugins" },
  vector: { supportsPricing: true, categoryLabel: "Vector Categories", typeLabel: "Vectors" },
  site: { supportsPricing: false, categoryLabel: "Site Categories", typeLabel: "Sites" },
  tutorial: {
    supportsPricing: false,
    categoryLabel: "Tutorial Categories",
    typeLabel: "Tutorials",
  },
  creator: { supportsPricing: false, categoryLabel: "Categories", typeLabel: "Creators" },
  expert: { supportsPricing: false, categoryLabel: "All Countries", typeLabel: "Experts" },
};

function getVirtualizerScroller() {
  return document.getElementById("clusterize-scroll");
}

function scrollVirtualizerToTop() {
  const scroll = getVirtualizerScroller();
  if (scroll) scroll.scrollTop = 0;
}
const URL_TYPE_ALIASES = {
  template: "templates",
  templates: "templates",
  component: "components",
  components: "components",
  plugin: "plugins",
  plugins: "plugins",
  vector: "vectors",
  vectors: "vectors",
  site: "sites",
  sites: "sites",
  tutorial: "tutorials",
  tutorials: "tutorials",
  creator: "creators",
  creators: "creators",
  expert: "experts",
  experts: "experts",
};
const URL_PATH_TYPE_ALIASES = {
  template: "templates",
  templates: "templates",
  component: "components",
  components: "components",
  plugin: "plugins",
  plugins: "plugins",
  vector: "vectors",
  vectors: "vectors",
  site: "sites",
  sites: "sites",
  tutorial: "tutorials",
  tutorials: "tutorials",
  creator: "creators",
  creators: "creators",
  expert: "experts",
  experts: "experts",
};
const URL_TYPE_TO_STATE = {
  templates: "template",
  components: "component",
  plugins: "plugin",
  vectors: "vector",
  sites: "site",
  tutorials: "tutorial",
  creators: "creator",
  experts: "expert",
};
const STATE_TO_URL_TYPE = {
  template: "templates",
  component: "components",
  plugin: "plugins",
  vector: "vectors",
  site: "sites",
  tutorial: "tutorials",
  creator: "creators",
  expert: "experts",
};
const DEFAULT_STATE = {
  type: "template",
  tab: "all",
  rankingMode: "popular",
  sortColumn: "rank",
  sortDirection: "asc",
  category: null,
  search: "",
};
const API_SESSION_CACHE_PREFIX = "ranks_api_v2_context__";
const LAST_UPDATED_STORAGE_KEY = "ranks_last_updated_ms";
const LAST_UPDATED_TIMEOUT_MS = 5000;
let isApplyingUrlState = false;
let onThemeStorageChange = null;
let lastThemePointerInteractionTs = Number.NEGATIVE_INFINITY;
const THEME_INTERACTION_DEDUPE_MS = 400;
const CONTEXT_TRANSITION_OUT_CLASS = "ranks-context-switching-out";
const CONTEXT_TRANSITION_IN_CLASS = "ranks-context-switching-in";
const CONTEXT_TRANSITION_OUT_MS = 140;
const CONTEXT_TRANSITION_IN_MS = 160;
const CONTEXT_TRANSITION_TIMEOUT_BUFFER_MS = 120;
const CONTEXT_CELL_CACHE_VERSION = "context-cells-v3";
const CONTEXT_CELL_SELECTORS = [
  ["rank", ".rank-cell"],
  ["change", ".change-cell"],
  ["toppercent", ".toppercent-cell"],
  ["trend", ".trend-cell"],
  ["category", ".categories-cell"],
];
const CONTEXT_FADE_TARGET_SELECTORS = [
  "#table-title-main",
  "#table-title-subtitle .subtitle-pricing",
  "#table-title-subtitle .subtitle-count",
  "#table-title-subtitle #gated-churn-subtitle",
  ".gated-download-csv-tooltip",
  "#clusterize-content",
  "#rankings-tfoot",
];
const contextTransitionState = {
  token: 0,
  pendingType: null,
};
const JUMP_HIGHLIGHT_CLASS = "row-jump-highlight";
const CELL_HIGHLIGHT_CLASS = "cell-jump-highlight";
const SORT_COLUMN_HIGHLIGHT_CLASS = "sort-column-highlight";
const JUMP_HIGHLIGHT_DURATION_MS = 4800;
const CELL_HIGHLIGHT_DURATION_MS = JUMP_HIGHLIGHT_DURATION_MS / 2;
const SORT_COLUMN_HIGHLIGHT_DURATION_MS = CELL_HIGHLIGHT_DURATION_MS / 2;
let jumpHighlightClearTimer = null;
let cellHighlightClearTimer = null;
let sortColumnHighlightClearTimer = null;
let cellHighlightFrameId = null;
let cellHighlightToken = 0;
const SEARCH_COMMAND_HIGHLIGHT_CLASS = "search-command-highlight";
const SEARCH_COMMAND_HIGHLIGHT_DURATION_MS = 2400;
let searchCommandHighlightClearTimer = null;

function setSearchCommandHintVisible(visible) {
  const hint = document.getElementById("search-command-hint");
  if (!hint) return;
  hint.classList.toggle("is-visible", visible);
  hint.setAttribute("aria-hidden", visible ? "false" : "true");
}

function isJsdomEnvironment() {
  return typeof navigator !== "undefined" && /jsdom/i.test(String(navigator.userAgent || ""));
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function shouldSkipContextTransitions() {
  return isJsdomEnvironment() || prefersReducedMotion();
}

function getContextFadeTargets() {
  if (typeof document === "undefined") return [];
  const targets = CONTEXT_FADE_TARGET_SELECTORS.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
  return [...new Set(targets)];
}

function cssEscape(value) {
  const target = String(value ?? "");
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(target);
  return target.replace(/["\\]/g, "\\$&");
}

function clearContextTransitionClasses() {
  if (typeof document === "undefined") return;
  document.body?.classList.remove(CONTEXT_TRANSITION_OUT_CLASS);
  document.body?.classList.remove(CONTEXT_TRANSITION_IN_CLASS);
}

export function highlightJumpTargetRow(itemId, durationMs = JUMP_HIGHLIGHT_DURATION_MS) {
  return highlightJumpTargetRows([itemId], durationMs);
}

function highlightJumpTargetRows(itemIds, durationMs = JUMP_HIGHLIGHT_DURATION_MS) {
  const targetIds = Array.isArray(itemIds) ? itemIds.map((itemId) => String(itemId || "").trim()).filter(Boolean) : [];
  if (targetIds.length === 0 || typeof document === "undefined") return false;
  const rows = targetIds
    .map((targetId) => {
      const escapedTargetId = cssEscape(targetId);
      return document.querySelector(`#clusterize-content tr[data-id="${escapedTargetId}"]`);
    })
    .filter(Boolean);
  if (rows.length === 0) return false;

  document.querySelectorAll(`#clusterize-content tr.${JUMP_HIGHLIGHT_CLASS}`).forEach((el) => {
    el.classList.remove(JUMP_HIGHLIGHT_CLASS);
  });
  rows.forEach((row) => {
    row.classList.add(JUMP_HIGHLIGHT_CLASS);
  });

  if (jumpHighlightClearTimer) clearTimeout(jumpHighlightClearTimer);
  jumpHighlightClearTimer = setTimeout(
    () => {
      rows.forEach((row) => {
        row.classList.remove(JUMP_HIGHLIGHT_CLASS);
      });
    },
    Math.max(300, Number(durationMs) || JUMP_HIGHLIGHT_DURATION_MS),
  );
  return true;
}

function clearCellHighlights() {
  if (typeof document === "undefined") return;
  document.querySelectorAll(`#clusterize-content .${CELL_HIGHLIGHT_CLASS}`).forEach((el) => {
    el.classList.remove(CELL_HIGHLIGHT_CLASS);
  });
}

function cancelPendingCellHighlightFrame() {
  if (cellHighlightFrameId === null) return;
  if (typeof cancelAnimationFrame === "function") {
    cancelAnimationFrame(cellHighlightFrameId);
  }
  cellHighlightFrameId = null;
}

function beginCellHighlightCycle() {
  cellHighlightToken += 1;
  cancelPendingCellHighlightFrame();
  return cellHighlightToken;
}

function addCellHighlights(cells, durationMs = CELL_HIGHLIGHT_DURATION_MS) {
  const targets = Array.from(cells || []).filter(Boolean);
  const highlightToken = cellHighlightToken;
  clearCellHighlights();
  if (cellHighlightClearTimer) {
    clearTimeout(cellHighlightClearTimer);
    cellHighlightClearTimer = null;
  }
  if (targets.length === 0) return false;
  targets.forEach((cell) => {
    cell.classList.add(CELL_HIGHLIGHT_CLASS);
  });
  const clearTimer = setTimeout(
    () => {
      if (highlightToken !== cellHighlightToken) return;
      cancelPendingCellHighlightFrame();
      clearCellHighlights();
      if (cellHighlightClearTimer === clearTimer) {
        cellHighlightClearTimer = null;
      }
    },
    Math.max(300, Number(durationMs) || CELL_HIGHLIGHT_DURATION_MS),
  );
  cellHighlightClearTimer = clearTimer;
  return true;
}

function highlightChangeCellsForItemIds(itemIds, durationMs = CELL_HIGHLIGHT_DURATION_MS) {
  const targetIds = Array.isArray(itemIds) ? itemIds.map((itemId) => String(itemId || "").trim()).filter(Boolean) : [];
  if (targetIds.length === 0 || typeof document === "undefined") return false;
  const cells = targetIds
    .map((targetId) => {
      const escapedTargetId = cssEscape(targetId);
      return document.querySelector(`#clusterize-content tr[data-id="${escapedTargetId}"] .change-cell`);
    })
    .filter(Boolean);
  return addCellHighlights(cells, durationMs);
}

function highlightVisibleChangeCells(durationMs = CELL_HIGHLIGHT_DURATION_MS) {
  if (typeof document === "undefined") return false;
  beginCellHighlightCycle();
  return addCellHighlights(document.querySelectorAll("#clusterize-content tr[data-id] .change-cell"), durationMs);
}

function highlightSortColumnHeader(column, durationMs = SORT_COLUMN_HIGHLIGHT_DURATION_MS) {
  if (!column || typeof document === "undefined") return false;
  const escapedColumn = cssEscape(column);
  const header = document.querySelector(`th.sortable[data-column="${escapedColumn}"]`);
  if (!header) return false;
  document.querySelectorAll(`th.${SORT_COLUMN_HIGHLIGHT_CLASS}`).forEach((el) => {
    el.classList.remove(SORT_COLUMN_HIGHLIGHT_CLASS);
  });
  header.classList.add(SORT_COLUMN_HIGHLIGHT_CLASS);
  if (sortColumnHighlightClearTimer) clearTimeout(sortColumnHighlightClearTimer);
  sortColumnHighlightClearTimer = setTimeout(
    () => {
      header.classList.remove(SORT_COLUMN_HIGHLIGHT_CLASS);
    },
    Math.max(300, Number(durationMs) || SORT_COLUMN_HIGHLIGHT_DURATION_MS),
  );
  return true;
}

function resetToDefaultSortForCurrentType() {
  const defaultsForType = getDefaultSortForType(state.type);
  state.sortColumn = defaultsForType.sortColumn;
  state.sortDirection = defaultsForType.sortDirection;
}

function applySortForSearchCommand(command) {
  const normalizedCommand = String(command || "")
    .trim()
    .toLowerCase();
  const firstNumericCommand = parseSearchCommands(normalizedCommand).commands.find((item) => item.kind === "numeric");
  if (firstNumericCommand?.field === "delta" && ["<", "<="].includes(firstNumericCommand.op)) {
    state.sortColumn = "change";
    state.sortDirection = "asc";
    return state.sortColumn;
  }
  if (firstNumericCommand?.field === "delta") {
    state.sortColumn = "change";
    state.sortDirection = "desc";
    return state.sortColumn;
  }
  if (state.type === "creator" && firstNumericCommand?.field) {
    state.sortColumn = firstNumericCommand.field;
    state.sortDirection = ["<", "<="].includes(firstNumericCommand.op) ? "asc" : "desc";
    return state.sortColumn;
  }
  resetToDefaultSortForCurrentType();
  return state.sortColumn;
}

function getTopVisibleItemIds(count) {
  const limit = Math.max(1, Number(count) || 1);
  return getOrderedRowsForCurrentView()
    .slice(0, limit)
    .map((row) => String(getRowItem(row)?.itemId ?? row?.itemId ?? ""))
    .filter(Boolean);
}

function applyRankHighlightMetric(count, options = {}) {
  const { refreshView = true } = options;
  const highlightToken = beginCellHighlightCycle();
  if (refreshView) {
    resetToDefaultSortForCurrentType();
    highlightSortColumnHeader(state.sortColumn);
    refreshSync();
    refreshGatedMetricsForVisibleList();
  }
  const highlight = () => {
    if (highlightToken !== cellHighlightToken) return false;
    const itemIds = getTopVisibleItemIds(count);
    scrollVirtualizerToTop();
    return highlightChangeCellsForItemIds(itemIds);
  };
  highlight();
  cellHighlightFrameId = requestAnimationFrame(() => {
    cellHighlightFrameId = null;
    highlight();
  });
}

function shouldHighlightAllVisibleChangeCellsForCommandTarget(target) {
  if (!target?.matches) return false;
  return target.matches(".churn-metrics__metric--moved-up, .churn-metrics__metric--total-rank-gain");
}

function highlightSearchInput() {
  const searchInput = document.getElementById("rankings-search");
  if (!searchInput) return false;
  const searchContainer = searchInput.closest(".search-container");
  if (!searchContainer) return false;
  searchContainer.classList.remove(SEARCH_COMMAND_HIGHLIGHT_CLASS);
  void searchContainer.offsetWidth;
  searchContainer.classList.add(SEARCH_COMMAND_HIGHLIGHT_CLASS);
  if (searchCommandHighlightClearTimer) clearTimeout(searchCommandHighlightClearTimer);
  searchCommandHighlightClearTimer = setTimeout(() => {
    searchContainer.classList.remove(SEARCH_COMMAND_HIGHLIGHT_CLASS);
  }, SEARCH_COMMAND_HIGHLIGHT_DURATION_MS);
  return true;
}

function clearSearchInputHighlight() {
  const searchInput = document.getElementById("rankings-search");
  const searchContainer = searchInput?.closest(".search-container");
  searchContainer?.classList.remove(SEARCH_COMMAND_HIGHLIGHT_CLASS);
  if (searchCommandHighlightClearTimer) {
    clearTimeout(searchCommandHighlightClearTimer);
    searchCommandHighlightClearTimer = null;
  }
}

function waitForOpacityTransitions(targets, timeoutMs) {
  if (!Array.isArray(targets) || targets.length === 0) return Promise.resolve();
  return new Promise((resolve) => {
    let remaining = targets.length;
    let finished = false;
    const cleanups = [];

    const finish = () => {
      if (finished) return;
      finished = true;
      cleanups.forEach((cleanup) => {
        cleanup();
      });
      resolve();
    };

    const timer = window.setTimeout(finish, timeoutMs);
    cleanups.push(() => window.clearTimeout(timer));

    targets.forEach((target) => {
      const onTransitionEnd = (event) => {
        if (event.target !== target || event.propertyName !== "opacity") return;
        remaining -= 1;
        if (remaining <= 0) finish();
      };
      target.addEventListener("transitionend", onTransitionEnd);
      cleanups.push(() => target.removeEventListener("transitionend", onTransitionEnd));
    });
  });
}

function waitForNextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function runContextTransition(applyContextUpdate) {
  if (typeof applyContextUpdate !== "function") return Promise.resolve();
  const token = ++contextTransitionState.token;

  if (shouldSkipContextTransitions()) {
    clearContextTransitionClasses();
    return Promise.resolve(applyContextUpdate());
  }

  const targets = getContextFadeTargets();

  return (async () => {
    try {
      clearContextTransitionClasses();
      document.body?.classList.add(CONTEXT_TRANSITION_OUT_CLASS);
      await waitForOpacityTransitions(targets, CONTEXT_TRANSITION_OUT_MS + CONTEXT_TRANSITION_TIMEOUT_BUFFER_MS);

      if (token !== contextTransitionState.token) return;
      const updateResult = applyContextUpdate();
      if (updateResult && typeof updateResult.then === "function") {
        await updateResult;
      }

      if (token !== contextTransitionState.token) return;
      const updatedTargets = getContextFadeTargets();
      await waitForNextFrame();
      if (token !== contextTransitionState.token) return;

      document.body?.classList.remove(CONTEXT_TRANSITION_OUT_CLASS);
      document.body?.classList.add(CONTEXT_TRANSITION_IN_CLASS);
      await waitForOpacityTransitions(updatedTargets, CONTEXT_TRANSITION_IN_MS + CONTEXT_TRANSITION_TIMEOUT_BUFFER_MS);
    } finally {
      if (token === contextTransitionState.token) {
        clearContextTransitionClasses();
      }
    }
  })();
}

function cancelPendingContextTransition() {
  contextTransitionState.token += 1;
  contextTransitionState.pendingType = null;
  clearContextTransitionClasses();
  document.body?.classList.remove("ranks-type-switching");
}

export function normalizeThemePreference(rawValue) {
  const normalized = String(rawValue || "")
    .trim()
    .toLowerCase();
  return THEME_CHOICES.has(normalized) ? normalized : DEFAULT_THEME_CHOICE;
}

export function resolveEffectiveTheme(preference, _mediaQueryList = null, fallback = FALLBACK_EFFECTIVE_THEME) {
  const normalizedPreference = normalizeThemePreference(preference);
  if (normalizedPreference === "dark" || normalizedPreference === "light") return normalizedPreference;
  return fallback;
}

export function readThemePreference() {
  if (typeof window === "undefined") return DEFAULT_THEME_CHOICE;
  try {
    const stored = window.localStorage?.getItem(THEME_STORAGE_KEY);
    return normalizeThemePreference(stored);
  } catch (_error) {
    return DEFAULT_THEME_CHOICE;
  }
}

export function writeThemePreference(preference) {
  if (typeof window === "undefined") return;
  const normalizedPreference = normalizeThemePreference(preference);
  try {
    window.localStorage?.setItem(THEME_STORAGE_KEY, normalizedPreference);
  } catch (_error) {
    // no-op: blocked storage should not break runtime theme updates
  }
}

export function applyTheme(effectiveTheme, preference = DEFAULT_THEME_CHOICE) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const normalizedEffective = effectiveTheme === "light" ? "light" : "dark";
  root.setAttribute("data-theme", normalizedEffective);
  root.setAttribute("data-theme-preference", normalizeThemePreference(preference));
}

function getSortColumnsForType(type) {
  return getSortableColumnsForType(type);
}

function getDefaultSortForType(type) {
  return getSchemaDefaultSortForType(type);
}

function getDefaultDirection(type, column) {
  return getSchemaDefaultDirection(type, String(column || ""));
}

function normalizeCanonicalType(rawType) {
  const normalized = String(rawType || "")
    .trim()
    .toLowerCase();
  return URL_TYPE_ALIASES[normalized] || null;
}

function normalizePriceParam(rawPrice) {
  const normalized = String(rawPrice || "")
    .trim()
    .toLowerCase();
  if (normalized === "mixedprice") return "all";
  if (normalized === "free" || normalized === "paid" || normalized === "all") return normalized;
  return null;
}

function normalizePathPriceParam(rawPrice) {
  const normalized = String(rawPrice || "")
    .trim()
    .toLowerCase();
  if (normalized === "mixedprice") return "all";
  if (normalized === "free" || normalized === "paid") return normalized;
  return null;
}

function normalizeRankingMode(rawMode) {
  const normalized = String(rawMode || "")
    .trim()
    .toLowerCase();
  return RANKING_MODE_OPTIONS.some((option) => option.mode === normalized) ? normalized : "popular";
}

function typeSupportsRankingMode(type) {
  return RANKING_MODE_TYPES.has(type);
}

function normalizeDirectionParam(rawDirection) {
  const normalized = String(rawDirection || "")
    .trim()
    .toLowerCase();
  if (normalized === "asc" || normalized === "desc") return normalized;
  return null;
}

function getTypeCapabilities(type) {
  return TYPE_CAPABILITIES[type] || TYPE_CAPABILITIES.template;
}

function typeSupportsPricing(type) {
  return getTypeCapabilities(type).supportsPricing === true;
}

function getDefaultTabForType(_type) {
  return "all";
}

function shouldHidePricingTabForType(_type, _tabName) {
  return false;
}

function getVisiblePricingOptionsForCurrentContext() {
  if (!typeSupportsPricing(state.type)) return [];
  const pricingDisabled = shouldDisablePricingTabsForCurrentContext();
  return PRICING_OPTIONS.filter(({ tab }) => {
    if (shouldHidePricingTabForType(state.type, tab)) return false;
    if (pricingDisabled && (tab === "free" || tab === "paid")) return false;
    return true;
  });
}

function getCategoryMetaForType(type, options = {}) {
  if (type === "component") return options.componentCategoryMeta || {};
  if (type === "plugin") return options.pluginCategoryMeta || {};
  if (type === "vector") return options.vectorCategoryMeta || {};
  if (type === "site") return options.siteCategoryMeta || {};
  if (type === "tutorial") return options.tutorialCategoryMeta || {};
  if (type === "expert") return options.expertType ? options.expertCountryMetaByType?.[options.expertType] || {} : options.expertCountryMeta || {};
  return options.templateCategoryMeta || options.categoryMeta || {};
}

function getCategoryTreeForType(type, options = {}) {
  if (type === "component") return options.componentCategoryTree || [];
  if (type === "plugin") return options.pluginCategoryTree || [];
  if (type === "vector") return options.vectorCategoryTree || [];
  if (type === "site") return options.siteCategoryTree || [];
  if (type === "tutorial") return options.tutorialCategoryTree || [];
  if (type === "expert") return options.expertType ? options.expertCountryTreeByType?.[options.expertType] || [] : options.expertCountryTree || [];
  return options.templateCategoryTree || options.categoryTree || [];
}

function getUrlResolutionOptions(currentState = state) {
  return {
    templateCategoryMeta: currentState.templateCategoryMeta,
    componentCategoryMeta: currentState.componentCategoryMeta,
    pluginCategoryMeta: currentState.pluginCategoryMeta,
    vectorCategoryMeta: currentState.vectorCategoryMeta,
    siteCategoryMeta: currentState.siteCategoryMeta,
    siteCategoryTree: currentState.siteCategoryTree,
    tutorialCategoryMeta: currentState.tutorialCategoryMeta,
    expertCountryMeta: currentState.expertCountryMeta,
    expertTypeMeta: currentState.expertTypeMeta,
    expertCountryMetaByType: currentState.expertCountryMetaByType,
    expertType: currentState.expertType,
    categoryMeta: currentState.categoryMeta,
  };
}

export function resolveUrlStateFromSearchParams(searchParams, options = {}) {
  const normalizedParams =
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || "");
  const canonicalType = normalizeCanonicalType(normalizedParams.get("type")) || STATE_TO_URL_TYPE[DEFAULT_STATE.type];
  const type = URL_TYPE_TO_STATE[canonicalType] || DEFAULT_STATE.type;
  const defaultsForType = getDefaultSortForType(type);
  const sortColumns = getSortColumnsForType(type);

  const requestedPrice = normalizePriceParam(normalizedParams.get("price"));
  const requestedSorting = String(normalizedParams.get("sorting") || "")
    .trim()
    .toLowerCase();
  const requestedDirection = normalizeDirectionParam(normalizedParams.get("direction"));
  const requestedCategory = String(normalizedParams.get("category") || "")
    .trim()
    .toLowerCase();
  const requestedSearch = String(normalizedParams.get("search") || "").trim();
  const requestedExpertType = String(normalizedParams.get("expertType") || "").trim().toLowerCase();
  const supportsPricing = typeSupportsPricing(type);

  const nextState = {
    type,
    tab: supportsPricing ? requestedPrice || getDefaultTabForType(type) : "all",
    rankingMode: typeSupportsRankingMode(type) ? normalizeRankingMode(normalizedParams.get("ranking")) : "popular",
    category: null,
    expertType: null,
    search: requestedSearch,
    sortColumn: defaultsForType.sortColumn,
    sortDirection: defaultsForType.sortDirection,
  };

  if (requestedSorting && sortColumns.has(requestedSorting)) {
    nextState.sortColumn = requestedSorting;
  }
  nextState.sortDirection = requestedDirection || getDefaultDirection(type, nextState.sortColumn);

  if (type === "expert" && requestedExpertType && Object.hasOwn(options.expertTypeMeta || {}, `type-${requestedExpertType}`)) {
    nextState.expertType = requestedExpertType;
  }
  const categoryMetaForType = getCategoryMetaForType(type, { ...options, expertType: nextState.expertType });
  if (requestedCategory && Object.hasOwn(categoryMetaForType, requestedCategory)) {
    nextState.category = requestedCategory;
  }

  if (type === "creator") {
    nextState.category = null;
    nextState.expertType = null;
    nextState.tab = "all";
  } else {
    if (!supportsPricing) nextState.tab = "all";
    if (!typeSupportsRankingMode(type)) nextState.rankingMode = "popular";
  }

  return nextState;
}

function normalizePath(pathname) {
  const raw = typeof pathname === "string" ? pathname : "/";
  const normalized = raw.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.slice(0, -1);
  }
  return normalized || "/";
}

function parseStatePath(pathname) {
  const normalizedPath = normalizePath(pathname);
  const segments = normalizedPath
    .split("/")
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);
  if (segments.length === 0) return null;

  const canonicalType = URL_PATH_TYPE_ALIASES[segments[0]] || null;
  if (!canonicalType) return null;

  if (canonicalType === "creators" || canonicalType === "experts") {
    return { type: canonicalType, malformed: segments.length > 1 };
  }

  const [, ...rest] = segments;
  const modeSegment = rest[0];
  const mode = modeSegment
    ? modeSegment === "lastmonth"
      ? "best30"
      : modeSegment === "lastquarter"
        ? "best90"
        : ["best30", "best90", "popular"].includes(modeSegment)
          ? normalizeRankingMode(rest.shift())
          : "popular"
    : "popular";
  if (modeSegment === "lastmonth" || modeSegment === "lastquarter") rest.shift();
  if (rest.length === 0) {
    return { type: canonicalType, rankingMode: mode };
  }

  if (rest.length === 1) {
    const [partA] = rest;
    if (normalizePathPriceParam(partA)) return { type: canonicalType, price: partA, rankingMode: mode };
    return { type: canonicalType, category: partA, rankingMode: mode };
  }

  if (rest.length === 2) {
    const [partA, partB] = rest;
    const priceB = normalizePathPriceParam(partB);
    if (priceB) return { type: canonicalType, category: partA, price: partB, rankingMode: mode };

    return { type: canonicalType, malformed: true };
  }

  return { type: canonicalType, malformed: true };
}

export function resolveUrlStateFromLocation(input = {}, options = {}) {
  const pathname = typeof input.pathname === "string" ? input.pathname : "/";
  const searchParams =
    input.searchParams instanceof URLSearchParams
      ? input.searchParams
      : new URLSearchParams(typeof input.search === "string" ? input.search : "");

  const pathState = parseStatePath(pathname);
  if (!pathState) {
    return resolveUrlStateFromSearchParams(searchParams, options);
  }

  const mergedParams = new URLSearchParams(searchParams);
  const requestedExpertCategory = pathState.type === "experts" ? mergedParams.get("category") : null;
  const requestedExpertType = pathState.type === "experts" ? mergedParams.get("expertType") : null;
  for (const key of PATH_STATE_QUERY_OVERRIDE_KEYS) {
    mergedParams.delete(key);
  }
  mergedParams.set("type", pathState.type);
  mergedParams.set("ranking", pathState.rankingMode || "popular");
  if (pathState.type === "creators") {
    mergedParams.delete("price");
    mergedParams.delete("category");
  } else if (pathState.type === "experts") {
    mergedParams.delete("price");
    if (requestedExpertCategory) {
      mergedParams.set("category", requestedExpertCategory);
    }
    if (requestedExpertType) mergedParams.set("expertType", requestedExpertType);
  } else {
    mergedParams.set("price", pathState.price || "");
    if (pathState.category) {
      mergedParams.set("category", pathState.category);
    } else {
      mergedParams.delete("category");
    }
  }

  return resolveUrlStateFromSearchParams(mergedParams, options);
}

function shouldEmitParam(key, value, currentState) {
  if (value === null || value === undefined || value === "") return false;
  if (!MANAGED_QUERY_ONLY_KEYS.has(key)) return false;
  if (key === "sorting") {
    return value !== getDefaultSortForType(currentState.type).sortColumn;
  }
  if (key === "direction") {
    return value !== getDefaultDirection(currentState.type, currentState.sortColumn);
  }
  return true;
}

export function serializeStateToCanonicalPath(currentState) {
  const rawType = String(currentState?.type || "").toLowerCase();
  const normalizedType = URL_TYPE_TO_STATE[URL_TYPE_ALIASES[rawType] || ""] || currentState.type || "template";
  const typeValue = STATE_TO_URL_TYPE[normalizedType] || "templates";
  if (typeValue === "creators") return "/creators";
  if (typeValue === "experts") return "/experts";

  const normalizedTab = String(currentState.tab || "all").toLowerCase();
  const priceValue = normalizedTab === "all" ? "mixedprice" : normalizedTab;
  const categoryValue = currentState.category ? String(currentState.category).toLowerCase() : "";
  const includePriceSegment = typeSupportsPricing(normalizedType) && typeValue !== "tutorials" && typeValue !== "sites";
  const rankingMode = typeSupportsRankingMode(normalizedType)
    ? normalizeRankingMode(currentState.rankingMode)
    : "popular";
  const modeSegment = rankingMode === "popular" ? "" : rankingMode === "best30" ? "/lastmonth" : "/lastquarter";
  if (categoryValue) {
    return includePriceSegment
      ? `/${typeValue}${modeSegment}/${categoryValue}/${priceValue}`
      : `/${typeValue}${modeSegment}/${categoryValue}`;
  }
  if (priceValue === "mixedprice") {
    return rankingMode === "popular" && typeValue === "templates" ? "/" : `/${typeValue}${modeSegment}`;
  }
  return includePriceSegment ? `/${typeValue}${modeSegment}/${priceValue}` : `/${typeValue}${modeSegment}`;
}

export function serializeStateToCanonicalQuery(currentState, baseSearchParams = null) {
  const rawType = String(currentState?.type || "").toLowerCase();
  const normalizedType = URL_TYPE_TO_STATE[URL_TYPE_ALIASES[rawType] || ""] || currentState.type || "template";
  const base =
    baseSearchParams instanceof URLSearchParams ? baseSearchParams : new URLSearchParams(baseSearchParams || "");
  const next = new URLSearchParams(base);
  for (const key of MANAGED_URL_KEYS) next.delete(key);
  next.delete("period");
  next.delete("ranking");

  const sortingValue = String(
    currentState.sortColumn || getDefaultSortForType(currentState.type).sortColumn,
  ).toLowerCase();
  const directionValue = String(
    currentState.sortDirection || getDefaultDirection(currentState.type, sortingValue),
  ).toLowerCase();
  const searchValue = String(currentState.search || "").trim();

  const managed = {
    sorting: sortingValue,
    direction: directionValue,
    search: searchValue,
  };

  for (const [key, value] of Object.entries(managed)) {
    if (shouldEmitParam(key, value, currentState)) {
      next.set(key, value);
    }
  }
  if (normalizedType === "expert" && currentState.category) {
    next.set("category", String(currentState.category).toLowerCase());
  }
  if (normalizedType === "expert" && currentState.expertType) {
    next.set("expertType", String(currentState.expertType).toLowerCase());
  }
  return next;
}

export function serializeStateToCanonicalParams(currentState, baseSearchParams = null) {
  return serializeStateToCanonicalQuery(currentState, baseSearchParams);
}

function syncUrlWithState() {
  if (typeof window === "undefined" || isApplyingUrlState) return;
  const rawPath = window.location.pathname || "/";
  const currentPath = normalizePath(rawPath);
  const current = new URLSearchParams(window.location.search);
  const canonicalPath = serializeStateToCanonicalPath(state);
  const canonicalQuery = serializeStateToCanonicalQuery(state, current);
  const nextQuery = canonicalQuery.toString();
  const currentQuery = current.toString();
  if (canonicalPath === currentPath && nextQuery === currentQuery && rawPath === canonicalPath) return;
  const nextUrl = `${canonicalPath}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash || ""}`;
  window.history.replaceState({}, "", nextUrl);
}

function updateThemeSwitchUi(preference) {
  const normalizedPreference = normalizeThemePreference(preference);
  const switchRoot = document.getElementById("theme-switch");
  if (!switchRoot) return;
  switchRoot.setAttribute("data-theme-choice", normalizedPreference);
  const buttons = switchRoot.querySelectorAll(".theme-switch-btn");
  buttons.forEach((button) => {
    const choice = button.getAttribute("data-theme-choice");
    const isActive = choice === normalizedPreference;
    button.classList.toggle("active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
  const cycleButton = document.getElementById("theme-btn-cycle");
  if (cycleButton) {
    cycleButton.setAttribute("data-theme-choice", normalizedPreference);
    const nextPreference = getNextThemePreference(normalizedPreference);
    const label = `Theme: ${normalizedPreference}. Tap to switch to ${nextPreference}.`;
    cycleButton.setAttribute("aria-label", label);
    cycleButton.setAttribute("title", label);
  }
}

function getNextThemePreference(preference) {
  const normalized = normalizeThemePreference(preference);
  const index = THEME_CYCLE_ORDER.indexOf(normalized);
  const nextIndex = index < 0 ? 0 : (index + 1) % THEME_CYCLE_ORDER.length;
  return THEME_CYCLE_ORDER[nextIndex];
}

function setThemePreference(preference) {
  const normalizedPreference = normalizeThemePreference(preference);
  writeThemePreference(normalizedPreference);
  const effectiveTheme = resolveEffectiveTheme(normalizedPreference);
  applyTheme(effectiveTheme, normalizedPreference);
  updateThemeSwitchUi(normalizedPreference);
}

function handleThemeSwitchInteraction(event) {
  const clicked = event?.target instanceof Element ? event.target : null;
  if (!clicked) return false;
  const cycleTarget = clicked.closest(".theme-cycle-btn");
  if (cycleTarget) {
    const currentPreference = normalizeThemePreference(readThemePreference());
    const nextPreference = getNextThemePreference(currentPreference);
    setThemePreference(nextPreference);
    trackEvent("theme_changed", { theme: nextPreference, control: "cycle" });
    return true;
  }
  const target = clicked.closest(".theme-switch-btn");
  if (!target) return false;
  const nextPreference = normalizeThemePreference(target.getAttribute("data-theme-choice"));
  setThemePreference(nextPreference);
  trackEvent("theme_changed", { theme: nextPreference, control: target.id || "button" });
  return true;
}

function initThemeController() {
  lastThemePointerInteractionTs = Number.NEGATIVE_INFINITY;
  const preference = readThemePreference();
  const effectiveTheme = resolveEffectiveTheme(preference);
  applyTheme(effectiveTheme, preference);
  updateThemeSwitchUi(preference);

  const switchRoot = document.getElementById("theme-switch");
  if (!switchRoot || switchRoot.dataset.bound === "1") return;
  switchRoot.dataset.bound = "1";

  switchRoot.addEventListener("pointerdown", (event) => {
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
    const handled = handleThemeSwitchInteraction(event);
    if (handled) {
      lastThemePointerInteractionTs = now;
      event.preventDefault();
      event.stopPropagation();
    }
  });

  switchRoot.addEventListener("click", (event) => {
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
    const isLikelyPointerSynthesizedClick = now - lastThemePointerInteractionTs < THEME_INTERACTION_DEDUPE_MS;
    if (isLikelyPointerSynthesizedClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const handled = handleThemeSwitchInteraction(event);
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  if (!onThemeStorageChange && typeof window !== "undefined") {
    onThemeStorageChange = (event) => {
      if (!event || event.key !== THEME_STORAGE_KEY) return;
      const preferenceFromStorage = normalizeThemePreference(event.newValue);
      const effectiveThemeFromStorage = resolveEffectiveTheme(preferenceFromStorage);
      applyTheme(effectiveThemeFromStorage, preferenceFromStorage);
      updateThemeSwitchUi(preferenceFromStorage);
    };
    window.addEventListener("storage", onThemeStorageChange);
  }
}

function placeThemeSwitchInGlobalControls() {
  const switchRoot = document.getElementById("theme-switch");
  if (!switchRoot) return;
  switchRoot.classList.remove("theme-switch--auth-dropdown");
  const authWidget = document.getElementById("auth-widget");
  if (authWidget?.parentNode) {
    authWidget.parentNode.insertBefore(switchRoot, authWidget);
  } else if (document.body.firstChild !== switchRoot) {
    document.body.insertBefore(switchRoot, document.body.firstChild);
  }
}

function placeThemeSwitchInAuthDropdown() {
  const switchRoot = document.getElementById("theme-switch");
  const dropdown = document.getElementById("auth-dropdown");
  const logoutButton = document.getElementById("auth-logout-btn");
  if (!switchRoot || !dropdown) return;
  switchRoot.classList.add("theme-switch--auth-dropdown");
  if (logoutButton?.parentElement === dropdown) {
    dropdown.insertBefore(switchRoot, logoutButton);
  } else {
    dropdown.appendChild(switchRoot);
  }
}

function setBootingState(isBooting) {
  if (typeof document === "undefined" || !document.body) return;
  document.body.classList.toggle("ranks-booting", Boolean(isBooting));
}

function applyCurrentStateToUi({ closeDropdown = false, scrollTop = false } = {}) {
  updateViewClass();
  renderCategoryTree();
  updateTabButtonStyles();
  updateControlDisabling();
  updateSearchFieldControls();
  updateColumnHeadersForMode();
  updateCategoryDropdownVisibility();
  renderExpertTypeDropdown();
  updateExpertTypeDropdownText();
  const activeMeta = getActiveCategoryMeta();
  const categoryName =
    state.category && activeMeta[state.category] ? activeMeta[state.category].name : getDefaultCategoryDropdownLabel();
  updateCategoryDropdownText(categoryName);
  updateCategorySelectedState(state.category);
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    searchInput.value = state.search || "";
    searchInput.classList.toggle("search-active", Boolean(state.search));
  }
  refresh();
  if (closeDropdown) closeCategoryDropdown();
  if (scrollTop) {
    scrollVirtualizerToTop();
  }
}

function detectPerfEnabled() {
  if (typeof window === "undefined") return false;
  if (window.__RANKS_PERF__ === true) return true;
  const params = new URLSearchParams(window.location.search);
  return params.get("perf") === "1";
}

function recordPerf(entry) {
  if (!perfState.enabled) return;
  perfState.history.push(entry);
  if (perfState.history.length > perfState.maxHistory) {
    perfState.history.shift();
  }
  console.log("[RANKS_PERF]", entry);
}

function nowMs() {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}

function roundMs(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function recordPerfMeasure(name, start, end = nowMs(), detail = {}) {
  if (!perfState.enabled) return null;
  const entry = {
    name,
    at: new Date().toISOString(),
    durationMs: roundMs(end - start),
    startMs: roundMs(start),
    endMs: roundMs(end),
    type: state.type,
    view: getCurrentContext(),
    detail,
  };
  perfState.measures.push(entry);
  if (perfState.measures.length > perfState.maxMeasures) {
    perfState.measures.shift();
  }
  console.log("[RANKS_PERF_MEASURE]", entry);
  return entry;
}

function startPerfMeasure(name, detail = {}) {
  const start = nowMs();
  const markName = `${name}:start:${start}`;
  if (perfState.enabled && typeof performance !== "undefined" && typeof performance.mark === "function") {
    try {
      performance.mark(markName);
    } catch {
      // ignore user timing failures
    }
  }
  let ended = false;
  return {
    end(extraDetail = {}) {
      if (ended) return null;
      ended = true;
      const end = nowMs();
      const entry = recordPerfMeasure(name, start, end, { ...detail, ...extraDetail });
      if (
        perfState.enabled &&
        typeof performance !== "undefined" &&
        typeof performance.mark === "function" &&
        typeof performance.measure === "function"
      ) {
        const endMarkName = `${name}:end:${end}`;
        try {
          performance.mark(endMarkName);
          performance.measure(name, markName, endMarkName);
        } catch {
          // ignore user timing failures
        }
      }
      return entry;
    },
  };
}

function measureNextPaint(name, detail = {}) {
  const span = startPerfMeasure(name, detail);
  if (typeof requestAnimationFrame !== "function") {
    span.end({ paintWait: "unavailable" });
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        span.end({ paintWait: "double-raf" });
        resolve();
      });
    });
  });
}

function buildNoResultsRow(message = "No results found") {
  return buildNoResultsRowForType(state.type, message);
}

function buildFooterStretchRow() {
  const columns = getColumnsForType(state.type);
  const cells = columns.map((column) => `<td class="${column.cellClass || ""}"></td>`).join("");
  return `<tr class="tfoot-spacer-row" aria-hidden="true">${cells}</tr>`;
}

function clearFilterCache() {
  filterCache.clear();
}

function getFilterCacheStats() {
  return { size: filterCache.size };
}

function buildFilterCacheKey() {
  const context = getCurrentContext();
  return buildCacheKey({
    type: state.type,
    category: state.category,
    tab: state.tab,
    column: "_",
    direction: "_",
    search: state.search,
  }).concat(
    `-ctx${context}`,
    `-et${state.expertType || ""}`,
    `-sr${state.searchFields.searchRank ? 1 : 0}`,
    `-sc${state.searchFields.searchChange ? 1 : 0}`,
    `-sp${state.searchFields.searchPrice ? 1 : 0}`,
    `-sn${state.searchFields.searchName ? 1 : 0}`,
    `-sa${state.searchFields.searchAuthor ? 1 : 0}`,
    `-n${state.allRows.length}`,
  );
}

function buildSearchFieldsCachePart() {
  return [
    state.searchFields.searchRank ? 1 : 0,
    state.searchFields.searchChange ? 1 : 0,
    state.searchFields.searchPrice ? 1 : 0,
    state.searchFields.searchName ? 1 : 0,
    state.searchFields.searchAuthor ? 1 : 0,
  ].join("");
}

function buildViewCacheKey(
  typeKey = buildApiTypeKey(),
  lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated,
) {
  return [
    typeKey,
    lastUpdated || "_",
    getCurrentContext(),
    state.expertType || "",
    state.sortColumn,
    state.sortDirection,
    normalizeSearchValue(state.search),
    buildSearchFieldsCachePart(),
    VIEW_CACHE_VERSION,
  ].join("|");
}

function buildCategoryTreeCacheKey(
  typeKey = buildApiTypeKey(),
  lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated,
) {
  return [typeKey, lastUpdated || "_", state.expertType || "", CATEGORY_TREE_CACHE_VERSION].join("|");
}

function getAssembledViewCacheEntry(viewCacheKey) {
  const cached = state.api.assembledViewCache.get(viewCacheKey);
  if (!Array.isArray(cached)) return null;
  state.api.assembledViewCache.delete(viewCacheKey);
  state.api.assembledViewCache.set(viewCacheKey, cached);
  return cached;
}

function saveAssembledViewCache(viewCacheKey, htmlRows) {
  if (!Array.isArray(htmlRows)) return;
  if (state.api.assembledViewCache.has(viewCacheKey)) {
    state.api.assembledViewCache.delete(viewCacheKey);
  }
  state.api.assembledViewCache.set(viewCacheKey, htmlRows);
  while (state.api.assembledViewCache.size > ASSEMBLED_VIEW_CACHE_MAX_SIZE) {
    const oldestKey = state.api.assembledViewCache.keys().next().value;
    if (!oldestKey) break;
    state.api.assembledViewCache.delete(oldestKey);
  }
}

function getViewCacheEntry(viewCacheKey) {
  const cached = state.api.viewCache.get(viewCacheKey);
  if (!cached) return null;
  state.api.viewCache.delete(viewCacheKey);
  state.api.viewCache.set(viewCacheKey, cached);
  return cached;
}

function saveCurrentViewCache() {
  const typeKey = buildApiTypeKey();
  const lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || null;
  const viewCacheKey = buildViewCacheKey(typeKey, lastUpdated);
  if (state.api.viewCache.has(viewCacheKey)) {
    state.api.viewCache.delete(viewCacheKey);
  }
  state.api.viewCache.set(viewCacheKey, {
    typeKey,
    lastUpdated,
    filteredRowIndices: state.filteredRows.map((row) => row.index),
    sortedIndices: state.sortedIndices,
  });
  while (state.api.viewCache.size > VIEW_CACHE_MAX_SIZE) {
    const oldestKey = state.api.viewCache.keys().next().value;
    if (!oldestKey) break;
    state.api.viewCache.delete(oldestKey);
  }
}

function getActiveCategoryMeta() {
  return getCategoryMetaForType(state.type, state);
}

function getActiveCategoryTree() {
  return getCategoryTreeForType(state.type, state);
}

function getAllCategoryIds() {
  return Array.from(
    new Set([
      ...Object.keys(state.templateCategoryMeta || {}),
      ...Object.keys(state.componentCategoryMeta || {}),
      ...Object.keys(state.pluginCategoryMeta || {}),
      ...Object.keys(state.vectorCategoryMeta || {}),
      ...Object.keys(state.tutorialCategoryMeta || {}),
      ...Object.keys(state.expertCountryMeta || {}),
      ...Object.keys(state.categoryMeta || {}),
    ]),
  );
}

function buildCategoryOrderMapsFromTree(tree = []) {
  const sortedTree = sortCategoryTree(Array.isArray(tree) ? tree : []);
  const orderByCategoryId = {};
  const depthByCategoryId = {};
  let nextOrder = 0;

  const walk = (nodes, depth) => {
    for (const node of nodes || []) {
      if (!node?.id) continue;
      const id = String(node.id);
      orderByCategoryId[id] = nextOrder++;
      depthByCategoryId[id] = depth;
      if (Array.isArray(node.children) && node.children.length > 0) {
        walk(node.children, depth + 1);
      }
    }
  };

  walk(sortedTree, 1);
  return { orderByCategoryId, depthByCategoryId };
}

function getRenderCategoryOptions() {
  const templateMaps = buildCategoryOrderMapsFromTree(state.templateCategoryTree);
  const componentMaps = buildCategoryOrderMapsFromTree(state.componentCategoryTree);
  const pluginMaps = buildCategoryOrderMapsFromTree(state.pluginCategoryTree);
  const vectorMaps = buildCategoryOrderMapsFromTree(state.vectorCategoryTree);
  const siteMaps = buildCategoryOrderMapsFromTree(state.siteCategoryTree);
  const tutorialMaps = buildCategoryOrderMapsFromTree(state.tutorialCategoryTree);
  const expertCountryMaps = buildCategoryOrderMapsFromTree(getActiveCategoryTree());

  return {
    activeContext: getCurrentContext(),
    activeContextOnly: false,
    deferContextHydration: shouldUsePostHydrateContextCells(),
    skipTrendRender: false,
    lazyCategoryTooltip: state.type !== "creator",
    categoryDisplayMode: getCategoryDisplayMode(state.type, state.allItems),
    categoryOrderByType: {
      template: templateMaps.orderByCategoryId,
      component: componentMaps.orderByCategoryId,
      plugin: pluginMaps.orderByCategoryId,
      vector: vectorMaps.orderByCategoryId,
      site: siteMaps.orderByCategoryId,
      tutorial: tutorialMaps.orderByCategoryId,
      expert: expertCountryMaps.orderByCategoryId,
    },
    categoryDepthByType: {
      template: templateMaps.depthByCategoryId,
      component: componentMaps.depthByCategoryId,
      plugin: pluginMaps.depthByCategoryId,
      vector: vectorMaps.depthByCategoryId,
      site: siteMaps.depthByCategoryId,
      tutorial: tutorialMaps.depthByCategoryId,
      expert: expertCountryMaps.depthByCategoryId,
    },
    engagementData: state.engagementData,
    authState: authClientState.status,
    categoryMetaByType: {
      template: state.templateCategoryMeta,
      component: state.componentCategoryMeta,
      plugin: state.pluginCategoryMeta,
      vector: state.vectorCategoryMeta,
      site: state.siteCategoryMeta,
      tutorial: state.tutorialCategoryMeta,
      expert: getActiveCategoryMeta(),
    },
  };
}

const DEFAULT_CATEGORY_DROPDOWN_LABELS = new Set([
  "Category",
  "Categories",
  "Template Categories",
  "Component Categories",
  "Country",
  "Countries",
  "All Countries",
]);

function getDefaultCategoryDropdownLabel() {
  return state.type === "expert" ? "Country" : "Category";
}

function getAllCategoriesOptionLabel() {
  return getTypeCapabilities(state.type).categoryLabel || "Categories";
}

function normalizeCategoryDisplayName(name) {
  if (typeof name !== "string") return name;
  const normalized = name
    .replace(/\s*Templates?\s*$/i, "")
    .replace(/\s*Components?\s*$/i, "")
    .replace(/\bCms\b/g, "CMS")
    .replace(/\bAi\b/g, "AI")
    .replace(/\bUi\b/g, "UI")
    .replace(/\bSeo\b/g, "SEO")
    .trim();
  return normalized.length > 0 ? normalized : name.trim();
}

function slugifyExpertCountryName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ============================================
// MUTEX HELPERS
// ============================================

function acquireLock(lockName) {
  if (locks[lockName]) {
    log(`[MUTEX] ${lockName} blocked - operation in progress`);
    return false;
  }
  locks[lockName] = true;
  log(`[MUTEX] ${lockName} acquired`);
  return true;
}

function releaseLock(lockName) {
  locks[lockName] = false;
  log(`[MUTEX] ${lockName} released`);
}

// ============================================
// VIEW CONTEXT HELPERS
// ============================================

/**
 * Get the current view context for CSS class and sorting
 * Returns: "global-all", "global-free", "global-paid", or
 *          "{category}-all", "{category}-free", "{category}-paid".
 */
function getCurrentContext() {
  if (state.type === "creator") return "creators";
  if (state.type === "expert") {
    if (state.expertType && state.category) return `expert-type-${state.expertType}-${state.category}-all`;
    if (state.expertType) return `expert-type-${state.expertType}-all`;
    return state.category ? `${state.category}-all` : "global-all";
  }

  const pricing = typeSupportsPricing(state.type) ? state.tab || "all" : "all";
  const mode = typeSupportsRankingMode(state.type) ? normalizeRankingMode(state.rankingMode) : "popular";
  const prefix = mode === "popular" ? "" : `${mode}-`;
  if (state.category) {
    return `${prefix}${state.category}-${pricing}`;
  }
  return `${prefix}global-${pricing}`;
}

function getContextStateKey() {
  return [state.type, state.category || "", state.expertType || "", state.rankingMode, state.tab].join("|");
}

function isApiModeEnabled() {
  if (typeof window === "undefined") return true;
  const forceStatic =
    String(window.FR_RANKS_FORCE_STATIC || "")
      .trim()
      .toLowerCase() === "true";
  if (forceStatic) return false;
  return true;
}

function getApiType() {
  if (state.type === "component") return "components";
  if (state.type === "plugin") return "plugins";
  if (state.type === "vector") return "vectors";
  if (state.type === "site") return "sites";
  if (state.type === "tutorial") return "tutorials";
  if (state.type === "creator") return "creators";
  if (state.type === "expert") return "experts";
  return "templates";
}

function shouldUsePostHydrateContextCells() {
  return state.type !== "creator" && state.type !== "expert";
}

function buildApiTypeKey() {
  return getApiType();
}

function getApiSessionStorageKey(typeKey) {
  return `${API_SESSION_CACHE_PREFIX}${typeKey}`;
}

function getLastUpdatedEndpoint() {
  const endpointRaw =
    typeof window !== "undefined" && typeof window.FR_RANKS_LAST_UPDATED_ENDPOINT === "string"
      ? window.FR_RANKS_LAST_UPDATED_ENDPOINT
      : "/api-json/manifest.json";
  return endpointRaw.trim() || "/api-json/manifest.json";
}

function readStoredLastUpdatedMs() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem(LAST_UPDATED_STORAGE_KEY);
    const parsed = Number.parseInt(String(raw || "").trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredLastUpdatedMs(value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(LAST_UPDATED_STORAGE_KEY, String(value));
  } catch {
    // ignore storage failures
  }
}

function buildApiUrlForCurrentState() {
  const type = getApiType();
  const staticFile = state.api.staticFiles?.[type]?.file;
  if (typeof staticFile === "string" && staticFile.trim()) {
    return staticFile.trim();
  }

  const params = new URLSearchParams();
  params.set("type", type);
  const lastUpdate = state.api.lastUpdateMs || readStoredLastUpdatedMs();
  if (lastUpdate) {
    params.set("lastUpdate", String(lastUpdate));
  }
  const endpointRaw =
    typeof window !== "undefined" && typeof window.FR_RANKS_API_ENDPOINT === "string"
      ? window.FR_RANKS_API_ENDPOINT
      : "/api/json";
  const endpoint = endpointRaw.trim() || "/api/json";
  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}${params.toString()}`;
}

function clearApiCache() {
  state.api.cache.clear();
  state.api.baseRowsCache.clear();
  state.api.viewCache.clear();
  state.api.assembledViewCache.clear();
  state.api.categoryTreeCache.clear();
  state.api.contextCellCache.clear();
  state.api.contextPopulationCache.clear();
  state.api.activeTypeKey = null;
  state.api.lastUpdated = null;
  state.api.lastUpdateMs = null;
  state.api.staticFiles = {};
  state.api.typeLastUpdated.clear();
  state.api.inFlightByTypeKey.clear();
  state.rowLookup.clear();
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(API_SESSION_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch {
    // ignore storage failures
  }
  clearAllTypePayloads().catch(() => {
    // ignore IndexedDB failures
  });
}

function isTopPercentColumnEnabledFromUiFlags(uiFlags) {
  return uiFlags?.topPercentColumn !== false;
}

function isTopPercentColumnEnabledForCurrentState() {
  return isTopPercentColumnEnabledFromUiFlags(state.uiFlags);
}

function clearRenderedRowCaches() {
  state.api.baseRowsCache.clear();
  state.api.viewCache.clear();
  state.api.assembledViewCache.clear();
  state.api.contextCellCache.clear();
  state.api.contextPopulationCache.clear();
}

function rerenderExpertRowsForContext() {
  if (state.type !== "expert" || !state.clusterize) return;
  clearRenderedRowCaches();
  renderRows();
}

function renderRowsForAuthState() {
  if (!state.clusterize) return;
  if (
    progressiveRenderState.active ||
    progressiveRenderState.commitPending ||
    progressiveRenderState.bootstrapUpdatePending
  ) {
    progressiveRenderState.authRefreshPending = true;
    return;
  }
  clearRenderedRowCaches();
  renderRows();
  clearFilterCache();
  state.filteredRows = filterRows();
  sortFilteredRows();
  const htmlRowsResult = buildHtmlRowsForCurrentView();
  updateVirtualizer(htmlRowsResult.htmlRows);
}

function isProgressiveRowsEnabled() {
  if (typeof window === "undefined") return false;
  if (window.FR_RANKS_PROGRESSIVE_ROWS === false) return false;
  if (window.FR_RANKS_PROGRESSIVE_ROWS === true) return true;
  try {
    const value = new URLSearchParams(window.location.search).get("progressive");
    if (value === "0") return false;
    return true;
  } catch {
    return true;
  }
}

function isBootstrapExperimentEnabled() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("experiment") === "bootstrap";
  } catch {
    return false;
  }
}

function renderBootstrapRowsIfEligible() {
  if (!isBootstrapExperimentEnabled()) return false;
  if (state.type !== "template" || state.tab !== "all" || state.category || state.rankingMode !== "popular")
    return false;
  const container = document.getElementById("clusterize-content");
  if (!container || !Array.isArray(TEMPLATE_BOOTSTRAP?.items) || TEMPLATE_BOOTSTRAP.items.length === 0) return false;
  const items = TEMPLATE_BOOTSTRAP.items.map((item) => ({
    id: item.id,
    type: "template",
    name: item.name,
    url: `https://www.framer.com/marketplace/template/${String(item.id).replace(/^template-/, "")}/`,
    thumbnailUrl: item.thumbnailUrl,
    creatorAvatarUrl: item.creatorAvatarUrl || null,
    price: String(item.price).toLowerCase() === "free" ? "Free" : `$${item.price}`,
    authorName: item.authorName || "",
    authorUrl: item.authorUrl || null,
    featuredAt: item.featuredAt || null,
    categories: Array.isArray(item.categories) ? item.categories : [],
    ranks: { "global-all": [item.rank, item.change] },
    trend: { "global-all": Array.isArray(item.trend) ? item.trend : [] },
  }));
  const rows = renderRowRecords(items, 0, {
    ...getRenderCategoryOptions(),
    activeContextOnly: true,
    deferContextHydration: false,
    activeContext: "global-all",
  });
  const rankingTabs = document.getElementById("ranking-mode-tabs");
  if (rankingTabs) rankingTabs.hidden = false;
  const pricingTabs = document.getElementById("ranking-tabs");
  if (pricingTabs) pricingTabs.hidden = false;
  updateColumnHeadersForMode();
  const titleMain = document.getElementById("table-title-main");
  if (titleMain) {
    titleMain.innerHTML =
      '<a href="https://www.framer.com/marketplace/templates/" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">All Templates</a>';
  }
  const titleSubtitle = document.getElementById("table-title-subtitle");
  if (titleSubtitle) {
    renderTableTitleSubtitle(
      titleSubtitle,
      `<span class="subtitle-pricing">$ Mixed Price</span> <span class="subtitle-sep">·</span> <span class="subtitle-period">◷ Trending</span> <span class="subtitle-sep">·</span> <span class="subtitle-count">${Number(TEMPLATE_BOOTSTRAP.totalItems || 0).toLocaleString()} items</span>`,
    );
  }
  const dateEl = document.getElementById("current-date");
  if (dateEl) {
    const parsedDate = new Date(TEMPLATE_BOOTSTRAP.lastUpdated);
    dateEl.textContent = Number.isNaN(parsedDate.getTime())
      ? "-"
      : parsedDate.toLocaleString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
  }
  // Keep bootstrap images deferred; the idle hydrator reveals them in small
  // batches so image decoding cannot monopolize the main thread.
  const bootstrapHtml = rows.map((row) => row.html).join("");
  container.innerHTML = bootstrapHtml;
  container.dataset.bootstrapRows = "1";
  if (typeof window !== "undefined") window.__RANKS_BOOTSTRAP_ROWS_RENDERED__ = items.length;
  return true;
}

function cancelProgressiveRowRender() {
  progressiveRenderState.generation += 1;
  progressiveRenderState.active = false;
  progressiveRenderState.nextIndex = 0;
  progressiveRenderState.typeKey = null;
  progressiveRenderState.lastUpdated = null;
  progressiveRenderState.payloadState = null;
  progressiveRenderState.authRefreshPending = false;
  progressiveRenderState.commitPending = false;
  progressiveRenderState.bootstrapUpdatePending = false;
  progressiveRenderState.renderVersion = 0;
  progressiveRenderState.virtualizerVersion = -1;
  if (progressiveRenderState.timer !== null && typeof window !== "undefined") {
    window.clearTimeout(progressiveRenderState.timer);
    window.cancelIdleCallback?.(progressiveRenderState.timer);
    window.cancelAnimationFrame?.(progressiveRenderState.timer);
  }
  progressiveRenderState.timer = null;
  if (progressiveRenderState.commitTimer !== null && typeof window !== "undefined") {
    window.clearTimeout(progressiveRenderState.commitTimer);
  }
  progressiveRenderState.commitTimer = null;
}

function scheduleProgressiveVirtualizerCommit() {
  if (typeof window === "undefined" || !progressiveRenderState.commitPending) return;
  const generation = progressiveRenderState.generation;
  const commit = () => {
    progressiveRenderState.commitTimer = null;
    if (!progressiveRenderState.commitPending || progressiveRenderState.generation !== generation) return;
    progressiveRenderState.commitPending = false;
    const bootstrapContainer = document.getElementById("clusterize-content");
    if (
      state.clusterize &&
      (bootstrapContainer?.dataset.bootstrapRows === "1" || bootstrapContainer?.dataset.progressiveRows === "1")
    ) {
      if (bootstrapContainer?.dataset.progressiveRows === "1") {
        const setSpacer = () => setProgressiveFullHeightSpacer();
        if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(setSpacer, { timeout: 1200 });
        else window.setTimeout(setSpacer, 0);
      }
      // Keep the visible bootstrap rows stable. Repaint only when the user
      // scrolls and the virtualizer actually needs to take over.
      progressiveRenderState.bootstrapUpdatePending = true;
      return;
    }
    if (state.clusterize) updateVirtualizer();
    if (progressiveRenderState.authRefreshPending) {
      progressiveRenderState.authRefreshPending = false;
      renderRowsForAuthState();
    }
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(commit, { timeout: 1200 });
  } else {
    progressiveRenderState.commitTimer = window.setTimeout(commit, 0);
  }
}

function flushDeferredBootstrapClusterizeUpdate() {
  if (!progressiveRenderState.bootstrapUpdatePending || !state.clusterize) return;
  progressiveRenderState.bootstrapUpdatePending = false;
  const bootstrapContainer = document.getElementById("clusterize-content");
  if (bootstrapContainer) {
    bootstrapContainer.dataset.bootstrapRows = "0";
    bootstrapContainer.dataset.progressiveRows = "0";
    bootstrapContainer.querySelector(".clusterize-progressive-spacer")?.remove();
  }
  updateVirtualizer(null, { allowBootstrap: true });
  if (progressiveRenderState.authRefreshPending) {
    progressiveRenderState.authRefreshPending = false;
    renderRowsForAuthState();
  }
}

function setProgressiveFullHeightSpacer() {
  const container = document.getElementById("clusterize-content");
  const clusterize = state.clusterize;
  if (!container || !clusterize?.options || container.dataset.progressiveRows !== "1") return;
  // Once Clusterize owns the complete row list, its own bottom offset already
  // reserves the full height. An extra spacer would double the scroll range.
  if (typeof clusterize.getRowsAmount === "function" && clusterize.getRowsAmount() >= state.sortedIndices.length) {
    container.querySelector(".clusterize-progressive-spacer")?.remove();
    return;
  }
  const visibleRows = container.querySelectorAll("tr[data-id]").length;
  const rowHeight = Number(clusterize.options.item_height || 0);
  if (visibleRows <= 0 || rowHeight <= 0) return;
  let spacer = container.querySelector(".clusterize-progressive-spacer");
  if (!spacer) {
    spacer = document.createElement("tr");
    spacer.className = "clusterize-extra-row clusterize-bottom-space clusterize-progressive-spacer";
    spacer.innerHTML = '<td colspan="100"></td>';
    container.appendChild(spacer);
  }
  spacer.style.height = `${Math.max(0, state.allRows.length - visibleRows) * rowHeight}px`;
}

function buildProgressivePlaceholderRow() {
  return '<tr class="clusterize-progressive-placeholder" aria-hidden="true"><td colspan="100"></td></tr>';
}

function buildProgressiveClusterRows() {
  return state.sortedIndices.map((filteredIndex) => {
    const row = state.filteredRows[filteredIndex];
    return row?.html || buildProgressivePlaceholderRow();
  });
}

function hydrateProgressiveScrollWindow({ force = false } = {}) {
  if (!progressiveRenderState.active || !state.clusterize) return;
  const scroll = document.getElementById("clusterize-scroll");
  const rowHeight =
    Number(state.clusterize.options?.item_height || 0) ||
    Number(document.querySelector("#clusterize-content tr[data-id]")?.getBoundingClientRect().height || 0) ||
    50;
  if (!scroll || state.sortedIndices.length === 0) return;
  const rowsInCluster = Number(state.clusterize.options?.rows_in_cluster) || 30;
  const first = Math.max(0, Math.floor(scroll.scrollTop / rowHeight) - rowsInCluster);
  const last = Math.min(state.sortedIndices.length, first + rowsInCluster * 2);
  const indices = [];
  for (let position = first; position < last; position += 1) {
    const row = state.filteredRows[state.sortedIndices[position]];
    if (row && !row.html) indices.push(row.index);
  }
  if (indices.length > 0) {
    renderProgressiveRowsAtIndices(indices, getRenderCategoryOptions());
    progressiveRenderState.renderVersion += 1;
  }
  if (!force && progressiveRenderState.virtualizerVersion === progressiveRenderState.renderVersion) return;
  const htmlRows = buildProgressiveClusterRows();
  htmlRows.push(buildFooterStretchRow());
  state.clusterize.update(htmlRows);
  progressiveRenderState.virtualizerVersion = progressiveRenderState.renderVersion;
  const container = document.getElementById("clusterize-content");
  if (container) {
    container.dataset.progressiveRows = "1";
    container.querySelector(".clusterize-progressive-spacer")?.remove();
  }
}

function renderProgressiveRowsAtIndices(indices, renderOptions) {
  for (const rowIndex of indices) {
    const row = state.allRows[rowIndex];
    const item = row ? state.allItems[row.index] : null;
    if (!row || !item || row.html) continue;
    const rendered = renderRowRecords([item], row.index, renderOptions)[0];
    if (rendered) row.html = rendered.html;
  }
}

function renderInitialProgressiveRows() {
  if (!progressiveRenderState.active) return;
  const initialIndices = state.sortedIndices.slice(0, getProgressiveInitialRowCount());
  const span = startPerfMeasure("ranks:rows-render-initial", { rowCount: initialIndices.length });
  const bootstrapContainer = document.getElementById("clusterize-content");
  const bootstrapRows =
    bootstrapContainer?.dataset.bootstrapRows === "1"
      ? Array.from(bootstrapContainer.querySelectorAll("tr[data-id]"))
      : [];
  const bootstrapRowsMatchCurrentType =
    bootstrapRows.length >= initialIndices.length &&
    bootstrapRows.every((row) => row.getAttribute("data-type") === state.type);
  if (bootstrapRowsMatchCurrentType) {
    // Keep the already-painted bootstrap DOM as the initial row HTML. This
    // avoids re-rendering/reloading the first 60 images during handoff.
    initialIndices.forEach((rowIndex, position) => {
      const row = state.allRows[rowIndex];
      if (row && bootstrapRows[position]) row.html = bootstrapRows[position].outerHTML;
    });
  } else {
    // sortedIndices index into filteredRows, not allRows. This distinction is
    // invisible for global views but matters for category/free/paid deep links;
    // using allRows here leaves the filtered rows empty and Clusterize renders
    // the no-results row even though matching items exist.
    const renderOptions = getRenderCategoryOptions();
    for (const filteredIndex of initialIndices) {
      const row = state.filteredRows[filteredIndex];
      const item = getRowItem(row);
      if (!row || !item || row.html) continue;
      const rendered = renderRowRecords([item], row.index, renderOptions)[0];
      if (rendered) row.html = rendered.html;
    }
  }
  span.end({ rowCount: initialIndices.filter((index) => Boolean(state.allRows[index]?.html)).length });
  progressiveRenderState.nextIndex = 0;
}

function finishProgressiveRowRender() {
  if (!progressiveRenderState.active) return;
  progressiveRenderState.active = false;
  progressiveRenderState.timer = null;
  progressiveRenderState.commitPending = true;
  if (typeof window !== "undefined") window.__RANKS_PROGRESSIVE_COMPLETE__ = true;
  const typeKey = progressiveRenderState.typeKey;
  const lastUpdated = progressiveRenderState.lastUpdated;
  const cacheKey = getBaseRowsCacheKey(typeKey, lastUpdated);
  state.api.baseRowsCache.set(cacheKey, {
    typeKey,
    lastUpdated,
    payloadState: progressiveRenderState.payloadState,
    rows: state.allRows,
  });
  writeBaseRowsPayload(cacheKey, {
    typeKey,
    lastUpdated,
    rows: state.allRows,
    savedAt: Date.now(),
  }).catch(() => {
    // ignore IndexedDB write failures
  });
  scheduleProgressiveVirtualizerCommit();
}

function scheduleProgressiveRowRender() {
  if (!progressiveRenderState.active || typeof window === "undefined") return;
  const generation = progressiveRenderState.generation;
  const run = () => {
    progressiveRenderState.timer = null;
    if (!progressiveRenderState.active || progressiveRenderState.generation !== generation) return;
    const start = progressiveRenderState.nextIndex;
    const end = Math.min(state.allRows.length, start + PROGRESSIVE_CHUNK_SIZE);
    const span = startPerfMeasure("ranks:rows-render-background", { start, end });
    renderProgressiveRowsAtIndices(
      Array.from({ length: Math.max(0, end - start) }, (_value, offset) => start + offset),
      getRenderCategoryOptions(),
    );
    progressiveRenderState.renderVersion += 1;
    span.end({ rendered: Math.max(0, end - start) });
    progressiveRenderState.nextIndex = end;
    if (end >= state.allRows.length) finishProgressiveRowRender();
    else scheduleProgressiveRowRender();
  };
  // Hydration is background work. Let input/scroll/paint win first; RAF here
  // made a large payload compete with every frame and could starve scrolling.
  if (typeof window.requestIdleCallback === "function") {
    progressiveRenderState.timer = window.requestIdleCallback(run, { timeout: 200 });
  } else {
    progressiveRenderState.timer = window.setTimeout(run, 0);
  }
}

function syncColumnFeatureFlags({ rerender = false } = {}) {
  const topPercentColumnEnabled = isTopPercentColumnEnabledForCurrentState();
  const changed = state.columnFeatures.topPercentColumnEnabled !== topPercentColumnEnabled;
  state.columnFeatures.topPercentColumnEnabled = topPercentColumnEnabled;
  setColumnFeatureFlags({ topPercentColumn: topPercentColumnEnabled });

  if (!changed || !rerender || !state.clusterize) return changed;
  const progressiveContainer = document.getElementById("clusterize-content");
  if (
    progressiveRenderState.active ||
    progressiveRenderState.commitPending ||
    progressiveRenderState.bootstrapUpdatePending ||
    progressiveContainer?.dataset.progressiveRows === "1"
  ) {
    progressiveRenderState.authRefreshPending = true;
    return changed;
  }

  clearRenderedRowCaches();
  renderRows();
  clearFilterCache();
  updateColumnHeadersForMode();
  refreshSync();
  return changed;
}

function getBaseRowsCacheKey(typeKey, lastUpdated, options = {}) {
  const topPercentColumnEnabled =
    typeof options.topPercentColumnEnabled === "boolean"
      ? options.topPercentColumnEnabled
      : state.columnFeatures.topPercentColumnEnabled;
  const topPercentMode = topPercentColumnEnabled ? "toppercent-on" : "toppercent-off";
  const authState = getBaseRowsAuthState();
  return `${typeKey}|${lastUpdated || "_"}|${BASE_ROWS_CACHE_VERSION}|auth-${authState}|${topPercentMode}`;
}

function deleteContextCellCacheForType(typeKey) {
  const prefix = `${typeKey}|`;
  for (const key of state.api.contextCellCache.keys()) {
    if (key.startsWith(prefix)) state.api.contextCellCache.delete(key);
  }
  for (const key of state.api.contextPopulationCache.keys()) {
    if (key.startsWith(prefix)) state.api.contextPopulationCache.delete(key);
  }
}

function deleteBaseRowsCacheForType(typeKey) {
  const prefix = `${typeKey}|`;
  state.api.cache.delete(typeKey);
  for (const key of state.api.baseRowsCache.keys()) {
    if (key.startsWith(prefix)) state.api.baseRowsCache.delete(key);
  }
  for (const key of state.api.viewCache.keys()) {
    if (key.startsWith(prefix)) state.api.viewCache.delete(key);
  }
  for (const key of state.api.assembledViewCache.keys()) {
    if (key.startsWith(prefix)) state.api.assembledViewCache.delete(key);
  }
  for (const key of state.api.categoryTreeCache.keys()) {
    if (key.startsWith(prefix)) state.api.categoryTreeCache.delete(key);
  }
  deleteContextCellCacheForType(typeKey);
}

function pruneApiMemoryCachesToActiveType(activeTypeKey) {
  if (!activeTypeKey) return;
  const activePrefix = `${activeTypeKey}|`;
  state.api.cache.clear();

  for (const key of state.api.baseRowsCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.baseRowsCache.delete(key);
  }
  for (const key of state.api.viewCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.viewCache.delete(key);
  }
  for (const key of state.api.assembledViewCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.assembledViewCache.delete(key);
  }
  for (const key of state.api.categoryTreeCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.categoryTreeCache.delete(key);
  }
  for (const key of state.api.contextCellCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.contextCellCache.delete(key);
  }
  for (const key of state.api.contextPopulationCache.keys()) {
    if (!key.startsWith(activePrefix)) state.api.contextPopulationCache.delete(key);
  }
}

function deleteStaleBaseRowsCacheForType(typeKey, activeCacheKey) {
  const prefix = `${typeKey}|`;
  const parts = String(activeCacheKey || "").split("|");
  const activeLastUpdated = parts.length > 1 ? parts[1] : "_";
  const activePrefix = `${typeKey}|${activeLastUpdated}|`;
  let removedStaleEntries = false;
  for (const key of state.api.baseRowsCache.keys()) {
    if (key.startsWith(prefix) && key !== activeCacheKey) {
      state.api.baseRowsCache.delete(key);
      removedStaleEntries = true;
    }
  }
  for (const key of state.api.viewCache.keys()) {
    if (key.startsWith(prefix) && !key.startsWith(activePrefix)) {
      state.api.viewCache.delete(key);
      removedStaleEntries = true;
    }
  }
  for (const key of state.api.assembledViewCache.keys()) {
    if (key.startsWith(prefix) && !key.startsWith(activePrefix)) {
      state.api.assembledViewCache.delete(key);
      removedStaleEntries = true;
    }
  }
  for (const key of state.api.categoryTreeCache.keys()) {
    if (key.startsWith(prefix) && !key.startsWith(activePrefix)) {
      state.api.categoryTreeCache.delete(key);
      removedStaleEntries = true;
    }
  }
  if (removedStaleEntries) {
    deleteContextCellCacheForType(typeKey);
  }
}

function normalizeCategoryTreeNodes(nodes = []) {
  if (!Array.isArray(nodes)) return [];
  return nodes
    .map((node) => {
      if (!node || typeof node !== "object") return null;
      const id = String(node.id || node.slug || "").trim();
      const name = String(node.name || "").trim();
      if (!id || !name) return null;
      return {
        ...node,
        id,
        name,
        children: normalizeCategoryTreeNodes(node.children || []),
      };
    })
    .filter(Boolean);
}

async function buildApiPayloadState(payload) {
  const normalizeSpan = startPerfMeasure("ranks:payload-normalize", {
    itemCount: Array.isArray(payload?.items) ? payload.items.length : 0,
  });
  const payloadState = {
    items: await normalizeApiPayloadItemsChunked(payload),
    marketplaceSources: payload.marketplaceSources || {},
    templateCategoryMeta: payload.templateCategoryMeta || {},
    templateCategoryTree: normalizeCategoryTreeNodes(payload.templateCategoryTree),
    componentCategoryMeta: payload.componentCategoryMeta || {},
    componentCategoryTree: normalizeCategoryTreeNodes(payload.componentCategoryTree),
    pluginCategoryMeta: payload.pluginCategoryMeta || {},
    pluginCategoryTree: normalizeCategoryTreeNodes(payload.pluginCategoryTree),
    vectorCategoryMeta: payload.vectorCategoryMeta || {},
    vectorCategoryTree: normalizeCategoryTreeNodes(payload.vectorCategoryTree),
    siteCategoryMeta: payload.siteCategoryMeta || {},
    siteCategoryTree: normalizeCategoryTreeNodes(payload.siteCategoryTree),
    tutorialCategoryMeta: payload.tutorialCategoryMeta || {},
    tutorialCategoryTree: normalizeCategoryTreeNodes(payload.tutorialCategoryTree),
    expertCountryMeta: payload.expertCountryMeta || {},
    expertCountryTree: normalizeCategoryTreeNodes(payload.expertCountryTree),
    expertTypeMeta: payload.expertTypeMeta || {},
    expertTypeTree: normalizeCategoryTreeNodes(payload.expertTypeTree),
    expertCountryMetaByType: payload.expertCountryMetaByType || {},
    expertCountryTreeByType: Object.fromEntries(
      Object.entries(payload.expertCountryTreeByType || {}).map(([key, value]) => [key, normalizeCategoryTreeNodes(value)]),
    ),
    currentDateStr: typeof payload.currentDateStr === "string" ? payload.currentDateStr.trim() : "",
    lastUpdated: typeof payload.lastUpdated === "string" ? payload.lastUpdated : "",
    uiFlags: payload.uiFlags || {},
  };
  normalizeSpan.end({ itemCount: payloadState.items.length });
  return payloadState;
}

function applyApiPayloadState(payloadState) {
  state.allItems = payloadState.items;
  state.marketplaceSources = payloadState.marketplaceSources;
  state.templateCategoryMeta = payloadState.templateCategoryMeta;
  state.templateCategoryTree = payloadState.templateCategoryTree;
  state.componentCategoryMeta = payloadState.componentCategoryMeta;
  state.componentCategoryTree = payloadState.componentCategoryTree;
  state.pluginCategoryMeta = payloadState.pluginCategoryMeta;
  state.pluginCategoryTree = payloadState.pluginCategoryTree;
  state.vectorCategoryMeta = payloadState.vectorCategoryMeta;
  state.vectorCategoryTree = payloadState.vectorCategoryTree;
  state.siteCategoryMeta = payloadState.siteCategoryMeta;
  state.siteCategoryTree = payloadState.siteCategoryTree;
  state.tutorialCategoryMeta = payloadState.tutorialCategoryMeta;
  state.tutorialCategoryTree = payloadState.tutorialCategoryTree;
  state.expertCountryMeta = payloadState.expertCountryMeta;
  state.expertCountryTree = payloadState.expertCountryTree;
  state.expertTypeMeta = payloadState.expertTypeMeta;
  state.expertTypeTree = payloadState.expertTypeTree;
  state.expertCountryMetaByType = payloadState.expertCountryMetaByType;
  state.expertCountryTreeByType = payloadState.expertCountryTreeByType;
  state.categoryMeta = state.templateCategoryMeta;
  state.categoryTree = state.templateCategoryTree;
  state.footerSummary = null;
  const uiFlags = payloadState.uiFlags || {};
  state.uiFlags = uiFlags;
  syncColumnFeatureFlags();
  state.api.contextCellCache.clear();
  state.api.contextPopulationCache.clear();

  if (typeof window !== "undefined") {
    window.__RANKS_TEMPLATE_CATEGORY_META__ = state.templateCategoryMeta;
    window.__RANKS_COMPONENT_CATEGORY_META__ = state.componentCategoryMeta;
    window.__RANKS_PLUGIN_CATEGORY_META__ = state.pluginCategoryMeta;
    window.__RANKS_VECTOR_CATEGORY_META__ = state.vectorCategoryMeta;
    window.__RANKS_SITE_CATEGORY_META__ = state.siteCategoryMeta;
    window.__RANKS_TUTORIAL_CATEGORY_META__ = state.tutorialCategoryMeta;
    window.__RANKS_EXPERT_COUNTRY_META__ = state.expertCountryMeta;
    window.__RANKS_EXPERT_TYPE_META__ = state.expertTypeMeta;
    window.__RANKS_CATEGORY_META__ = {
      ...state.templateCategoryMeta,
      ...state.componentCategoryMeta,
      ...state.pluginCategoryMeta,
      ...state.vectorCategoryMeta,
      ...state.siteCategoryMeta,
      ...state.tutorialCategoryMeta,
      ...state.expertCountryMeta,
    };
    window.__RANKS_SPARKLINE_LABEL_MODE__ = uiFlags.sparklineLabelMode === "all" ? "all" : "none";
    window.__RANKS_SPARKLINE_TITLE_ATTR__ = uiFlags.sparklineTitleAttr === true;
  }

  const dateEl = document.getElementById("current-date");
  if (dateEl) {
    if (payloadState.currentDateStr) {
      dateEl.textContent = payloadState.currentDateStr;
    } else if (payloadState.lastUpdated) {
      const parsedDate = new Date(payloadState.lastUpdated);
      dateEl.textContent = Number.isNaN(parsedDate.getTime())
        ? "-"
        : parsedDate.toLocaleString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
    } else {
      dateEl.textContent = "-";
    }
  }
}

async function clearAllRanksCaches() {
  clearApiCache();
  await clearGatedCache();
}

async function fetchSnapshotManifest() {
  const endpoint = getLastUpdatedEndpoint();
  const span = startPerfMeasure("ranks:validator", { endpoint });
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeoutId =
    controller && typeof window !== "undefined"
      ? window.setTimeout(() => {
          controller.abort();
        }, LAST_UPDATED_TIMEOUT_MS)
      : null;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      cache: "no-store",
      headers: {
        "X-Client-Source": "ranks-html",
        "X-Client-Version": "last-updated-v1",
      },
      signal: controller?.signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to load last-updated validator: ${response.status}`);
    }
    const payload = await response.json();
    const lastUpdatedMs = Number(payload?.lastUpdatedMs ?? payload?.lastUpdate);
    if (!Number.isFinite(lastUpdatedMs) || lastUpdatedMs <= 0) {
      throw new Error("Invalid last-updated validator payload");
    }
    const staticFiles = payload?.types && typeof payload.types === "object" ? payload.types : {};
    return { lastUpdatedMs, staticFiles };
  } finally {
    if (timeoutId !== null && typeof window !== "undefined") {
      window.clearTimeout(timeoutId);
    }
    span.end();
  }
}

async function resolveSnapshotValidatorState() {
  if (typeof window === "undefined") {
    return { forceFreshApiContext: false };
  }

  try {
    const manifest = await fetchSnapshotManifest();
    const remoteLastUpdatedMs = manifest.lastUpdatedMs;
    const storedLastUpdatedMs = readStoredLastUpdatedMs();
    const hasVersionMismatch = storedLastUpdatedMs !== remoteLastUpdatedMs;
    if (hasVersionMismatch) {
      await clearAllRanksCaches();
    }
    state.api.lastUpdateMs = remoteLastUpdatedMs;
    state.api.staticFiles = manifest.staticFiles || {};
    writeStoredLastUpdatedMs(remoteLastUpdatedMs);
    return { forceFreshApiContext: hasVersionMismatch };
  } catch (_error) {
    return { forceFreshApiContext: true };
  }
}

function mapApiTypeToInternalType(value) {
  if (value === "components") return "component";
  if (value === "plugins") return "plugin";
  if (value === "vectors") return "vector";
  if (value === "sites") return "site";
  if (value === "tutorials") return "tutorial";
  if (value === "creators") return "creator";
  if (value === "experts") return "expert";
  return "template";
}

function _toInternalChangeValue(row) {
  if (row?.changeLabel === "new") return "new";
  if (row?.changeLabel === "comeback") return "comeback";
  if (row?.changeLabel === "price_change") return "price_change";
  if (typeof row?.delta1d === "number") return row.delta1d;
  return null;
}

function normalizeApiPayloadItems(payload) {
  const payloadItems = Array.isArray(payload?.items) ? payload.items : [];
  const indexOffset = Number.isInteger(payload?.__indexOffset) ? payload.__indexOffset : 0;
  const categoryDictionary = Array.isArray(payload?.categoryDictionary) ? payload.categoryDictionary : null;
  const contextDictionary = Array.isArray(payload?.contextDictionary) ? payload.contextDictionary : null;

  function decodeCategoryRefs(value) {
    if (!Array.isArray(value)) return [];
    const decoded = [];
    for (const entry of value) {
      const category =
        typeof entry === "number" && categoryDictionary?.[entry]
          ? categoryDictionary[entry]
          : typeof entry === "string"
            ? entry
            : null;
      if (typeof category === "string" && category.length > 0) decoded.push(category);
    }
    return decoded;
  }

  function decodeContextRecord(value) {
    if (typeof value !== "object" || value === null) return {};
    const decoded = {};
    for (const [key, recordValue] of Object.entries(value)) {
      const numericKey = Number(key);
      const contextKey = Number.isInteger(numericKey) && contextDictionary?.[numericKey];
      decoded[contextKey || key] = recordValue;
    }
    return decoded;
  }

  return payloadItems.map((item, localIndex) => {
    const index = indexOffset + localIndex;
    const apiType = String(item.type || "templates");
    const internalType = mapApiTypeToInternalType(apiType);
    const thumbnailUrl = typeof item.thumbnailUrl === "string" && item.thumbnailUrl ? item.thumbnailUrl : null;
    const creatorRef = Number.isInteger(item.creatorRef) ? item.creatorRef : null;
    const creatorRecord = creatorRef !== null && Array.isArray(payload?.creators) ? payload.creators[creatorRef] : null;
    const creatorName =
      typeof creatorRecord?.name === "string"
        ? creatorRecord.name
        : typeof item.authorName === "string"
          ? item.authorName
          : "";
    const creatorUrl =
      typeof creatorRecord?.url === "string"
        ? creatorRecord.url
        : typeof item.authorUrl === "string"
          ? item.authorUrl
          : null;
    const creatorAvatarUrl =
      typeof creatorRecord?.avatarUrl === "string"
        ? creatorRecord.avatarUrl
        : typeof item.creatorAvatarUrl === "string"
          ? item.creatorAvatarUrl
          : null;
    const rawPrice = String(item.basePrice || "");
    const price =
      rawPrice.toLowerCase() === "free" ? "Free" : /^\d+(?:\.\d+)?$/.test(rawPrice) ? `$${rawPrice}` : rawPrice || "-";

    if (internalType === "expert") {
      const expertChange = item.delta1d === "new" || typeof item.delta1d === "number" ? item.delta1d : null;
      return {
        id: item.id || `expert-${index}`,
        type: "expert",
        rank: Number(item.rank || index + 1),
        change: expertChange,
        name: item.name || "Unknown expert",
        slug: item.slug || "",
        profileUrl: item.profileUrl || item.url || "#",
        avatarUrl: item.avatarUrl || null,
        country: item.country || "",
        badges: Array.isArray(item.badges) ? item.badges : [],
        expertTypes: Array.isArray(item.expertTypes) ? item.expertTypes : [],
        bio: item.bio || "",
        thumbnail: thumbnailUrl,
        countryRank: item.countryRank ?? null,
        countryChange: item.countryChange ?? null,
        categories: decodeCategoryRefs(item.categories),
        ranks: {
          "global-all": [Number(item.rank || index + 1), expertChange],
          ...decodeContextRecord(item.ranks),
        },
      };
    }

    if (internalType === "creator") {
      const creatorStats = item.creatorStats || {};
      const creatorSlug = String(item.creatorSlug || "").trim();
      const creatorUrl =
        typeof item.url === "string" && item.url
          ? item.url
          : creatorSlug
            ? `https://framer.com/@${creatorSlug}`
            : "https://framer.com/marketplace/";
      return {
        id: item.id || `creator-${index}`,
        type: "creator",
        name: item.name || "Unknown creator",
        creatorSlug,
        creatorUrl,
        url: creatorUrl,
        thumbnail: thumbnailUrl,
        price: "-",
        authorName: creatorSlug ? `@${creatorSlug}` : "-",
        authorUrl: creatorUrl,
        featuredAt: item.featuredAt || null,
        ranks: decodeContextRecord(item.ranks),
        categories: decodeCategoryRefs(item.categories),
        badges:
          typeof item.badges === "object" && item.badges !== null
            ? item.badges
            : { crowns: [], medals: [], medalScore: 0 },
        creatorStats: {
          totalItems: Number(creatorStats.totalItems || 0),
          totalTemplates: Number(creatorStats.totalTemplates || 0),
          totalComponents: Number(creatorStats.totalComponents || 0),
          totalPlugins: Number(creatorStats.totalPlugins || 0),
          totalVectors: Number(creatorStats.totalVectors || 0),
          totalTutorials: Number(creatorStats.totalTutorials || 0),
          totalTemplatesDelta1d:
            creatorStats.totalTemplatesDelta1d === null || creatorStats.totalTemplatesDelta1d === undefined
              ? null
              : Number(creatorStats.totalTemplatesDelta1d),
          totalComponentsDelta1d:
            creatorStats.totalComponentsDelta1d === null || creatorStats.totalComponentsDelta1d === undefined
              ? null
              : Number(creatorStats.totalComponentsDelta1d),
          totalPluginsDelta1d:
            creatorStats.totalPluginsDelta1d === null || creatorStats.totalPluginsDelta1d === undefined
              ? null
              : Number(creatorStats.totalPluginsDelta1d),
          totalVectorsDelta1d:
            creatorStats.totalVectorsDelta1d === null || creatorStats.totalVectorsDelta1d === undefined
              ? null
              : Number(creatorStats.totalVectorsDelta1d),
          totalTutorialsDelta1d:
            creatorStats.totalTutorialsDelta1d === null || creatorStats.totalTutorialsDelta1d === undefined
              ? null
              : Number(creatorStats.totalTutorialsDelta1d),
          totalItemsDelta1d:
            creatorStats.totalItemsDelta1d === null || creatorStats.totalItemsDelta1d === undefined
              ? null
              : Number(creatorStats.totalItemsDelta1d),
        },
      };
    }

    return {
      id: item.id || `${internalType}-${index}`,
      type: internalType,
      name: item.name || "",
      url: item.url || "",
      thumbnailUrl,
      creatorAvatarUrl,
      price,
      authorName: creatorName,
      authorUrl: creatorUrl,
      featuredAt: item.featuredAt || null,
      categories: decodeCategoryRefs(item.categories),
      mainCategory: typeof item.mainCategory === "string" ? item.mainCategory : null,
      ranks: decodeContextRecord(item.ranks),
      trend: decodeContextRecord(item.trend),
    };
  });
}

async function normalizeApiPayloadItemsChunked(payload) {
  const payloadItems = Array.isArray(payload?.items) ? payload.items : [];
  const normalizedItems = [];
  // Keep each slice below a frame-sized task. MessageChannel yields a fresh
  // task without the nested setTimeout clamp, so large payloads do not turn
  // into thousands of 4ms timer waits.
  const chunkSize = 25;
  const yieldToBrowserTask = () =>
    new Promise((resolve) => {
      if (typeof MessageChannel !== "function") {
        setTimeout(resolve, 0);
        return;
      }
      const channel = new MessageChannel();
      channel.port1.onmessage = () => {
        channel.port1.close();
        channel.port2.close();
        resolve();
      };
      channel.port2.postMessage(0);
    });
  for (let start = 0; start < payloadItems.length; start += chunkSize) {
    const chunk = normalizeApiPayloadItems({
      ...payload,
      items: payloadItems.slice(start, start + chunkSize),
      __indexOffset: start,
    });
    normalizedItems.push(...chunk);
    if (start + chunkSize < payloadItems.length) {
      await yieldToBrowserTask();
    }
  }
  return normalizedItems;
}

async function applyApiPayloadAndRender(payload, options = {}) {
  const typeKey = options.typeKey || buildApiTypeKey();
  if (typeKey !== buildApiTypeKey()) return;
  const lastUpdated = options.lastUpdated ?? payload?.lastUpdated ?? state.api.lastUpdated ?? null;
  const payloadState = await buildApiPayloadState(payload);
  const cacheKey = getBaseRowsCacheKey(typeKey, lastUpdated, {
    topPercentColumnEnabled: isTopPercentColumnEnabledFromUiFlags(payloadState.uiFlags),
  });
  deleteStaleBaseRowsCacheForType(typeKey, cacheKey);
  state.api.activeTypeKey = typeKey;
  if (lastUpdated) {
    state.api.lastUpdated = lastUpdated;
    state.api.typeLastUpdated.set(typeKey, lastUpdated);
  }
  const span = startPerfMeasure("ranks:payload-apply", {
    itemCount: Array.isArray(payload?.items) ? payload.items.length : 0,
    typeKey,
  });
  const expectedRowType = mapApiTypeToInternalType(typeKey);
  const rowsMatchExpectedType = (rows) =>
    Array.isArray(rows) &&
    rows.every((row) => {
      if (typeof row?.html !== "string" || !row.html.includes("<tr")) return true;
      return row.html.match(/data-type="([^"]+)"/)?.[1] === expectedRowType;
    });
  const cachedRowsCandidate = state.api.baseRowsCache.get(cacheKey);
  const cachedRows = rowsMatchExpectedType(cachedRowsCandidate?.rows) ? cachedRowsCandidate : null;
  if (cachedRowsCandidate && !cachedRows) state.api.baseRowsCache.delete(cacheKey);
  const persistedRowsCandidate =
    !cachedRows && options.persistedBaseRows?.cacheKey === cacheKey ? options.persistedBaseRows : null;
  const persistedRows = rowsMatchExpectedType(persistedRowsCandidate?.rows) ? persistedRowsCandidate : null;
  if (cachedRows) {
    applyApiPayloadState(cachedRows.payloadState);
    state.allRows = cachedRows.rows;
    state.rowLookup = new Map(state.allRows.map((row) => [row.itemId, row]));
    startPerfMeasure("ranks:base-rows-cache", {
      typeKey,
      hit: true,
      source: "memory",
      rows: state.allRows.length,
    }).end();
  } else if (persistedRows) {
    applyApiPayloadState(payloadState);
    state.allRows = persistedRows.rows;
    state.rowLookup = new Map(state.allRows.map((row) => [row.itemId, row]));
    state.api.baseRowsCache.set(cacheKey, {
      typeKey,
      lastUpdated,
      payloadState,
      rows: state.allRows,
    });
    startPerfMeasure("ranks:base-rows-cache", {
      typeKey,
      hit: true,
      source: "indexeddb",
      rows: state.allRows.length,
    }).end();
  } else {
    const applyStateSpan = startPerfMeasure("ranks:payload-apply-state", { typeKey });
    applyApiPayloadState(payloadState);
    applyStateSpan.end();
    const renderRowsSpan = startPerfMeasure("ranks:payload-render-rows", { typeKey });
    renderRows();
    renderRowsSpan.end({ progressive: progressiveRenderState.active });
    if (progressiveRenderState.active) {
      progressiveRenderState.payloadState = payloadState;
    } else {
      state.api.baseRowsCache.set(cacheKey, {
        typeKey,
        lastUpdated,
        payloadState,
        rows: state.allRows,
      });
      startPerfMeasure("ranks:base-rows-cache", { typeKey, hit: false, rows: state.allRows.length }).end();
      writeBaseRowsPayload(cacheKey, {
        typeKey,
        lastUpdated,
        rows: state.allRows,
        savedAt: Date.now(),
      }).catch(() => {
        // ignore IndexedDB write failures
      });
    }
    clearFilterCache();
    clearSortCache();
  }
  // On the initial bootstrap path the category tree is rendered again during
  // init(), after the first rows are mounted. Avoid doing this 1s+ synchronous
  // menu build while the bootstrap table is trying to become interactive.
  if (state.clusterize) {
    const categoryTreeSpan = startPerfMeasure("ranks:payload-render-category-tree", { typeKey });
    renderCategoryTree();
    categoryTreeSpan.end();
  }
  updateCategorySelectedState(state.category);
  const headersSpan = startPerfMeasure("ranks:payload-update-headers", { typeKey });
  updateColumnHeadersForMode();
  headersSpan.end();
  pruneApiMemoryCachesToActiveType(typeKey);
  span.end({ rowCount: state.allRows.length, baseRowsCacheHit: Boolean(cachedRows || persistedRows) });
}

async function readPersistedBaseRowsForPayload(typeKey, lastUpdated) {
  const baseRowsCacheKey = getBaseRowsCacheKey(typeKey, lastUpdated);
  const baseRowsSpan = startPerfMeasure("ranks:base-rows-idb-read", { typeKey, cacheKey: baseRowsCacheKey });
  const persistedBaseRows = await readBaseRowsPayload(baseRowsCacheKey);
  baseRowsSpan.end({ hit: Boolean(persistedBaseRows), rows: persistedBaseRows?.rows?.length || 0 });
  return persistedBaseRows;
}

const EXPECTED_API_SCHEMA_VERSION = "3.1.0";
const TYPE_PAYLOAD_CACHE_VERSION = "type-payload-v4";

export function isCacheEnvelopeValid(envelope, expectedTypeKey) {
  if (!envelope || typeof envelope !== "object") return false;
  if (typeof envelope.schemaVersion !== "string" || envelope.schemaVersion !== EXPECTED_API_SCHEMA_VERSION)
    return false;
  if (envelope.cacheVersion !== TYPE_PAYLOAD_CACHE_VERSION) return false;
  if (typeof envelope.typeKey !== "string" || envelope.typeKey !== expectedTypeKey) return false;
  if (!envelope.payload || typeof envelope.payload !== "object") return false;
  return true;
}

async function loadApiContextForCurrentState(force = false) {
  const typeKey = buildApiTypeKey();
  const itemsBelongToCurrentType =
    state.allItems.length === 0 || state.allItems.every((item) => item?.type === state.type);
  const firstRenderedRow = state.allRows.find((row) => typeof row?.html === "string" && row.html.includes("<tr"));
  const renderedRowType = firstRenderedRow?.html.match(/data-type="([^"]+)"/)?.[1] || null;
  const rowsBelongToCurrentType = !renderedRowType || renderedRowType === state.type;

  if (
    !force &&
    itemsBelongToCurrentType &&
    rowsBelongToCurrentType &&
    state.api.activeTypeKey === typeKey &&
    state.api.typeLastUpdated.has(typeKey)
  ) {
    return;
  }

  if (state.api.inFlightByTypeKey.has(typeKey)) {
    await state.api.inFlightByTypeKey.get(typeKey);
    return;
  }

  const loadPromise = (async () => {
    async function fetchApiContextPayload(details = {}) {
      const url = buildApiUrlForCurrentState();
      const fetchSpan = startPerfMeasure("ranks:api-fetch", { typeKey, url, ...details });
      const responseSpan = startPerfMeasure("ranks:api-response", { typeKey, url, ...details });
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Client-Source": "ranks-html",
          "X-Client-Version": "api-v2",
        },
      });
      responseSpan.end({ ok: response.ok, status: response.status });

      if (!response.ok) {
        fetchSpan.end({ ok: false, status: response.status });
        return { ok: false, status: response.status, payload: null };
      }

      let payload;
      let payloadJson = null;
      if (typeof response.text === "function") {
        payloadJson = await response.text();
        payload = JSON.parse(payloadJson);
      } else {
        payload = await response.json();
      }
      fetchSpan.end({
        ok: true,
        status: response.status,
        itemCount: Array.isArray(payload?.items) ? payload.items.length : 0,
      });
      return { ok: true, status: response.status, payload, payloadJson };
    }

    // The manifest is checked before this loader runs. A matching payload is
    // safe to reuse because its lastUpdated must equal the manifest timestamp.
    if (!force && state.api.lastUpdateMs && state.api.staticFiles?.[typeKey]?.file) {
      const expectedLastUpdated = new Date(state.api.lastUpdateMs).toISOString();
      const cacheSpan = startPerfMeasure("ranks:cache-read", { typeKey });
      const cached = await readTypePayload(typeKey, { lastUpdated: expectedLastUpdated });
      const cacheValid =
        cached?.cacheVersion === TYPE_PAYLOAD_CACHE_VERSION &&
        cached?.typeKey === typeKey &&
        cached?.lastUpdated === expectedLastUpdated &&
        isCacheEnvelopeValid(
          {
            schemaVersion: EXPECTED_API_SCHEMA_VERSION,
            cacheVersion: cached?.cacheVersion,
            typeKey: cached?.typeKey,
            payload: cached?.payload,
          },
          typeKey,
        );
      cacheSpan.end({ hit: cacheValid });
      if (cacheValid) {
        const payload = cached.payload;
        const lastUpdated = payload.lastUpdated || cached.lastUpdated;
        if (typeKey !== buildApiTypeKey()) return;
        state.api.lastUpdated = lastUpdated;
        state.api.typeLastUpdated.set(typeKey, lastUpdated);
        const persistedBaseRows = await readPersistedBaseRowsForPayload(typeKey, lastUpdated);
        await applyApiPayloadAndRender(payload, { typeKey, lastUpdated, persistedBaseRows });
        state.api.activeTypeKey = typeKey;
        if (authClientState.status === AUTH_BOOT_STATE_AUTHENTICATED) {
          onSnapshotLastUpdatedChanged().catch(() => {
            // ignore snapshot sync failures
          });
          scheduleEngagementLoad();
        }
        return;
      }
    }

    let apiResult = await fetchApiContextPayload();
    if (!apiResult.ok && apiResult.status === 409) {
      await resolveSnapshotValidatorState();
      apiResult = await fetchApiContextPayload({ retry: "stale-last-update" });
    }
    if (!apiResult.ok) {
      throw new Error(`Failed to load API context: ${apiResult.status}`);
    }

    const payload = apiResult.payload;
    if (!payload?.ok) {
      throw new Error("API payload returned not ok");
    }
    if (typeKey !== buildApiTypeKey()) return;

    const existingLastUpdated = state.api.typeLastUpdated.get(typeKey);
    if (existingLastUpdated && payload.lastUpdated && payload.lastUpdated > existingLastUpdated) {
      state.api.cache.delete(typeKey);
      deleteBaseRowsCacheForType(typeKey);
      try {
        sessionStorage.removeItem(getApiSessionStorageKey(typeKey));
      } catch (_error) {
        // ignore storage failures
      }
    }

    state.api.lastUpdated = payload.lastUpdated || state.api.lastUpdated;
    state.api.typeLastUpdated.set(typeKey, payload.lastUpdated);
    const lastUpdated = payload.lastUpdated || null;
    const persistedBaseRows = await readPersistedBaseRowsForPayload(typeKey, lastUpdated);
    if (typeKey !== buildApiTypeKey()) return;
    await applyApiPayloadAndRender(payload, { typeKey, lastUpdated, persistedBaseRows });
    if (state.api.staticFiles?.[typeKey]?.file) {
      writeTypePayload(typeKey, {
        schemaVersion: "3.0",
        cacheVersion: TYPE_PAYLOAD_CACHE_VERSION,
        lastUpdated,
        savedAt: Date.now(),
        payloadJson: apiResult.payloadJson || undefined,
        payload,
      }).catch(() => {
        // ignore IndexedDB write failures
      });
    }
    state.api.activeTypeKey = typeKey;
    if (authClientState.status === AUTH_BOOT_STATE_AUTHENTICATED) {
      onSnapshotLastUpdatedChanged().catch(() => {
        // ignore snapshot sync failures
      });
      scheduleEngagementLoad();
    }
  })();

  state.api.inFlightByTypeKey.set(typeKey, loadPromise);
  try {
    await loadPromise;
  } finally {
    state.api.inFlightByTypeKey.delete(typeKey);
  }
}

/**
 * Inject CSS rules for category views dynamically
 */
function injectCategoryCssRules() {
  // Check if already injected
  if (document.getElementById("category-view-styles")) return;

  const style = document.createElement("style");
  style.id = "category-view-styles";

  // Generate rules for all categories, modes, and pricing contexts.
  const rules = [];
  for (const categoryId of getAllCategoryIds()) {
    for (const mode of ["popular", "best30", "best90"]) {
      for (const tab of ["all", "free", "paid"]) {
        const context = `${mode === "popular" ? "" : `${mode}-`}${categoryId}-${tab}`;
        rules.push(`.view-${context} .rank-cell .r-${context} { display: inline !important; }`);
        rules.push(`.view-${context} .change-cell .c-${context} { display: inline !important; }`);
        rules.push(`.view-${context} .trend-cell .t-${context} { display: inline !important; }`);
        rules.push(`.view-${context} .category-rank .crr-${tab} { display: inline !important; }`);
      }
    }
  }
  for (const mode of ["popular", "best30", "best90"]) {
    for (const tab of ["all", "free", "paid"]) {
      const context = `${mode === "popular" ? "" : `${mode}-`}global-${tab}`;
      rules.push(`.view-${context} .rank-cell .r-${context} { display: inline !important; }`);
      rules.push(`.view-${context} .change-cell .c-${context} { display: inline !important; }`);
      rules.push(`.view-${context} .trend-cell .t-${context} { display: inline !important; }`);
      rules.push(`.view-${context} .category-rank .crr-${tab} { display: inline !important; }`);
    }
  }

  style.textContent = rules.join("\n");
  document.head.appendChild(style);
  log(`[CSS] Injected ${rules.length / 2} category view rules`);
}

/**
 * Update container CSS class for span visibility
 */
function updateViewClass() {
  const container = document.getElementById("rankings-table-container");
  if (!container) return;

  // Remove all view-* classes
  const classes = Array.from(container.classList);
  for (const cls of classes) {
    if (cls.startsWith("view-")) {
      container.classList.remove(cls);
    }
  }

  // Add current view class
  const context = getCurrentContext();
  container.classList.add(`view-${context}`);
  container.classList.toggle("components-table", state.type !== "template" && state.type !== "creator");
  container.classList.toggle("creators-table", state.type === "creator");
  container.classList.toggle("category-view", Boolean(state.category));
  log(`[CSS] View class set to: view-${context}`);
}

// ============================================
// FILTERING
// ============================================

/**
 * Filter rows based on current state
 * Returns indices into allRows that match current filters
 */
function filterRows() {
  log(
    `[FILTER] Filtering: type=${state.type}, tab=${state.tab}, category=${state.category || "none"}, search="${state.search}"`,
  );
  const startTime = performance.now();

  const filterCacheKey = buildFilterCacheKey();
  if (filterCache.has(filterCacheKey)) {
    const cachedRows = filterCache.get(filterCacheKey);
    if (cachedRows) {
      filterCache.delete(filterCacheKey);
      filterCache.set(filterCacheKey, cachedRows);
      log(`[FILTER] Cache HIT: ${filterCacheKey} (${cachedRows.length} rows)`);
      return cachedRows;
    }
  }

  const filtered = [];
  const parsedSearch = parseSearchCommands(state.search);
  const query = normalizeSearchValue(parsedSearch.textQuery);
  const searchCommands = parsedSearch.commands;
  const context = getCurrentContext();
  const topPercentTotal = state.type === "creator" ? 0 : getContextPopulationTotal(buildApiTypeKey(), context);

  for (let i = 0; i < state.allRows.length; i++) {
    const row = state.allRows[i];
    const item = getRowItem(row);
    if (!item) continue;

    // Filter by type
    if (item.type !== state.type) continue;

    if (state.type === "creator") {
      if (searchCommands.length > 0 && !itemMatchesSearchCommands(item, searchCommands, { context, topPercentTotal })) {
        continue;
      }
      if (query && !matchesSearch(item, query, state.searchFields)) continue;
      filtered.push(row);
      continue;
    }

    // Filter by tab (free/paid items) only for global views.
    // In category mode we trust category context ranks (e.g. cat-lastweek-free),
    // not global item.price, which can be stale relative to category snapshots.
    if (!state.category) {
      if (state.tab === "free" && item.price !== "Free") continue;
      if (state.tab === "paid" && item.price === "Free") continue;
    }

    // Filter by category (templates only) — use rank context to include closure-expanded subcategory items
    if (state.category || (state.type === "expert" && state.expertType)) {
      const rankEntry = item.ranks?.[context];
      if (!rankEntry || rankEntry[0] === null) continue;
    }

    // Filter by search
    if (searchCommands.length > 0 && !itemMatchesSearchCommands(item, searchCommands, { context, topPercentTotal })) {
      continue;
    }
    if (query && !matchesSearch(item, query, state.searchFields)) continue;

    filtered.push(row);
  }

  const duration = Math.round(performance.now() - startTime);
  log(`[FILTER] Filtered to ${filtered.length} rows in ${duration}ms`);

  while (filterCache.size >= FILTER_CACHE_MAX_SIZE) {
    const oldestKey = filterCache.keys().next().value;
    filterCache.delete(oldestKey);
  }
  filterCache.set(filterCacheKey, filtered);

  return filtered;
}

// ============================================
// SORTING
// ============================================

/**
 * Sort the filtered rows by current sort settings
 * Note: This function does NOT acquire locks - caller must handle locking
 */
function sortFilteredRows() {
  const startTime = performance.now();
  const context = getCurrentContext();

  // Build explicit cache key from full filter state
  const cacheKey = buildCacheKey({
    type: state.type,
    category: state.category,
    tab: state.tab,
    column: state.sortColumn,
    direction: state.sortDirection,
    search: state.search,
  }).concat(`-ctx${context}`);

  log(
    `[SORT] Sorting: column=${state.sortColumn}, direction=${state.sortDirection}, context=${context}, cacheKey=${cacheKey}`,
  );

  // Get items from filtered rows for sorting
  const items = state.filteredRows.map((row) => getRowItem(row)).filter(Boolean);

  // Get sorted indices with explicit cache key
  const indices = getSortedIndices(items, state.sortColumn, state.sortDirection, context, cacheKey);

  // Reorder filtered rows
  state.sortedIndices = indices;

  const duration = Math.round(performance.now() - startTime);
  log(`[SORT] Sorted ${indices.length} rows in ${duration}ms`);
}

// ============================================
// CLUSTERIZE UPDATE
// ============================================

/**
 * Update Clusterize with current sorted/filtered rows
 */
function buildHtmlRowsForCurrentView() {
  const htmlRowsStart = performance.now();
  const rows = state.sortedIndices.map((i) => state.filteredRows[i]).filter(Boolean);
  let htmlRows = progressiveRenderState.active
    ? buildProgressiveClusterRows()
    : rows.map((row) => row.html).filter(Boolean);

  if (htmlRows.length === 0) {
    const emptyMessage = state.type === "creator" ? "No creators found" : "No results found";
    htmlRows = [buildNoResultsRow(emptyMessage)];
  }
  htmlRows.push(buildFooterStretchRow());
  const htmlRowsMs = performance.now() - htmlRowsStart;
  return { htmlRows, htmlRowsMs };
}

function updateClusterize(cachedHtmlRows = null) {
  if (!state.clusterize) return { clusterUpdateMs: 0, sparklineMs: 0, htmlRowsMs: 0, clusterMs: 0 };

  const startTime = performance.now();

  const htmlRowsResult = Array.isArray(cachedHtmlRows)
    ? { htmlRows: cachedHtmlRows, htmlRowsMs: 0 }
    : buildHtmlRowsForCurrentView();
  const { htmlRows, htmlRowsMs } = htmlRowsResult;
  const updateStartedAt = performance.now();
  state.clusterize.update(htmlRows);
  if (shouldUsePostHydrateContextCells()) {
    const contextCellsSpan = startPerfMeasure("ranks:init-context-cells", {});
    hydrateVisibleContextCells();
    contextCellsSpan.end();
  }
  scheduleClusterizeLayoutRefresh("update");
  const clusterUpdateMs = performance.now() - updateStartedAt;

  // Initialize sparklines for newly rendered rows
  const sparklinesStartedAt = performance.now();
  initSparklines();
  const sparklineMs = performance.now() - sparklinesStartedAt;

  const duration = Math.round(performance.now() - startTime);
  log(`[CLUSTERIZE] Updated with ${htmlRows.length} rows in ${duration}ms`);
  return { clusterUpdateMs, sparklineMs, htmlRowsMs, clusterMs: performance.now() - startTime, htmlRows };
}

function reconcileClusterizeRowHeight(reason = "unknown") {
  if (!state.clusterize?.options) return;
  const container = document.getElementById("clusterize-content");
  // Progressive mode still needs this correction: the initial Clusterize
  // measurement can accidentally use a bootstrap/table spacer height instead
  // of a real row, which makes clusters far too large and causes blank loops
  // while scrolling. The bootstrap-only handoff remains deferred.
  if (container?.dataset.bootstrapRows === "1") return;
  const firstDataRow = document.querySelector("#clusterize-content tr[data-id]");
  const measuredRowHeight = firstDataRow?.offsetHeight || 0;
  const currentRowHeight = Number(state.clusterize.options.item_height || 0);
  if (measuredRowHeight <= 0 || Math.abs(measuredRowHeight - currentRowHeight) <= 2) return;

  state.clusterize.refresh(true);
  if (shouldUsePostHydrateContextCells()) {
    hydrateVisibleContextCells();
  }
  initSparklines();
  log(`[CLUSTERIZE] Refreshed stale row height (${reason}): ${currentRowHeight}px -> ${measuredRowHeight}px`);
}

function scheduleClusterizeLayoutRefresh(reason = "unknown") {
  const token = ++clusterizeLayoutRefreshToken;
  const run = () => {
    if (token !== clusterizeLayoutRefreshToken) return;
    reconcileClusterizeRowHeight(reason);
  };

  if (typeof requestAnimationFrame !== "function") {
    window.setTimeout(run, 0);
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
}

function getContextCellCacheKey(typeKey, context, rowId) {
  const lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || "_";
  return `${CONTEXT_CELL_CACHE_VERSION}|${typeKey}|${lastUpdated}|${context}|${rowId}`;
}

function getContextPopulationCacheKey(typeKey, context) {
  const lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || "_";
  return `${typeKey}|${lastUpdated}|${context}`;
}

function hasFiniteRankForContext(item, context) {
  const rank = item?.ranks?.[context]?.[0];
  return Number.isFinite(Number(rank));
}

function itemMatchesTopPercentContext(item, context) {
  if (!item || item.type !== state.type || state.type === "creator") return false;
  if (!hasFiniteRankForContext(item, context)) return false;

  if (!state.category) {
    if (state.tab === "free" && item.price !== "Free") return false;
    if (state.tab === "paid" && item.price === "Free") return false;
    return true;
  }

  return Array.isArray(item.categories) && item.categories.includes(state.category);
}

function getContextPopulationTotal(typeKey, context) {
  if (state.type === "creator") return 0;
  const cacheKey = getContextPopulationCacheKey(typeKey, context);
  const cached = state.api.contextPopulationCache.get(cacheKey);
  if (Number.isFinite(cached)) return cached;

  let total = 0;
  for (const item of state.allItems) {
    if (itemMatchesTopPercentContext(item, context)) total += 1;
  }
  state.api.contextPopulationCache.set(cacheKey, total);
  return total;
}

function getOrderedRowsForCurrentView() {
  return state.sortedIndices.map((index) => state.filteredRows[index]).filter(Boolean);
}

function getCsvDataLastUpdated() {
  const typeKey = buildApiTypeKey();
  return state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || new Date().toISOString();
}

function getCsvCategoryMetadataByType() {
  return {
    template: state.templateCategoryMeta || state.categoryMeta || {},
    component: state.componentCategoryMeta || {},
    plugin: state.pluginCategoryMeta || {},
    vector: state.vectorCategoryMeta || {},
    tutorial: state.tutorialCategoryMeta || {},
    category: state.categoryMeta || {},
  };
}

function buildCsvExportInput(rows = getOrderedRowsForCurrentView()) {
  const context = getCurrentContext();
  const typeKey = buildApiTypeKey();
  return {
    type: state.type,
    rows,
    resolveItem: getRowItem,
    context,
    categoryMetadataByType: getCsvCategoryMetadataByType(),
    topPercentTotal: getContextPopulationTotal(typeKey, context),
  };
}

function buildCsvFilenameForCurrentView() {
  return buildCsvFilename({
    dataLastUpdated: getCsvDataLastUpdated(),
    type: state.type,
    tab: state.tab,
    category: state.category,
    sortColumn: state.sortColumn,
    sortDirection: state.sortDirection,
    search: state.search,
  });
}

function buildCsvForCurrentView() {
  return buildCsv(buildCsvExportInput());
}

function updateCsvDownloadButtonState() {
  const button = document.getElementById("gated-download-csv-btn");
  if (!button) return;
  const hasRows = getOrderedRowsForCurrentView().length > 0;
  button.disabled = canSeeGatedMetrics() && !hasRows;
  button.removeAttribute("title");
  const tooltip = document.getElementById("gated-download-csv-tooltip-text");
  if (tooltip) {
    tooltip.textContent = canSeeGatedMetrics()
      ? hasRows
        ? "Download the current visible list as CSV"
        : "No rows to download"
      : "Log In to export CSV. It's free";
  }
}

function bindCsvDownloadButton() {
  const button = document.getElementById("gated-download-csv-btn");
  if (!button || button.dataset.csvDownloadBound) return;
  button.dataset.csvDownloadBound = "true";
  button.addEventListener("click", () => {
    downloadCsvForCurrentView();
  });
}

function downloadCsvForCurrentView() {
  if (!canSeeGatedMetrics()) {
    trackEvent("auth_modal_opened", { source: "csv_download" });
    void postClientAuthEvent("auth_modal_opened");
    openSignInModal();
    return false;
  }
  const rows = getOrderedRowsForCurrentView();
  if (rows.length === 0) {
    updateCsvDownloadButtonState();
    return false;
  }
  const csv = buildCsvForCurrentView();
  const filename = buildCsvFilenameForCurrentView();
  trackEvent("csv_download_clicked", {
    row_count: rows.length,
    tab: state.tab,
    category: state.category || "all",
    sort_column: state.sortColumn,
    sort_direction: state.sortDirection,
    has_search: Boolean(state.search),
  });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
}

function getRowItem(row) {
  if (!row) return null;
  if (row.item) return row.item;
  return state.allItems[row.index] || null;
}

function getRowById(rowId) {
  return state.rowLookup.get(rowId) || state.allRows.find((row) => row?.itemId === rowId) || null;
}

function recordContextInvariantFailure(details) {
  if (typeof window === "undefined") return;
  window.__RANKS_CONTEXT_INVARIANT_FAILURES__ = (window.__RANKS_CONTEXT_INVARIANT_FAILURES__ || 0) + 1;
  console.error("[RANKS_CONTEXT_INVARIANT]", details);
}

function applyContextCellFragments(rowElement, fragments, context) {
  if (!rowElement || !fragments || fragments.context !== context) {
    recordContextInvariantFailure({
      reason: "fragment-context-mismatch",
      expected: context,
      received: fragments?.context || null,
      rowId: rowElement?.getAttribute("data-id") || null,
    });
    return false;
  }

  rowElement.dataset.renderedContext = context;
  for (const [fragmentName, selector] of CONTEXT_CELL_SELECTORS) {
    const cell = rowElement.querySelector(selector);
    if (!cell) continue;
    cell.innerHTML = fragments[fragmentName] || "";
    cell.dataset.renderedContext = context;
  }
  return true;
}

function hydrateVisibleContextCells() {
  if (!shouldUsePostHydrateContextCells()) return;
  const rows = document.querySelectorAll("#clusterize-content tr[data-id]");
  if (!rows.length) return;
  const typeKey = buildApiTypeKey();
  const context = getCurrentContext();
  const topPercentTotal = getContextPopulationTotal(typeKey, context);
  const renderOptions = { ...getRenderCategoryOptions(), activeContext: context, topPercentTotal };

  rows.forEach((tr) => {
    const rowId = tr.getAttribute("data-id");
    if (!rowId) return;
    const row = getRowById(rowId);
    const item = getRowItem(row);
    if (!item) return;
    const cacheKey = getContextCellCacheKey(typeKey, context, rowId);
    let fragments = state.api.contextCellCache.get(cacheKey);
    if (!fragments) {
      fragments = renderContextCellsForContext(item, context, renderOptions);
      state.api.contextCellCache.set(cacheKey, fragments);
    }
    applyContextCellFragments(tr, fragments, context);
  });
}

function updateVisibleProgressiveContextRows() {
  if (!state.clusterize || !progressiveRenderState.active) return;
  const visibleRows = state.sortedIndices
    .slice(0, getProgressiveInitialRowCount())
    .map((index) => state.filteredRows[index])
    .filter(Boolean);
  const renderOptions = getRenderCategoryOptions();
  for (const row of visibleRows) {
    const item = getRowItem(row);
    if (!item) continue;
    // These rows may already contain the previous context's cells. Rebuild
    // only the visible window so a context switch cannot show stale ranks.
    row.html = "";
    const rendered = renderRowRecords([item], row.index, renderOptions)[0];
    if (rendered) row.html = rendered.html;
  }
  const htmlRows = buildProgressiveClusterRows();
  htmlRows.push(buildFooterStretchRow());
  state.clusterize.update(htmlRows);
  const container = document.getElementById("clusterize-content");
  if (container) {
    container.dataset.progressiveRows = "1";
    container.querySelector(".clusterize-progressive-spacer")?.remove();
  }
  scheduleVisibleThumbnailHydration();
}

function hydrateVisibleThumbnails() {
  const images = Array.from(document.querySelectorAll("#clusterize-content img.thumbnail-deferred[data-src]"));
  const hydrateBatch = (start) => {
    const end = Math.min(images.length, start + 1);
    images.slice(start, end).forEach((image) => {
      const source = image.getAttribute("data-src");
      if (!source) return;
      image.setAttribute("src", source);
      image.removeAttribute("data-src");
      image.classList.remove("thumbnail-deferred");
    });
    if (end >= images.length) return;
    const continueHydration = () => hydrateBatch(end);
    if (typeof window.requestIdleCallback === "function")
      window.requestIdleCallback(continueHydration, { timeout: 500 });
    else window.setTimeout(continueHydration, 0);
  };
  hydrateBatch(0);
}

function scheduleVisibleThumbnailHydration(delayMs = 250) {
  state.thumbnailHydrationToken += 1;
  const token = state.thumbnailHydrationToken;
  if (state.thumbnailHydrationTimer) {
    window.clearTimeout(state.thumbnailHydrationTimer);
    state.thumbnailHydrationTimer = null;
  }
  state.thumbnailHydrationTimer = window.setTimeout(() => {
    if (token !== state.thumbnailHydrationToken) return;
    state.thumbnailHydrationTimer = null;
    hydrateVisibleThumbnails();
  }, delayMs);
}

function updateVirtualizer(cachedHtmlRows = null, { allowBootstrap = false } = {}) {
  const bootstrapContainer = document.getElementById("clusterize-content");
  if (!allowBootstrap && bootstrapContainer?.dataset.progressiveRows === "1") {
    if (!progressiveRenderState.active) {
      // Background hydration has finished, but the passive handoff may still
      // be deferred until scroll. Explicit view changes must take ownership
      // immediately or the old DOM order can receive the new context cells.
      progressiveRenderState.bootstrapUpdatePending = false;
      bootstrapContainer.dataset.progressiveRows = "0";
      bootstrapContainer.dataset.bootstrapRows = "0";
      bootstrapContainer.querySelector(".clusterize-progressive-spacer")?.remove();
    } else {
      progressiveRenderState.bootstrapUpdatePending = true;
      // Context switches can happen while background rows are still hydrating.
      // Clusterize must stay deferred, but the already-visible rank/delta/trend
      // cells must switch context immediately instead of showing stale Popular
      // values after selecting Best30/Best90 or a category.
      updateVisibleProgressiveContextRows();
      if (shouldUsePostHydrateContextCells()) hydrateVisibleContextCells();
      return { clusterUpdateMs: 0, sparklineMs: 0, htmlRowsMs: 0, clusterMs: 0 };
    }
  }
  if (
    !allowBootstrap &&
    bootstrapContainer?.dataset.bootstrapRows === "1" &&
    state.allItems.length > getProgressiveInitialRowCount()
  ) {
    if (!progressiveRenderState.active) {
      progressiveRenderState.bootstrapUpdatePending = false;
      bootstrapContainer.dataset.bootstrapRows = "0";
      bootstrapContainer.dataset.progressiveRows = "0";
      bootstrapContainer.querySelector(".clusterize-progressive-spacer")?.remove();
    } else {
      progressiveRenderState.bootstrapUpdatePending = true;
      updateVisibleProgressiveContextRows();
      if (shouldUsePostHydrateContextCells()) hydrateVisibleContextCells();
      return { clusterUpdateMs: 0, sparklineMs: 0, htmlRowsMs: 0, clusterMs: 0 };
    }
  }
  const span = startPerfMeasure("ranks:virtualizer-update", { virtualizer: "clusterize", type: state.type });
  const result = updateClusterize(cachedHtmlRows);
  scheduleVisibleThumbnailHydration();
  span.end({ rows: state.filteredRows.length });
  return result;
}

/**
 * Initialize sparklines for all elements with data-sparkline="true"
 */
function initSparklines() {
  const sparklines = document.querySelectorAll('[data-sparkline="true"]');
  log(`[SPARKLINES] Initializing ${sparklines.length} sparklines`);

  for (const sparkline of sparklines) {
    // renderSparkline() already outputs inline SVG; skip expensive re-init.
    if (sparkline.querySelector("svg")) continue;
    initSparkline(sparkline);
  }
}

function refreshSync() {
  const refreshStartedAt = performance.now();
  // Expert rows contain rank/delta spans for the active type/country context.
  // Rebuild the row shells before applying a search command so a filtered view
  // cannot reuse HTML rendered for the previous context (for example global
  // ranks after clicking the Pro "new today" badge).
  if (state.type === "expert") rerenderExpertRowsForContext();
  const typeKey = buildApiTypeKey();
  const lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || null;
  const viewCacheKey = buildViewCacheKey(typeKey, lastUpdated);
  const cachedView = getViewCacheEntry(viewCacheKey);
  let viewCacheHit = false;
  let filterMs = 0;
  let sortMs = 0;
  let clusterMs = 0;
  let clusterMetrics = { clusterUpdateMs: 0, sparklineMs: 0, htmlRowsMs: 0, clusterMs: 0 };

  if (cachedView) {
    viewCacheHit = true;
    state.filteredRows = (cachedView.filteredRowIndices || []).map((index) => state.allRows[index]).filter(Boolean);
    state.sortedIndices = cachedView.sortedIndices;
    const cachedHtmlRows = getAssembledViewCacheEntry(viewCacheKey);
    startPerfMeasure("ranks:view-cache", {
      typeKey,
      hit: true,
      rows: state.filteredRows.length,
      assembledHtmlHit: Boolean(cachedHtmlRows),
    }).end();
    const clusterStart = performance.now();
    if (cachedHtmlRows) {
      clusterMetrics = updateVirtualizer(cachedHtmlRows);
    } else {
      const htmlRowsResult = buildHtmlRowsForCurrentView();
      saveAssembledViewCache(viewCacheKey, htmlRowsResult.htmlRows);
      clusterMetrics = updateVirtualizer(htmlRowsResult.htmlRows);
    }
    clusterMs = performance.now() - clusterStart;
  } else {
    const viewCacheSpan = startPerfMeasure("ranks:view-cache", { typeKey, hit: false });

    // Note: Cache key now includes item count, so no need to clear manually
    const filterStart = performance.now();
    state.filteredRows = filterRows();
    filterMs = performance.now() - filterStart;

    const sortStart = performance.now();
    sortFilteredRows();
    sortMs = performance.now() - sortStart;

    const htmlRowsResult = buildHtmlRowsForCurrentView();
    saveCurrentViewCache();
    saveAssembledViewCache(viewCacheKey, htmlRowsResult.htmlRows);
    viewCacheSpan.end({ rows: state.filteredRows.length });

    const clusterStart = performance.now();
    clusterMetrics = updateVirtualizer(htmlRowsResult.htmlRows);
    clusterMs = performance.now() - clusterStart;
  }

  const indicatorsStart = performance.now();
  updateSortIndicators();
  const indicatorsMs = performance.now() - indicatorsStart;

  const titleStart = performance.now();
  updateTableTitle(); // Update static count in title
  const titleMs = performance.now() - titleStart;

  const footerStart = performance.now();
  updateTableFooter(); // Update dynamic stats in footer
  const footerMs = performance.now() - footerStart;
  updateCsvDownloadButtonState();
  renderVisibleGatedMetrics();

  const totalMs = performance.now() - refreshStartedAt;
  recordPerf({
    at: new Date().toISOString(),
    view: getCurrentContext(),
    type: state.type,
    rows: state.filteredRows.length,
    viewCacheHit,
    filterMs: Math.round(filterMs * 100) / 100,
    sortMs: Math.round(sortMs * 100) / 100,
    clusterMs: Math.round(clusterMs * 100) / 100,
    clusterUpdateMs: Math.round((clusterMetrics?.clusterUpdateMs ?? 0) * 100) / 100,
    htmlRowsMs: Math.round((clusterMetrics?.htmlRowsMs ?? 0) * 100) / 100,
    sparklineMs: Math.round((clusterMetrics?.sparklineMs ?? 0) * 100) / 100,
    indicatorsMs: Math.round(indicatorsMs * 100) / 100,
    titleMs: Math.round(titleMs * 100) / 100,
    footerMs: Math.round(footerMs * 100) / 100,
    totalMs: Math.round(totalMs * 100) / 100,
  });
}

/**
 * Full refresh: filter, sort, and update display
 */
async function refresh(options = {}) {
  const { beforeSync = null } = options;
  const requestedContextStateKey = getContextStateKey();
  const span = startPerfMeasure("ranks:refresh", { apiEnabled: state.api.enabled, type: state.type });
  if (state.api.enabled) {
    try {
      await loadApiContextForCurrentState(false);
    } catch (error) {
      console.error("[API_MODE] Failed to refresh context:", error);
      if (typeof window !== "undefined" && typeof window.showToast === "function") {
        window.showToast("Failed to load data. Please retry.", "error", () => refresh());
      }
      span.end({ ok: false, stale: false });
      return { ok: false, stale: false };
    }
  }

  if (requestedContextStateKey !== getContextStateKey()) {
    span.end({ ok: false, stale: true });
    return { ok: false, stale: true };
  }

  if (typeof beforeSync === "function") beforeSync();
  refreshSync();
  span.end({ ok: true, stale: false, rows: state.filteredRows.length });
  return { ok: true, stale: false, rows: state.filteredRows.length };
}

// ============================================
// EVENT HANDLERS
// ============================================

function refreshGatedMetricsForVisibleList() {
  if (!supportsGatedMetricsForType()) {
    clearGatedMetricsForUnsupportedContext();
    return Promise.resolve({ ok: true, reason: "unsupported-context", data: null });
  }
  return refreshGatedMetricsIfAuthenticated();
}

async function refreshVisibleListAndGatedMetrics(options = {}) {
  const requestedContextStateKey = getContextStateKey();
  const refreshResult = await refresh(options);
  if (refreshResult?.stale || requestedContextStateKey !== getContextStateKey()) return refreshResult;
  await refreshGatedMetricsForVisibleList();
  return refreshResult;
}

/**
 * Handle type switch (templates/components)
 */
function handleTypeSwitch(newType) {
  if (newType === "tutorial" || newType === "creator") {
    return;
  }
  if (state.type === newType && contextTransitionState.pendingType && contextTransitionState.pendingType !== newType) {
    cancelPendingContextTransition();
    return Promise.resolve();
  }
  const fromType = state.type;
  const isReselectingSameTypeFromCategory =
    state.type === newType && state.category !== null && NON_CREATOR_TYPES.has(newType);
  const isLeavingCreators = state.type === "creator" && newType !== "creator";
  if (state.type === newType && !isReselectingSameTypeFromCategory && contextTransitionState.pendingType !== newType)
    return;
  contextTransitionState.pendingType = newType;
  const clickSpan = startPerfMeasure("ranks:type-click-to-next-paint", { fromType, toType: newType });
  document.body.classList.add("ranks-type-switching");
  return runContextTransition(() => {
    const switchSpan = startPerfMeasure("ranks:type-switch", { fromType, toType: newType });
    log(`[TYPE] Switching to ${newType}`);
    state.type = newType;
    if (!typeSupportsRankingMode(newType)) state.rankingMode = "popular";
    state.category = null; // Clear category when switching type or reselecting same type
    state.expertType = null;

    if (newType === "creator") {
      state.tab = getDefaultTabForType(newType);
      const defaultsForType = getDefaultSortForType("creator");
      state.sortColumn = defaultsForType.sortColumn;
      state.sortDirection = defaultsForType.sortDirection;
    }

    if (isLeavingCreators) {
      state.tab = "all";
    }

    // Reselecting the same non-creator type while in category view snaps to global default.
    if (isReselectingSameTypeFromCategory) {
      state.tab = "all";
    }

    if (newType !== "creator") {
      if (!typeSupportsPricing(newType)) {
        state.tab = "all";
      } else if (newType === "template") {
        state.tab = getDefaultTabForType(newType);
      } else if (fromType === "template" && state.tab === getDefaultTabForType("template")) {
        state.tab = getDefaultTabForType(newType);
      }
      const defaultsForType = getDefaultSortForType(newType);
      state.sortColumn = defaultsForType.sortColumn;
      state.sortDirection = defaultsForType.sortDirection;
    }

    renderCategoryTree();
    updateCategoryDropdownText(getDefaultCategoryDropdownLabel()); // Reset dropdown text
    updateCategorySelectedState(null); // Clear selected category in dropdown
    updateExpertTypeDropdownText();
    disableFreePaidTabs(!typeSupportsPricing(state.type));
    updateCategoryDropdownVisibility(); // Ensure components-mode is set
    updateControlDisabling();
    updateSearchFieldControls();

    updateTabButtonStyles();
    syncUrlWithState();

    // Scroll to top
    scrollVirtualizerToTop();

    trackEvent(`tab_clicked_${STATE_TO_URL_TYPE[newType] || newType}`);
    return refreshVisibleListAndGatedMetrics({
      beforeSync: () => {
        updateViewClass();
        updateColumnHeadersForMode();
      },
    }).finally(() => {
      switchSpan.end({ rows: state.filteredRows.length });
      measureNextPaint("ranks:type-next-paint", { fromType, toType: newType }).then(() => {
        clickSpan.end({ rows: state.filteredRows.length });
      });
    });
  }).finally(() => {
    if (contextTransitionState.pendingType === newType) contextTransitionState.pendingType = null;
    document.body.classList.remove("ranks-type-switching");
  });
}

/**
 * Handle tab switch (all/free/paid)
 */
function handleTabSwitch(newTab) {
  if (!typeSupportsPricing(state.type)) return;
  if (shouldDisablePricingTabsForCurrentContext() && (newTab === "free" || newTab === "paid")) return;
  if (state.tab === newTab) return;
  return runContextTransition(() => {
    log(`[TAB] Switching to ${newTab}`);
    state.tab = newTab;
    if (!state.category) {
      state.category = null; // Keep existing global behavior explicit
    }
    disableFreePaidTabs(shouldDisablePricingTabsForCurrentContext());

    // Update sort to match tab
    const defaultsForType = getDefaultSortForType(state.type);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    updateTabButtonStyles();
    syncUrlWithState();

    // Scroll to top
    scrollVirtualizerToTop();

    trackEvent(`tab_clicked_${newTab}`, { table: state.type });
    return refreshVisibleListAndGatedMetrics();
  });
}

/**
 * Handle category selection
 */
function handleCategorySelect(categoryId, categoryName) {
  if (state.type === "creator") return;
  return runContextTransition(() => {
    log(`[CATEGORY] Selected: ${categoryName} (${categoryId})`);

    state.category = categoryId;
    const defaultsForType = getDefaultSortForType(state.type);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    updateTabButtonStyles();
    updateCategoryDropdownText(categoryName);
    updateCategorySelectedState(categoryId);
    disableFreePaidTabs(!typeSupportsPricing(state.type));
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    rerenderExpertRowsForContext();
    syncUrlWithState();

    if (shouldCloseCategoryDropdownOnCategorySelect()) {
      closeCategoryDropdown();
    }

    if (state.type === "expert") {
      trackEvent("country_selected", { countryId: categoryId, countryName: categoryName, source: "dropdown" });
    } else {
      trackEvent("category_selected", { categoryId, categoryName });
    }
    return refreshVisibleListAndGatedMetrics();
  });
}

/**
 * Handle category clear
 */
function handleCategoryClear() {
  if (state.type === "creator") return;
  return runContextTransition(() => {
    log(`[CATEGORY] Cleared`);

    state.category = null;
    const defaultsForType = getDefaultSortForType(state.type);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    updateCategoryDropdownText(getDefaultCategoryDropdownLabel());
    updateCategorySelectedState(null);
    disableFreePaidTabs(!typeSupportsPricing(state.type));
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    rerenderExpertRowsForContext();
    syncUrlWithState();

    trackEvent(
      state.type === "expert" ? "country_cleared" : "category_cleared",
      state.type === "expert" ? { source: "dropdown" } : {},
    );
    return refreshVisibleListAndGatedMetrics();
  });
}

function handleMedalJump(categoryId, rank, market = "all", itemType = null, itemIdForScroll = null) {
  if (!categoryId) return;
  return runContextTransition(() => {
    const nextType = NON_CREATOR_TYPES.has(itemType)
      ? itemType
      : NON_CREATOR_TYPES.has(state.type)
        ? state.type
        : "template";
    const categoryMetaForType = getCategoryMetaForType(nextType, state) || {};
    const categoryName = categoryMetaForType?.[categoryId]?.name || categoryId;
    const nextTab = market === "free" || market === "paid" ? market : "all";

    state.type = nextType;
    state.category = categoryId;
    if (!typeSupportsRankingMode(nextType)) state.rankingMode = "popular";
    state.tab = typeSupportsPricing(nextType) ? nextTab : "all";
    const defaultsForType = getDefaultSortForType(nextType);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    updateTabButtonStyles();
    updateCategoryDropdownText(categoryName);
    updateCategorySelectedState(categoryId);
    disableFreePaidTabs(!typeSupportsPricing(nextType));
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    if (nextType === "expert") rerenderExpertRowsForContext();
    syncUrlWithState();
    closeCategoryDropdown();
    scrollVirtualizerToTop();

    trackEvent("medal_jump_clicked", {
      categoryId,
      rank,
      market: nextTab,
      itemType: nextType,
    });
    return refreshVisibleListAndGatedMetrics().then(() => {
      scheduleScrollToItemInCurrentView(itemIdForScroll);
    });
  });
}

function handleCategoryJump(categoryId, itemType = null, itemIdForScroll = null) {
  if (!categoryId) return;
  return runContextTransition(() => {
    const nextType = NON_CREATOR_TYPES.has(itemType)
      ? itemType
      : NON_CREATOR_TYPES.has(state.type)
        ? state.type
        : "template";
    const categoryMetaForType = getCategoryMetaForType(nextType, state) || {};
    const categoryName = categoryMetaForType?.[categoryId]?.name || categoryId;

    const nextTab = typeSupportsPricing(nextType) ? state.tab || "all" : "all";
    state.type = nextType;
    state.category = categoryId;
    if (!typeSupportsRankingMode(nextType)) state.rankingMode = "popular";
    state.tab = nextTab;
    const defaultsForType = getDefaultSortForType(nextType);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    updateTabButtonStyles();
    updateCategoryDropdownText(categoryName);
    updateCategorySelectedState(categoryId);
    disableFreePaidTabs(!typeSupportsPricing(nextType));
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    if (nextType === "expert") rerenderExpertRowsForContext();
    syncUrlWithState();
    closeCategoryDropdown();

    trackEvent("category_jump_clicked", {
      categoryId,
      itemType: nextType,
      tab: state.tab,
    });
    return refreshVisibleListAndGatedMetrics().then(() => {
      scheduleScrollToItemInCurrentView(itemIdForScroll);
    });
  });
}

function scheduleScrollToItemInCurrentView(itemId) {
  if (!itemId) return;
  const targetId = String(itemId);
  const attempt = () => {
    const scroller = getVirtualizerScroller();
    if (!scroller) return;
    const targetSortedIndex = state.sortedIndices.findIndex(
      (idx) => String(state.filteredRows[idx]?.itemId ?? "") === targetId,
    );
    if (targetSortedIndex < 0) return;

    const escapedItemId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(targetId) : targetId;
    const rowEl = document.querySelector(`#clusterize-content tr[data-id="${escapedItemId}"]`);
    if (rowEl) {
      const rowRect = rowEl.getBoundingClientRect();
      const scrollRect = scroller.getBoundingClientRect();
      const rowTopInScroller = rowRect.top - scrollRect.top + scroller.scrollTop;
      const desiredTop = rowTopInScroller - (scroller.clientHeight - rowRect.height) / 2;
      const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      scroller.scrollTop = Math.max(0, Math.min(maxScrollTop, desiredTop));
      highlightJumpTargetRow(targetId);
      return;
    }

    const probeRow = document.querySelector("#clusterize-content tr[data-id]:not(.tfoot-spacer-row)");
    const estimatedRowHeight = probeRow?.getBoundingClientRect().height || 36;
    const desiredTop = targetSortedIndex * estimatedRowHeight - (scroller.clientHeight - estimatedRowHeight) / 2;
    const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = Math.max(0, Math.min(maxScrollTop, desiredTop));
  };

  requestAnimationFrame(() => {
    attempt();
    requestAnimationFrame(() => {
      attempt();
      requestAnimationFrame(() => {
        highlightJumpTargetRow(targetId);
      });
    });
  });
}

function hydrateLazyCategoryTooltip(trigger) {
  if (!trigger) return false;
  const rowEl = trigger.closest?.("[data-id]");
  const targetId = rowEl?.getAttribute("data-id");
  if (!targetId) return false;

  const placeholder = trigger.querySelector?.('[data-lazy-category-tooltip="1"]');
  if (!placeholder) return false;

  const sourceRow = getRowById(targetId);
  const tooltipHtml = renderCategoryTooltipHtml(getRowItem(sourceRow), getRenderCategoryOptions());
  if (!tooltipHtml) return false;

  const span = startPerfMeasure("ranks:lazy-category-tooltip-hydrate", { itemId: targetId, type: state.type });
  const root = trigger.closest?.("#clusterize-content") || document;
  root.querySelectorAll?.(".categories-cell .with-tooltip").forEach((candidate) => {
    if (candidate !== trigger) dehydrateLazyCategoryTooltip(candidate);
  });
  placeholder.outerHTML = tooltipHtml;
  span.end({ ok: true });
  return true;
}

function dehydrateLazyCategoryTooltip(trigger) {
  if (!trigger) return false;
  const tooltip = trigger.querySelector?.(".tooltip.right");
  if (!tooltip) return false;

  tooltip.outerHTML = '<span data-lazy-category-tooltip="1"></span>';
  return true;
}

function hydrateLazyCategoryTooltipFromEvent(event) {
  const target = event?.target;
  if (!(target instanceof Element)) return;
  const trigger = target.closest(".categories-cell .with-tooltip");
  if (trigger) hydrateLazyCategoryTooltip(trigger);
}

function dehydrateLazyCategoryTooltipFromEvent(event) {
  const target = event?.target;
  if (!(target instanceof Element)) return;
  const trigger = target.closest(".categories-cell .with-tooltip");
  if (!trigger) return;
  const nextTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
  if (nextTarget && trigger.contains(nextTarget)) return;
  dehydrateLazyCategoryTooltip(trigger);
}

function handleTrophyJump(trophyMarket, itemType, itemIdForScroll = null) {
  return runContextTransition(() => {
    const nextType = NON_CREATOR_TYPES.has(itemType) ? itemType : "template";
    const nextTab = typeSupportsPricing(nextType)
      ? trophyMarket === "all"
        ? "all"
        : trophyMarket === "free"
          ? "free"
          : "paid"
      : "all";

    state.type = nextType;
    state.category = null;
    if (!typeSupportsRankingMode(nextType)) state.rankingMode = "popular";
    state.tab = nextTab;
    const defaultsForType = getDefaultSortForType(nextType);
    state.sortColumn = defaultsForType.sortColumn;
    state.sortDirection = defaultsForType.sortDirection;

    updateViewClass();
    renderCategoryTree();
    updateTabButtonStyles();
    updateCategoryDropdownText(getDefaultCategoryDropdownLabel());
    updateCategorySelectedState(null);
    disableFreePaidTabs(!typeSupportsPricing(nextType));
    updateControlDisabling();
    updateSearchFieldControls();
    updateColumnHeadersForMode();
    updateCategoryDropdownVisibility();
    syncUrlWithState();
    scrollVirtualizerToTop();

    trackEvent("trophy_jump_clicked", {
      tab: nextTab,
      type: nextType,
    });
    return refreshVisibleListAndGatedMetrics().then(() => {
      scheduleScrollToItemInCurrentView(itemIdForScroll);
    });
  });
}

/**
 * Handle sort column click
 */
function handleSortClick(column) {
  if (!acquireLock("sorting")) return;

  log(`[SORT] Column clicked: ${column}`);

  // Toggle direction if same column
  if (state.sortColumn === column) {
    state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
  } else {
    state.sortColumn = column;
    state.sortDirection = getDefaultDirection(state.type, column);
  }

  sortFilteredRows();
  const htmlRowsResult = buildHtmlRowsForCurrentView();
  const viewCacheKey = buildViewCacheKey();
  saveCurrentViewCache();
  saveAssembledViewCache(viewCacheKey, htmlRowsResult.htmlRows);
  updateVirtualizer(htmlRowsResult.htmlRows);
  updateSortIndicators();
  updateCsvDownloadButtonState();
  syncUrlWithState();

  releaseLock("sorting");

  trackEvent("sorting_changed", { column, direction: state.sortDirection });
}

/**
 * Handle search input
 */
let searchDebounceTimer = null;
function handleSearchInput(query, options = {}) {
  const { afterRefresh = null } = options;
  const rawQuery = String(query ?? "");
  const trimmedQuery = rawQuery.trim();

  // Debounce search input
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  // Update search-active class on input
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    searchInput.classList.toggle("search-active", trimmedQuery.length > 0);
  }

  searchDebounceTimer = setTimeout(async () => {
    log(`[SEARCH] Query: "${trimmedQuery}"`);
    state.search = normalizeSearchValue(trimmedQuery);

    // Clear sort cache to ensure fresh results with new filter
    clearSortCache();

    if (trimmedQuery) {
      trackEvent("search_executed", { table: state.type });
    }

    await refresh();
    await refreshGatedMetricsForVisibleList();
    syncUrlWithState();

    // Scroll to top after search (consistent with tab/type/period switches)
    scrollVirtualizerToTop();

    if (typeof afterRefresh === "function") afterRefresh();
  }, 300);
}

function applySearchCommand(command, options = {}) {
  const normalizedCommand = String(command || "").trim();
  if (!normalizedCommand) return;
  const sortedColumn = applySortForSearchCommand(normalizedCommand);
  highlightSortColumnHeader(sortedColumn);
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    searchInput.value = normalizedCommand;
    searchInput.classList.add("search-active");
    try {
      searchInput.focus({ preventScroll: true });
    } catch {
      searchInput.focus();
    }
    searchInput.select();
    highlightSearchInput();
  }
  handleSearchInput(normalizedCommand, options);
}

function appendSearchCommand(command) {
  const normalizedCommand = String(command || "").trim();
  const searchInput = document.getElementById("rankings-search");
  if (!normalizedCommand || !searchInput) return;

  const currentQuery = searchInput.value.trim();
  const nextQuery = currentQuery ? `${currentQuery} ${normalizedCommand}` : normalizedCommand;
  clearSearchInputHighlight();
  searchInput.value = nextQuery;
  searchInput.classList.add("search-active");
  try {
    searchInput.focus({ preventScroll: true });
  } catch {
    searchInput.focus();
  }
  searchInput.setSelectionRange(nextQuery.length, nextQuery.length);
  handleSearchInput(nextQuery);
  setTimeout(() => {
    if (document.activeElement === searchInput) searchInput.setSelectionRange(nextQuery.length, nextQuery.length);
  }, 0);
}

// ============================================
// UI UPDATES
// ============================================

function updateTabButtonStyles() {
  const pricingEnabled = typeSupportsPricing(state.type);
  const buttons = document.querySelectorAll("#ranking-tabs .tab-button");
  buttons.forEach((btn) => {
    const tab = btn.dataset.tab;
    const isHidden = shouldHidePricingTabForType(state.type, tab);
    const isActive = pricingEnabled && !isHidden && tab === state.tab;
    btn.classList.toggle("active", isActive);
  });

  // Update type buttons
  ITEM_TYPE_OPTIONS.forEach(({ buttonId, type }) => {
    const button = document.getElementById(buttonId);
    if (button) button.classList.toggle("active", state.type === type);
  });
  updateDataTypeDropdownState();
  updatePricingDropdownState();
  updateRankingModeState();
}

function getItemTypeLabel(type) {
  return ITEM_TYPE_OPTIONS.find((option) => option.type === type)?.label || ITEM_TYPE_OPTIONS[0].label;
}

function getPricingLabel(tab) {
  return PRICING_OPTIONS.find((option) => option.tab === tab)?.label || PRICING_OPTIONS[0].label;
}

function getRankingModeLabel(mode) {
  return RANKING_MODE_OPTIONS.find((option) => option.mode === mode)?.label || RANKING_MODE_OPTIONS[0].label;
}

function updateRankingModeState() {
  const enabled = typeSupportsRankingMode(state.type);
  const mode = enabled ? normalizeRankingMode(state.rankingMode) : "popular";
  const tabsRoot = document.getElementById("ranking-mode-tabs");
  if (tabsRoot) tabsRoot.hidden = !enabled;
  const tabs = document.querySelectorAll("#ranking-mode-tabs [data-ranking-mode]");
  tabs.forEach((button) => {
    const active = enabled && button.dataset.rankingMode === mode;
    button.classList.toggle("active", active);
    button.disabled = !enabled;
    button.setAttribute("aria-pressed", String(active));
  });
  const wrapper = document.querySelector(".ranking-mode-dropdown-wrapper");
  const button = document.getElementById("ranking-mode-dropdown-button");
  const text = document.getElementById("ranking-mode-dropdown-text");
  if (text) text.textContent = getRankingModeLabel(mode);
  if (wrapper) wrapper.hidden = !enabled;
  if (button) {
    button.disabled = !enabled;
    button.classList.toggle("active", enabled);
  }
  document.querySelectorAll(".ranking-mode-dropdown-option[data-ranking-mode]").forEach((option) => {
    const active = enabled && option.dataset.rankingMode === mode;
    option.classList.toggle("selected", active);
    option.setAttribute("aria-selected", String(active));
  });
}

function renderRankingModeDropdownOptions() {
  const content = document.getElementById("ranking-mode-dropdown-content");
  if (!content) return;
  content.innerHTML = RANKING_MODE_OPTIONS.map(
    ({ mode, label }) =>
      `<button type="button" class="control-dropdown-option ranking-mode-dropdown-option" data-ranking-mode="${mode}" role="option" aria-selected="false">${label}</button>`,
  ).join("");
}

function handleRankingModeSwitch(newMode) {
  const normalized = normalizeRankingMode(newMode);
  if (!typeSupportsRankingMode(state.type) || state.rankingMode === normalized) return;
  return runContextTransition(() => {
    state.rankingMode = normalized;
    state.sortColumn = getDefaultSortForType(state.type).sortColumn;
    state.sortDirection = getDefaultDirection(state.type, state.sortColumn);
    updateRankingModeState();
    updateViewClass();
    syncUrlWithState();
    scrollVirtualizerToTop();
    trackEvent("ranking_mode_changed", { mode: normalized, table: state.type });
    return refreshVisibleListAndGatedMetrics();
  });
}

function renderDataTypeDropdownOptions() {
  const content = document.getElementById("data-type-dropdown-content");
  if (!content || content.dataset.rendered === "true") return;
  content.innerHTML = ITEM_TYPE_OPTIONS.map(
    ({ type, label }) =>
      `<button type="button" class="data-type-option${type === "creator" ? " tab-disabled" : ""}" data-type="${type}" role="option" aria-selected="false"${type === "creator" ? " disabled" : ""}>${label}</button>`,
  ).join("");
  content.dataset.rendered = "true";
}

function renderPricingDropdownOptions() {
  const content = document.getElementById("pricing-dropdown-content");
  if (!content) return;
  content.innerHTML = getVisiblePricingOptionsForCurrentContext()
    .map(
      ({ tab, label }) =>
        `<button type="button" class="control-dropdown-option pricing-dropdown-option" data-tab="${tab}" role="option" aria-selected="false" aria-disabled="false">${label}</button>`,
    )
    .join("");
}

function updateDataTypeDropdownState() {
  renderDataTypeDropdownOptions();
  const textEl = document.getElementById("data-type-dropdown-text");
  const buttonEl = document.getElementById("data-type-dropdown-button");
  if (textEl) textEl.textContent = getItemTypeLabel(state.type);
  buttonEl?.classList.toggle("active", Boolean(state.type));
  document.querySelectorAll(".data-type-option[data-type]").forEach((option) => {
    const isSelected = option.getAttribute("data-type") === state.type;
    option.classList.toggle("selected", isSelected);
    option.setAttribute("aria-selected", String(isSelected));
  });
}

function updatePricingDropdownState() {
  renderPricingDropdownOptions();
  const textEl = document.getElementById("pricing-dropdown-text");
  const buttonEl = document.getElementById("pricing-dropdown-button");
  const wrapperEl = document.querySelector(".pricing-dropdown-wrapper");
  const visibleOptions = getVisiblePricingOptionsForCurrentContext();
  const shouldHideDropdown = visibleOptions.length === 0;
  if (textEl) textEl.textContent = getPricingLabel(state.tab);
  buttonEl?.classList.toggle("active", typeSupportsPricing(state.type));
  if (wrapperEl) wrapperEl.hidden = shouldHideDropdown;
  if (buttonEl) {
    buttonEl.disabled = shouldHideDropdown;
    if (shouldHideDropdown) {
      buttonEl.setAttribute("aria-expanded", "false");
      closeControlDropdown("pricing", "disabled");
    }
  }
  document.querySelectorAll(".pricing-dropdown-option[data-tab]").forEach((option) => {
    const tab = option.getAttribute("data-tab");
    const isSelected = tab === state.tab;
    option.classList.toggle("selected", isSelected);
    option.classList.remove("disabled");
    option.setAttribute("aria-selected", String(isSelected));
    option.setAttribute("aria-disabled", "false");
  });
}

function closeDataTypeDropdown(reason = "close") {
  const panel = document.getElementById("data-type-dropdown-panel");
  const button = document.getElementById("data-type-dropdown-button");
  const wasOpen = panel?.classList.contains("open") || false;
  panel?.classList.remove("open");
  button?.setAttribute("aria-expanded", "false");
  if (wasOpen) trackEvent("data_type_dropdown_closed", { reason });
}

function closeControlDropdown(kind, reason = "close", eventKind = kind.replace(/-/g, "_")) {
  const panel = document.getElementById(`${kind}-dropdown-panel`);
  const button = document.getElementById(`${kind}-dropdown-button`);
  const wasOpen = panel?.classList.contains("open") || false;
  panel?.classList.remove("open");
  button?.setAttribute("aria-expanded", "false");
  if (wasOpen) trackEvent(`${eventKind}_dropdown_closed`, { reason });
}

function updateControlDisabling() {
  const disablePricing = shouldDisablePricingTabsForCurrentContext();
  const visiblePricingTabs = new Set(getVisiblePricingOptionsForCurrentContext().map(({ tab }) => tab));
  const rankingTabs = document.getElementById("ranking-tabs");
  if (rankingTabs) rankingTabs.hidden = visiblePricingTabs.size === 0;
  const pricingTabs = document.querySelectorAll("#ranking-tabs .tab-button");
  pricingTabs.forEach((tab) => {
    const tabName = tab.getAttribute("data-tab");
    const shouldHide = !visiblePricingTabs.has(tabName);
    tab.classList.toggle("tab-hidden", shouldHide);
    tab.classList.toggle("tab-disabled", disablePricing || shouldHide);
    tab.disabled = disablePricing || shouldHide;
  });

  updatePricingDropdownState();
}

function updateSearchFieldControls() {
  const creatorsMode = state.type === "creator";
  const container = document.getElementById("search-field-checkboxes");
  if (container) {
    container.classList.remove("show");
    container.classList.toggle("search-field-checkboxes-hidden", creatorsMode);
  }

  const checkboxIds = [
    "search-field-rank",
    "search-field-change",
    "search-field-price",
    "search-field-name",
    "search-field-author",
  ];
  checkboxIds.forEach((id) => {
    const checkbox = document.getElementById(id);
    if (checkbox) checkbox.disabled = creatorsMode;
  });
}

function bindSortHeaderListeners() {
  document.querySelectorAll("th.sortable").forEach((header) => {
    header.onclick = () => handleSortClick(header.dataset.column);
  });
}

function updateColumnHeadersForMode() {
  const thead = document.querySelector("#rankings-table thead");
  const tfoot = document.getElementById("rankings-tfoot");
  if (!thead || !tfoot) return;
  mountTableStructure({
    thead,
    tfoot,
    standardType: state.type,
    categoryDisplayMode: getCategoryDisplayMode(state.type, state.allItems),
  });
  bindSortHeaderListeners();
}

function updateSortIndicators() {
  const headers = document.querySelectorAll("th.sortable");
  headers.forEach((header) => {
    const column = header.dataset.column;
    const upArrow = header.querySelector(".sort-arrow-up");
    const downArrow = header.querySelector(".sort-arrow-down");

    if (upArrow) upArrow.classList.remove("active");
    if (downArrow) downArrow.classList.remove("active");

    if (column === state.sortColumn) {
      if (state.sortDirection === "asc" && upArrow) {
        upArrow.classList.add("active");
      } else if (downArrow) {
        downArrow.classList.add("active");
      }
    }
  });
}

function updateCategoryDropdownVisibility() {
  const container = document.querySelector(".rankings-container");
  if (container) {
    if (state.type !== "template") {
      container.classList.add("components-mode");
    } else {
      container.classList.remove("components-mode");
    }
  }

  // Hide category dropdown entirely for creators.
  const dropdownButton = document.getElementById("category-dropdown-button");
  const dropdownWrapper = dropdownButton?.closest(".category-dropdown-wrapper");
  const shouldDisable = state.type === "creator";
  dropdownWrapper?.classList.toggle("expert-country-dropdown-wrapper", state.type === "expert");

  if (dropdownWrapper) {
    dropdownWrapper.hidden = shouldDisable;
    dropdownWrapper.classList.toggle("tab-disabled", shouldDisable);
    dropdownWrapper.removeAttribute("data-tooltip");
  }

  if (dropdownButton) {
    dropdownButton.disabled = shouldDisable;
  }

  const typeWrapper = document.getElementById("expert-type-dropdown-wrapper");
  if (typeWrapper) typeWrapper.hidden = state.type !== "expert";
  const typeButton = document.getElementById("expert-type-dropdown-button");
  if (typeButton) typeButton.disabled = state.type !== "expert";
}

function updateExpertTypeDropdownText() {
  const text = document.getElementById("expert-type-dropdown-text");
  const button = document.getElementById("expert-type-dropdown-button");
  const arrow = document.querySelector("#expert-type-dropdown-button .dropdown-arrow");
  const meta = state.expertType ? state.expertTypeMeta?.[`type-${state.expertType}`] : null;
  if (text) text.textContent = meta?.name || "Type";
  button?.classList.toggle("active", Boolean(meta));
  if (arrow) {
    arrow.innerHTML = meta
      ? '<span class="category-clear-btn">✕</span>'
      : '<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>';
    arrow.querySelector(".category-clear-btn")?.addEventListener("click", (event) => {
      event.stopPropagation();
      handleExpertTypeSelect(null);
    });
  }
}

function renderExpertTypeDropdown() {
  const content = document.getElementById("expert-type-dropdown-content");
  if (!content) return;
  content.innerHTML = "";
  const all = document.createElement("div");
  all.className = `category-item category-item-all${!state.expertType ? " selected" : ""}`;
  all.textContent = "All types";
  all.addEventListener("click", () => handleExpertTypeSelect(null));
  content.appendChild(all);
  for (const node of state.expertTypeTree || []) {
    const item = document.createElement("div");
    const count = Number(node.count || 0);
    item.className = `category-item${state.expertType === node.id ? " selected" : ""}${count === 0 ? " category-item-disabled" : ""}`;
    item.dataset.expertType = node.id;
    item.setAttribute("aria-disabled", String(count === 0));
    item.innerHTML = `<span class="category-label-text"><span class="category-label-name">${escapeHtml(node.name)}</span><span class="category-item-count">(${count})</span></span>`;
    if (count > 0) item.addEventListener("click", () => handleExpertTypeSelect(node.id));
    content.appendChild(item);
  }
  updateExpertTypeDropdownText();
}

function closeExpertTypeDropdown(reason = "close") {
  const panel = document.getElementById("expert-type-dropdown-panel");
  const button = document.getElementById("expert-type-dropdown-button");
  const wasOpen = panel?.classList.contains("open") || false;
  panel?.classList.remove("open");
  button?.setAttribute("aria-expanded", "false");
  if (wasOpen) trackEvent("expert_type_dropdown_closed", { reason });
}

function handleExpertTypeSelect(typeId, itemIdForScroll = null, source = "dropdown") {
  if (state.type !== "expert") return;
  return runContextTransition(() => {
    state.expertType = typeId
      ? String(typeId).replace(/^type-/, "").toLowerCase()
      : null;
    state.category = null;
    state.sortColumn = "rank";
    state.sortDirection = "asc";
    renderExpertTypeDropdown();
    renderCategoryTree();
    updateCategoryDropdownText(getDefaultCategoryDropdownLabel());
    updateCategorySelectedState(null);
    updateViewClass();
    syncUrlWithState();
    trackEvent(state.expertType ? "expert_type_selected" : "expert_type_cleared", {
      ...(state.expertType ? { expertType: state.expertType } : {}),
      source,
      ...(itemIdForScroll ? { itemId: itemIdForScroll } : {}),
    });
    closeExpertTypeDropdown(state.expertType ? "select" : "clear");
    scrollVirtualizerToTop();
    return refreshVisibleListAndGatedMetrics().then(() => {
      scheduleScrollToItemInCurrentView(itemIdForScroll);
    });
  });
}

function updateCategoryDropdownText(text) {
  const textEl = document.getElementById("category-dropdown-text");
  const buttonEl = document.getElementById("category-dropdown-button");
  const arrowEl = document.querySelector("#category-dropdown-button .dropdown-arrow");
  const isSelected = !DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text);

  if (textEl) {
    const displayText = DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text) ? text : normalizeCategoryDisplayName(text);
    textEl.textContent = displayText;
  }

  if (buttonEl) {
    buttonEl.classList.toggle("active", isSelected);
  }

  if (arrowEl) {
    if (DEFAULT_CATEGORY_DROPDOWN_LABELS.has(text)) {
      arrowEl.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    } else {
      arrowEl.innerHTML = `<span class="category-clear-btn">✕</span>`;
      arrowEl.querySelector(".category-clear-btn")?.addEventListener("click", (e) => {
        e.stopPropagation();
        handleCategoryClear();
      });
    }
  }
}

function updateCategorySelectedState(categoryId) {
  // Remove selected class from all category items
  document.querySelectorAll(".category-item.selected").forEach((el) => {
    el.classList.remove("selected");
  });

  // Add selected class to the matching category item
  if (categoryId) {
    const item = document.querySelector(`.category-item[data-category-id="${categoryId}"]`);
    if (item) {
      item.classList.add("selected");
    }
  }
}

function disableFreePaidTabs(disabled) {
  const tabs = document.querySelectorAll("#ranking-tabs .tab-button[data-tab]");
  const shouldShowComingSoon = disabled && state.type !== "creator" && state.type !== "tutorial";
  const visiblePricingTabs = new Set(getVisiblePricingOptionsForCurrentContext().map(({ tab }) => tab));
  const rankingTabs = document.getElementById("ranking-tabs");
  if (rankingTabs) rankingTabs.hidden = visiblePricingTabs.size === 0;
  tabs.forEach((tab) => {
    const tabName = tab.getAttribute("data-tab");
    const isFreePaidTab = tabName === "free" || tabName === "paid";
    const shouldHide = !visiblePricingTabs.has(tabName);
    const shouldDisable = shouldHide || (disabled && isFreePaidTab);
    tab.classList.toggle("tab-disabled", disabled && isFreePaidTab);
    tab.classList.toggle("tab-hidden", shouldHide);
    tab.disabled = shouldDisable;
    // Add/remove "Coming Soon" tooltip icon
    const existingIcon = tab.querySelector(".tooltip-icon");
    if (shouldShowComingSoon && isFreePaidTab && !existingIcon) {
      const icon = document.createElement("span");
      icon.className = "tooltip-icon";
      icon.setAttribute("data-tooltip", "Coming Soon");
      icon.textContent = "?";
      tab.appendChild(icon);
    } else if (!shouldShowComingSoon && existingIcon) {
      existingIcon.remove();
    }
  });
}

function getCategoryNavHref(categoryId) {
  const nextState = { ...state, category: categoryId };
  const path = serializeStateToCanonicalPath(nextState);
  const query = serializeStateToCanonicalQuery(nextState, typeof window !== "undefined" ? window.location.search : "");
  const queryString = query.toString();
  return `${path}${queryString ? `?${queryString}` : ""}`;
}

function getCategoryNavGroups() {
  if (state.type === "template") return CURATED_TEMPLATE_CATEGORY_NAV;
  return getCategoryTreeForType(state.type, state).map((node) => ({
    name: node.name,
    id: node.id,
    children: Array.isArray(node.children) ? node.children.map((child) => [child.name, child.id]) : [],
  }));
}

function renderCategoryNav() {
  const nav = document.getElementById("category-nav");
  if (!nav) return;

  if (state.type === "expert") {
    nav.hidden = true;
    return;
  }

  const groups = getCategoryNavGroups();
  nav.classList.toggle("category-nav--template", state.type === "template");
  nav.classList.toggle("category-nav--compact", state.type !== "template");
  nav.hidden = groups.length === 0;
  if (groups.length === 0) return;

  const renderedType = nav.dataset.renderedType || "";
  if (renderedType !== state.type) {
    const fragment = document.createDocumentFragment();
    const isTemplateGroup = state.type === "template";
    const allEntries = groups.flatMap((group) => [
      [group.name, group.id, isTemplateGroup],
      ...(group.children || []).map(([name, id]) => [name, id, false]),
    ]);
    const columns = isTemplateGroup
      ? groups.map((group) => [
          [group.name, group.id, true],
          ...(group.children || []).map(([name, id]) => [name, id, false]),
        ])
      : Array.from({ length: Math.ceil(allEntries.length / 5) }, (_, index) =>
          allEntries.slice(index * 5, index * 5 + 5),
        );

    const appendLink = (parent, [name, categoryId, isParent]) => {
      const link = document.createElement("a");
      link.className = `category-nav__link${isParent ? " category-nav__link--parent" : ""}`;
      link.dataset.categoryId = categoryId;
      link.dataset.categoryName = name;
      link.textContent = name;
      parent.appendChild(link);
    };

    if (isTemplateGroup) {
      for (const entries of columns) {
        const groupEl = document.createElement("div");
        groupEl.className = "category-nav__group";
        entries.forEach((entry) => {
          appendLink(groupEl, entry);
        });
        fragment.appendChild(groupEl);
      }
    } else {
      allEntries.forEach((entry) => {
        appendLink(fragment, entry);
      });
    }
    nav.replaceChildren(fragment);
    nav.dataset.renderedType = state.type;
  }

  nav.querySelectorAll("a[data-category-id]").forEach((link) => {
    const categoryId = link.dataset.categoryId;
    const isActive = state.category === categoryId;
    link.href = getCategoryNavHref(categoryId);
    link.classList.toggle("is-active", isActive);
    if (isActive) link.setAttribute("aria-current", "page");
    else link.removeAttribute("aria-current");
  });
}

function bindCategoryNav() {
  const nav = document.getElementById("category-nav");
  if (!nav || nav.dataset.bound === "true") return;
  nav.dataset.bound = "true";
  nav.addEventListener("click", (event) => {
    const link = event.target.closest("a[data-category-id]");
    if (!link || event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    event.preventDefault();
    const categoryId = link.dataset.categoryId;
    if (state.category === categoryId) {
      handleCategoryClear();
      return;
    }
    handleCategorySelect(categoryId, link.dataset.categoryName || link.textContent.trim());
  });
}

function shouldDisablePricingTabsForCurrentContext() {
  return !typeSupportsPricing(state.type);
}

function updateTableTitle() {
  const titleMain = document.getElementById("table-title-main");
  const titleSubtitle = document.getElementById("table-title-subtitle");
  if (!titleMain) return;
  const questionWord = document.getElementById("ranks-question-word");
  if (questionWord) questionWord.textContent = state.type === "expert" ? "Who's" : "What's";
  renderCategoryNav();

  const renderTodayBadge = (todayCount, options = {}) => {
    if (!Number.isFinite(todayCount) || todayCount <= 0) return "";
    const formatted = todayCount.toLocaleString();
    const commandAttrs = options.searchCommand
      ? ` role="button" tabindex="0" data-search-command="${options.searchCommand}"`
      : "";
    return ` <span class="subtitle-today"${commandAttrs}><span class="subtitle-today-value">(+${formatted} today!)</span></span>`;
  };

  if (state.type === "creator") {
    const creatorCount = state.filteredRows?.length ?? 0;
    const newCreatorsToday = (state.filteredRows || []).reduce((sum, row) => {
      const stats = getRowItem(row)?.creatorStats;
      if (!stats) return sum;
      const totals = [
        Number(stats.totalTemplates || 0),
        Number(stats.totalComponents || 0),
        Number(stats.totalPlugins || 0),
        Number(stats.totalVectors || 0),
        Number(stats.totalTutorials || 0),
      ];
      const deltas = [
        Number(stats.totalTemplatesDelta1d),
        Number(stats.totalComponentsDelta1d),
        Number(stats.totalPluginsDelta1d),
        Number(stats.totalVectorsDelta1d),
        Number(stats.totalTutorialsDelta1d),
      ];
      const totalItems = Number(stats.totalItems || 0);
      const totalDelta = Number(stats.totalItemsDelta1d);
      if (!Number.isFinite(totalDelta) || !Number.isFinite(totalItems) || totalItems <= 0) return sum;
      if (totalDelta !== totalItems) return sum;
      for (let i = 0; i < totals.length; i += 1) {
        if (!Number.isFinite(deltas[i]) || deltas[i] !== totals[i]) return sum;
      }
      return sum + 1;
    }, 0);
    titleMain.innerHTML = `<span class="category-title-link">Creators</span>`;
    if (titleSubtitle) {
      renderTableTitleSubtitle(
        titleSubtitle,
        `<span class="subtitle-count">${creatorCount.toLocaleString()} creators${renderTodayBadge(newCreatorsToday, { searchCommand: "total:1 totaldelta:+1" })}</span>`,
      );
    }
    return;
  }

  const externalIcon = `<svg class="inline-block ml-1 -mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;

  // Build subtitle parts
  const itemCount = state.filteredRows?.length ?? 0;
  const context = getCurrentContext();
  const newItemsToday = (state.filteredRows || []).reduce((sum, row) => {
    const change = getRowItem(row)?.ranks?.[context]?.[1];
    return change === "new" ? sum + 1 : sum;
  }, 0);
  const typeInfo = getTypeCapabilities(state.type);
  const typeLabel = typeInfo.typeLabel || "Items";
  const typePath = STATE_TO_URL_TYPE[state.type] || "templates";
  const rankingMode = typeSupportsRankingMode(state.type) ? normalizeRankingMode(state.rankingMode) : "popular";
  const rankingModeLabel = getRankingModeLabel(rankingMode);

  // Build subtitle: Pricing · Ranking mode · Count
  // Dollar icon: detailed fill
  const pricingIcon = `<svg class="subtitle-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" clip-rule="evenodd" d="M13 3.5C13 2.94772 12.5523 2.5 12 2.5C11.4477 2.5 11 2.94772 11 3.5V4.0592C9.82995 4.19942 8.75336 4.58509 7.89614 5.1772C6.79552 5.93745 6 7.09027 6 8.5C6 9.77399 6.49167 10.9571 7.5778 11.7926C8.43438 12.4515 9.58764 12.8385 11 12.959V17.9219C10.2161 17.7963 9.54046 17.5279 9.03281 17.1772C8.32378 16.6874 8 16.0903 8 15.5C8 14.9477 7.55228 14.5 7 14.5C6.44772 14.5 6 14.9477 6 15.5C6 16.9097 6.79552 18.0626 7.89614 18.8228C8.75336 19.4149 9.82995 19.8006 11 19.9408V20.5C11 21.0523 11.4477 21.5 12 21.5C12.5523 21.5 13 21.0523 13 20.5V19.9435C14.1622 19.8101 15.2376 19.4425 16.0974 18.8585C17.2122 18.1013 18 16.9436 18 15.5C18 14.1934 17.5144 13.0022 16.4158 12.1712C15.557 11.5216 14.4039 11.1534 13 11.039V6.07813C13.7839 6.20366 14.4596 6.47214 14.9672 6.82279C15.6762 7.31255 16 7.90973 16 8.5C16 9.05228 16.4477 9.5 17 9.5C17.5523 9.5 18 9.05228 18 8.5C18 7.09027 17.2045 5.93745 16.1039 5.17721C15.2467 4.58508 14.1701 4.19941 13 4.0592V3.5ZM11 6.07814C10.2161 6.20367 9.54046 6.47215 9.03281 6.8228C8.32378 7.31255 8 7.90973 8 8.5C8 9.22601 8.25834 9.79286 8.79722 10.2074C9.24297 10.5503 9.94692 10.8384 11 10.9502V6.07814ZM13 13.047V17.9263C13.7911 17.8064 14.4682 17.5474 14.9737 17.204C15.6685 16.7321 16 16.1398 16 15.5C16 14.7232 15.7356 14.1644 15.2093 13.7663C14.7658 13.4309 14.0616 13.1537 13 13.047Z"/></svg>`;
  const subtitleParts = [];
  if (typeSupportsPricing(state.type)) {
    const pricingLabel = state.tab === "all" ? "Mixed Price" : state.tab === "free" ? "Free only" : "Paid only";
    subtitleParts.push(`<span class="subtitle-pricing">${pricingIcon} ${pricingLabel}</span>`);
  }
  if (typeSupportsRankingMode(state.type)) {
    const periodIcon = `<svg class="subtitle-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3H14M12 9V13L14 15M20 13C20 17.4183 16.4183 21 12 21C7.58172 21 4 17.4183 4 13C4 8.58172 7.58172 5 12 5C16.4183 5 20 8.58172 20 13Z"/></svg>`;
    subtitleParts.push(`<span class="subtitle-period">${periodIcon} ${rankingModeLabel}</span>`);
  }
  const countLabel = state.type === "expert" ? (itemCount === 1 ? "expert" : "experts") : "items";
  subtitleParts.push(
    `<span class="subtitle-count">${itemCount.toLocaleString()} ${countLabel}${renderTodayBadge(newItemsToday, { searchCommand: ":new" })}</span>`,
  );

  if (titleSubtitle) {
    renderTableTitleSubtitle(titleSubtitle, subtitleParts.join(' <span class="subtitle-sep">·</span> '));
  }

  // Update main title with link
  const sortParamByType =
    state.type === "plugin"
      ? "&sort=top"
      : state.type === "vector" || state.type === "tutorial" || state.type === "site"
        ? "&sort=popular"
        : "";
  const rankingParamByMode =
    rankingMode === "best30"
      ? "&sort=best"
      : rankingMode === "best90"
        ? "&sort=best&period=90d"
        : sortParamByType || "";

  if (state.type === "expert") {
    const categoryDisplayName = state.category
      ? state.category === "country-gb"
        ? "UK"
        : normalizeCategoryDisplayName(getActiveCategoryMeta()[state.category]?.name || state.category)
      : "";
    const typeDisplayName = state.expertType ? state.expertTypeMeta?.[`type-${state.expertType}`]?.name || state.expertType : "";
    const expertTitle = `${typeDisplayName ? `Framer ${typeDisplayName} Experts` : "Framer Experts"}${categoryDisplayName ? ` in ${categoryDisplayName}` : ""}`;
    const expertTitleUrl = categoryDisplayName
      ? `https://www.framer.com/community/experts/countries/${slugifyExpertCountryName(categoryDisplayName)}/`
      : "https://www.framer.com/community/experts/";
    titleMain.innerHTML = `<a href="${expertTitleUrl}" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">${expertTitle}${externalIcon}</a>`;
  } else if (state.category) {
    const activeMeta = getActiveCategoryMeta();
    const meta = activeMeta[state.category];
    const categoryDisplayName = meta
      ? normalizeCategoryDisplayName(meta.name)
      : normalizeCategoryDisplayName(state.category);
    const pricing = state.tab === "free" ? "&pricing=free" : state.tab === "paid" ? "&pricing=paid" : "";
    const categoryUrlParams = `${pricing}${rankingParamByMode}`.replace(/^&/, "?");
    const categoryUrl = meta?.url ? `${meta.url}${categoryUrlParams}` : "#";
    const titlePrefix = state.tab === "free" ? "Free " : state.tab === "paid" ? "Paid " : "";
    titleMain.innerHTML = `<a href="${categoryUrl}" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">${titlePrefix}'${categoryDisplayName}' ${typeLabel}${externalIcon}</a>`;
  } else {
    const pricingParam = state.tab === "free" ? "&pricing=free" : state.tab === "paid" ? "&pricing=paid" : "";
    const marketplaceParams = `${pricingParam}${rankingParamByMode}`.replace(/^&/, "?");
    const marketplaceUrl =
      state.type === "expert"
        ? "https://www.framer.com/community/experts/"
        : marketplaceParams
          ? `https://www.framer.com/marketplace/${typePath}/${marketplaceParams}`
          : `https://www.framer.com/marketplace/${typePath}/`;
    const titlePrefix = state.tab === "free" ? "Free" : state.tab === "paid" ? "Paid" : "All";
    titleMain.innerHTML = `<a href="${marketplaceUrl}" target="_blank" rel="noopener noreferrer" class="category-title-link hover:underline">${titlePrefix} ${typeLabel}${externalIcon}</a>`;
  }
}

function renderTableTitleSubtitle(titleSubtitle, subtitleHtml) {
  const gatedBlock = document.getElementById("gated-block");
  titleSubtitle.innerHTML = `<span class="table-title-subtitle-left">${subtitleHtml}</span><span id="gated-block-slot" class="table-title-subtitle-gated-slot">${renderGatedChurnSubtitleSlot()}</span>`;
  if (gatedBlock) {
    placeGatedBlockInTitleSubtitle(gatedBlock);
  }
  if (
    supportsGatedMetricsForType() &&
    authClientState.status === AUTH_BOOT_STATE_AUTHENTICATED &&
    state.gated.currentMetrics
  ) {
    renderMetricsGatedState(state.gated.currentMetrics);
  } else if (!supportsGatedMetricsForType()) {
    resetGatedChurnMetricsUi();
  }
}

function shouldPlaceGatedBlockInSearchRow() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(GATED_BLOCK_SEARCH_ROW_MEDIA).matches;
}

function placeGatedBlockInSearchRow(gatedBlock) {
  const searchRow = document.querySelector(".search-row");
  const searchContainer = searchRow?.querySelector(".search-container");
  if (!searchRow || !searchContainer) return false;
  searchRow.insertBefore(gatedBlock, searchContainer);
  return true;
}

function placeGatedBlockInTitleSubtitle(gatedBlockOverride = null) {
  const gatedBlock = gatedBlockOverride || document.getElementById("gated-block");
  const titleSubtitle = document.getElementById("table-title-subtitle");
  if (!gatedBlock || !titleSubtitle) return;
  let slot = document.getElementById("gated-block-slot");
  if (!slot) {
    titleSubtitle.querySelector("#gated-churn-subtitle")?.remove();
    const existingHtml = titleSubtitle.innerHTML;
    titleSubtitle.innerHTML = `<span class="table-title-subtitle-left">${existingHtml}</span><span id="gated-block-slot" class="table-title-subtitle-gated-slot">${renderGatedChurnSubtitleSlot()}</span>`;
    slot = document.getElementById("gated-block-slot");
  } else if (!slot.querySelector("#gated-churn-subtitle")) {
    slot.insertAdjacentHTML("afterbegin", renderGatedChurnSubtitleSlot());
  }
  if (shouldPlaceGatedBlockInSearchRow() && placeGatedBlockInSearchRow(gatedBlock)) return;
  slot?.appendChild(gatedBlock);
}

function bindGatedBlockViewportPlacement() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
  if (document.body.dataset.gatedBlockViewportBound === "true") return;
  const mediaQuery = window.matchMedia(GATED_BLOCK_SEARCH_ROW_MEDIA);
  const handlePlacementChange = () => {
    placeGatedBlockInTitleSubtitle();
  };
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", handlePlacementChange);
  } else if (typeof mediaQuery.addListener === "function") {
    mediaQuery.addListener(handlePlacementChange);
  }
  document.body.dataset.gatedBlockViewportBound = "true";
}

/**
 * Update table footer with dynamic stats based on filtered rows
 */
function updateTableFooter() {
  const creatorsFootCount = document.getElementById("tfoot-creators-count");
  const creatorsFootTemplates = document.getElementById("tfoot-creators-templates");
  const creatorsFootTemplatesDelta = document.getElementById("tfoot-creators-templatesdelta");
  const creatorsFootComponents = document.getElementById("tfoot-creators-components");
  const creatorsFootComponentsDelta = document.getElementById("tfoot-creators-componentsdelta");
  const creatorsFootPlugins = document.getElementById("tfoot-creators-plugins");
  const creatorsFootPluginsDelta = document.getElementById("tfoot-creators-pluginsdelta");
  const creatorsFootVectors = document.getElementById("tfoot-creators-vectors");
  const creatorsFootVectorsDelta = document.getElementById("tfoot-creators-vectorsdelta");
  const creatorsFootTutorials = document.getElementById("tfoot-creators-tutorials");
  const creatorsFootTutorialsDelta = document.getElementById("tfoot-creators-tutorialsdelta");
  const creatorsFootTotal = document.getElementById("tfoot-creators-total");
  const creatorsFootTotalDelta = document.getElementById("tfoot-creators-totaldelta");
  const tfootRank = document.getElementById("tfoot-rank");
  const tfootChange = document.getElementById("tfoot-change");
  const tfootPrice = document.getElementById("tfoot-price");
  const tfootName = document.getElementById("tfoot-name");
  const tfootAuthor = document.getElementById("tfoot-author");

  if (!tfootRank && !creatorsFootCount) return;

  const filteredItems = state.filteredRows?.map((row) => getRowItem(row)).filter(Boolean) || [];
  const count = filteredItems.length;
  const context = getCurrentContext();
  const formatDeltaTotalsHtml = (positive, negativeAbs) => {
    const positiveClass = positive === 0 ? "tfoot-change-zero" : "tfoot-change-positive";
    const negativeClass = negativeAbs === 0 ? "tfoot-change-zero" : "tfoot-change-negative";
    return `<span class="${positiveClass}">+${positive.toLocaleString()}</span><span class="tfoot-change-sep">/</span><span class="${negativeClass}">-${negativeAbs.toLocaleString()}</span>`;
  };

  if (state.type === "creator") {
    let templatesTotal = 0;
    let componentsTotal = 0;
    let pluginsTotal = 0;
    let vectorsTotal = 0;
    let tutorialsTotal = 0;
    let totalTotal = 0;
    let templatesDeltaPos = 0;
    let templatesDeltaNegAbs = 0;
    let componentsDeltaPos = 0;
    let componentsDeltaNegAbs = 0;
    let pluginsDeltaPos = 0;
    let pluginsDeltaNegAbs = 0;
    let vectorsDeltaPos = 0;
    let vectorsDeltaNegAbs = 0;
    let tutorialsDeltaPos = 0;
    let tutorialsDeltaNegAbs = 0;
    let totalDeltaPos = 0;
    let totalDeltaNegAbs = 0;

    const addDelta = (rawValue, applyPos, applyNegAbs) => {
      const value = Number(rawValue);
      if (!Number.isFinite(value)) return;
      if (value > 0) applyPos(value);
      else if (value < 0) applyNegAbs(Math.abs(value));
    };
    const countAddedItemsByType = (itemType) => {
      let added = 0;
      for (const item of state.allItems) {
        if (item?.type !== itemType) continue;
        const change = item?.ranks?.["global-all"]?.[1];
        if (change === "new" || change === "comeback") added += 1;
      }
      return added;
    };
    const getSourceTotals = (sourceKey) => {
      const source = state.marketplaceSources?.[sourceKey];
      if (!source || !Array.isArray(source.snapshots) || !Array.isArray(source.usage)) return null;
      const usage = source.usage.find(
        (entry) => entry?.pipelineKind === "globals" && entry?.rankingType === "alltime-all",
      );
      if (!usage?.currentSnapshotId || !usage?.previousSnapshotId) return null;
      const byId = new Map(
        source.snapshots
          .filter((snapshot) => snapshot?.snapshotId)
          .map((snapshot) => [snapshot.snapshotId, Number(snapshot.totalItems) || 0]),
      );
      if (!byId.has(usage.currentSnapshotId) || !byId.has(usage.previousSnapshotId)) return null;
      return {
        currentTotal: byId.get(usage.currentSnapshotId) || 0,
        previousTotal: byId.get(usage.previousSnapshotId) || 0,
      };
    };
    const deriveDeltaBreakdown = (itemType, sourceKey, fallbackNet) => {
      const added = countAddedItemsByType(itemType);
      if (added <= 0) return null;
      const totals = getSourceTotals(sourceKey);
      const net = totals ? totals.currentTotal - totals.previousTotal : fallbackNet;
      if (!Number.isFinite(net)) return null;
      const removed = Math.max(0, added - net);
      return { added, removed, net };
    };

    for (const item of filteredItems) {
      const stats = item?.creatorStats || {};
      templatesTotal += Number(stats.totalTemplates || 0);
      componentsTotal += Number(stats.totalComponents || 0);
      pluginsTotal += Number(stats.totalPlugins || 0);
      vectorsTotal += Number(stats.totalVectors || 0);
      tutorialsTotal += Number(stats.totalTutorials || 0);
      totalTotal += Number(stats.totalItems || 0);
      addDelta(
        stats.totalTemplatesDelta1d,
        (value) => {
          templatesDeltaPos += value;
        },
        (value) => {
          templatesDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalComponentsDelta1d,
        (value) => {
          componentsDeltaPos += value;
        },
        (value) => {
          componentsDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalPluginsDelta1d,
        (value) => {
          pluginsDeltaPos += value;
        },
        (value) => {
          pluginsDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalVectorsDelta1d,
        (value) => {
          vectorsDeltaPos += value;
        },
        (value) => {
          vectorsDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalTutorialsDelta1d,
        (value) => {
          tutorialsDeltaPos += value;
        },
        (value) => {
          tutorialsDeltaNegAbs += value;
        },
      );
      addDelta(
        stats.totalItemsDelta1d,
        (value) => {
          totalDeltaPos += value;
        },
        (value) => {
          totalDeltaNegAbs += value;
        },
      );
    }

    // Row-level creator deltas miss removals from creators that no longer exist in current snapshot.
    // When unfiltered, derive +/− from source snapshot set-diffs to keep footer aligned with global counts.
    const hasNoCreatorFilter = typeof state.search === "string" ? state.search.trim().length === 0 : true;
    if (hasNoCreatorFilter) {
      const templateDerived = deriveDeltaBreakdown("template", "templates", templatesDeltaPos - templatesDeltaNegAbs);
      const componentDerived = deriveDeltaBreakdown(
        "component",
        "components",
        componentsDeltaPos - componentsDeltaNegAbs,
      );
      const pluginDerived = deriveDeltaBreakdown("plugin", "plugins", pluginsDeltaPos - pluginsDeltaNegAbs);
      const vectorDerived = deriveDeltaBreakdown("vector", "vectors", vectorsDeltaPos - vectorsDeltaNegAbs);
      const tutorialDerived = deriveDeltaBreakdown("tutorial", "tutorials", tutorialsDeltaPos - tutorialsDeltaNegAbs);

      if (templateDerived) templatesDeltaNegAbs = Math.max(templatesDeltaNegAbs, templateDerived.removed);
      if (componentDerived) componentsDeltaNegAbs = Math.max(componentsDeltaNegAbs, componentDerived.removed);
      if (pluginDerived) pluginsDeltaNegAbs = Math.max(pluginsDeltaNegAbs, pluginDerived.removed);
      if (vectorDerived) vectorsDeltaNegAbs = Math.max(vectorsDeltaNegAbs, vectorDerived.removed);
      if (tutorialDerived) tutorialsDeltaNegAbs = Math.max(tutorialsDeltaNegAbs, tutorialDerived.removed);
    }

    if (creatorsFootCount) {
      const label = count === 1 ? "creator" : "creators";
      creatorsFootCount.textContent = `${count.toLocaleString()} ${label}`;
    }
    if (creatorsFootTemplates) creatorsFootTemplates.textContent = templatesTotal.toLocaleString();
    if (creatorsFootComponents) creatorsFootComponents.textContent = componentsTotal.toLocaleString();
    if (creatorsFootPlugins) creatorsFootPlugins.textContent = pluginsTotal.toLocaleString();
    if (creatorsFootVectors) creatorsFootVectors.textContent = vectorsTotal.toLocaleString();
    if (creatorsFootTutorials) creatorsFootTutorials.textContent = tutorialsTotal.toLocaleString();
    if (creatorsFootTotal) creatorsFootTotal.textContent = totalTotal.toLocaleString();
    if (creatorsFootTemplatesDelta) {
      creatorsFootTemplatesDelta.innerHTML = formatDeltaTotalsHtml(templatesDeltaPos, templatesDeltaNegAbs);
    }
    if (creatorsFootComponentsDelta) {
      creatorsFootComponentsDelta.innerHTML = formatDeltaTotalsHtml(componentsDeltaPos, componentsDeltaNegAbs);
    }
    if (creatorsFootPluginsDelta) {
      creatorsFootPluginsDelta.innerHTML = formatDeltaTotalsHtml(pluginsDeltaPos, pluginsDeltaNegAbs);
    }
    if (creatorsFootVectorsDelta) {
      creatorsFootVectorsDelta.innerHTML = formatDeltaTotalsHtml(vectorsDeltaPos, vectorsDeltaNegAbs);
    }
    if (creatorsFootTutorialsDelta) {
      creatorsFootTutorialsDelta.innerHTML = formatDeltaTotalsHtml(tutorialsDeltaPos, tutorialsDeltaNegAbs);
    }
    if (creatorsFootTotalDelta) {
      creatorsFootTotalDelta.innerHTML = formatDeltaTotalsHtml(totalDeltaPos, totalDeltaNegAbs);
    }
    return;
  }

  let newCount = 0;
  let movedInCount = 0;
  let comebackCount = 0;
  let repricedCount = 0;
  let positiveChangeSum = 0;
  let freeCount = 0;
  let paidCount = 0;
  let meanPrice = 0;
  let medianPrice = 0;
  let uniqueAuthorCount = 0;

  for (const item of filteredItems) {
    const change = item.ranks?.[context]?.[1];
    if (typeof change === "number" && change > 0) positiveChangeSum += change;
    if (change === "new") newCount++;
    if (change === "moved-in") movedInCount++;
    if (change === "comeback") comebackCount++;
    if (change === "price_change") repricedCount++;
  }

  freeCount = filteredItems.filter((t) => t.price === "Free").length;
  paidCount = filteredItems.filter((t) => t.price !== "Free").length;

  const paidPrices = [];
  for (const item of filteredItems) {
    if (item.price !== "Free") {
      const priceNum = parseFloat(item.price?.replace(/[$,]/g, ""));
      if (!Number.isNaN(priceNum) && priceNum > 0) {
        paidPrices.push(priceNum);
      }
    }
  }
  if (paidPrices.length > 0) {
    meanPrice = paidPrices.reduce((a, b) => a + b, 0) / paidPrices.length;
    paidPrices.sort((a, b) => a - b);
    const mid = Math.floor(paidPrices.length / 2);
    medianPrice = paidPrices.length % 2 !== 0 ? paidPrices[mid] : (paidPrices[mid - 1] + paidPrices[mid]) / 2;
  }

  uniqueAuthorCount = new Set(filteredItems.map((t) => t.authorName).filter(Boolean)).size;

  // Update tfoot cells
  // Rank column: count + free/paid with custom tooltip
  const rankTooltipTop = `${count.toLocaleString()} items`;
  const rankTooltipBottom = `${freeCount.toLocaleString()} free / ${paidCount.toLocaleString()} paid`;
  tfootRank.classList.add("with-tooltip");
  if (tfootPrice) tfootPrice.classList.add("with-tooltip");
  tfootRank.innerHTML = `
    <div class="tfoot-rank-total">${count.toLocaleString()}</div>
    <div class="tfoot-rank-breakdown">${freeCount.toLocaleString()} / ${paidCount.toLocaleString()}</div>
    <div class="tooltip top">
      <div class="tooltip-item">${rankTooltipTop}<br/>${rankTooltipBottom}</div>
    </div>
  `;

  // Change column: public event counters, with authenticated-only up-sum.
  const canShowUpSum = canSeeGatedMetrics();
  const upSumHtml = canShowUpSum
    ? `<div class="tfoot-change-upsum tfoot-change-positive">+${positiveChangeSum.toLocaleString()}</div>`
    : "";
  const upSumTooltipHtml = canShowUpSum
    ? `<div class="tfoot-change-tooltip-line tfoot-change-tooltip-upsum-line">up sum: <span class="tfoot-change-tooltip-value tfoot-change-positive">+${positiveChangeSum.toLocaleString()}</span><span class="tfoot-change-tooltip-desc">(sum of positive 1d rank gains)</span></div>`
    : "";
  const changeEventCountsHtml = `<span class="tfoot-change-new" role="button" tabindex="0" data-search-command=":new">${newCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-movedin" role="button" tabindex="0" data-search-command=":moved-in">${movedInCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-comeback" role="button" tabindex="0" data-search-command=":comeback">${comebackCount}</span> <span class="tfoot-change-sep">/</span> <span class="tfoot-change-price" role="button" tabindex="0" data-search-command=":repriced">${repricedCount}</span>`;
  const changeEventTooltipHtml = [
    ["new", newCount, "published today", "new"],
    ["movedin", movedInCount, "items changed category", "moved-in"],
    ["comeback", comebackCount, "was previously unlisted, is back today", "comeback"],
    ["repriced", repricedCount, "switch free↔paid category", "repriced"],
  ]
    .map(
      ([className, value, description, label]) =>
        `<div class="tfoot-change-tooltip-line tfoot-change-tooltip-${className}-line">${label}: <span class="tfoot-change-tooltip-value tfoot-change-tooltip-value-${className}" role="button" tabindex="0" data-search-command=":${label === "repriced" ? "repriced" : label}">${value}</span><span class="tfoot-change-tooltip-desc">(${description})</span></div>`,
    )
    .join("");
  if (tfootChange) {
    tfootChange.classList.add("with-tooltip");
    tfootChange.innerHTML = `${upSumHtml}<div class="tfoot-change-events">${changeEventCountsHtml}</div><div class="tooltip top"><div class="tooltip-item tfoot-change-tooltip-item">${upSumTooltipHtml}${changeEventTooltipHtml}</div></div>`;
  }

  // Price column: avg/mid
  const avgStr = meanPrice > 0 ? `$${Math.round(meanPrice)}` : "-";
  const midStr = medianPrice > 0 ? `$${Math.round(medianPrice)}` : "-";
  if (tfootPrice) {
    tfootPrice.innerHTML = `<div class="tfoot-price-label">avg / mid</div><div class="tfoot-price-values">${avgStr} / ${midStr}</div><div class="tooltip top"><div class="tooltip-item">Average price vs median price</div></div>`;
  }

  // Name column: empty
  if (tfootName) tfootName.textContent = "";

  // Creator column: unique creator count
  const creatorLabel = uniqueAuthorCount === 1 ? "creator" : "creators";
  if (tfootAuthor) tfootAuthor.textContent = `${uniqueAuthorCount.toLocaleString()} ${creatorLabel}`;
}

function buildActiveCategorySearchIndex() {
  const activeTree = sortCategoryTree(getActiveCategoryTree(), getCategoryTreeOrderMap());
  categorySearchState.index = buildCategorySearchIndex(activeTree, { countCategoryItems });
  categorySearchState.results = [];
  categorySearchState.activeIndex = -1;
}

function getCategorySearchElements() {
  return {
    input: document.getElementById("category-search-input"),
    clearButton: document.getElementById("category-search-clear"),
    tree: document.getElementById("category-tree"),
  };
}

function trackCategorySearchCleared(previousQuery) {
  const queryLength = String(previousQuery || "").length;
  if (queryLength <= 0) return;
  trackEvent("category_search_cleared", {
    query_length: queryLength,
    type: state.type,
  });
}

function resetCategorySearch(options = {}) {
  const { input, tree } = getCategorySearchElements();
  const previousQuery = input?.value || categorySearchState.lastQuery || "";
  if (input) {
    input.value = "";
    input.classList.remove("search-active");
  }
  if (tree) resetCategorySearchTree(tree);
  categorySearchState.results = [];
  categorySearchState.activeIndex = -1;
  categorySearchState.lastQuery = "";
  categorySearchState.started = false;
  if (options.track) trackCategorySearchCleared(previousQuery);
}

function updateActiveCategorySearchResult() {
  document.querySelectorAll(".category-item.is-active-search-result").forEach((result) => {
    result.classList.remove("is-active-search-result");
    result.setAttribute("aria-selected", "false");
  });

  categorySearchState.results.forEach((entry, index) => {
    const result = document.querySelector(`.category-item[data-category-id="${cssEscape(entry.id)}"]`);
    if (!result) return;
    const isActive = index === categorySearchState.activeIndex;
    if (isActive) result.classList.add("is-active-search-result");
    result.setAttribute("aria-selected", isActive ? "true" : "false");
  });
}

function createCategorySearchControls() {
  const wrap = document.createElement("div");
  wrap.className = "category-search-wrap";
  wrap.innerHTML = `
    <input id="category-search-input" class="category-search-input" type="search"
      placeholder="Search categories..." autocomplete="off" spellcheck="false"
      aria-label="Search categories" />
    <button id="category-search-clear" type="button" class="category-search-clear-button" aria-label="Clear category search"></button>
  `;
  return wrap;
}

function updateCategorySearchInputState(input) {
  input?.classList.toggle("search-active", Boolean(input.value.trim()));
}

function getDirectCategoryChildren(node) {
  return Array.from(node?.children || []).find((child) => child.classList?.contains("category-children")) || null;
}

function getDirectCategoryExpandIcon(node) {
  return node?.querySelector(":scope > .category-item > .category-expand-icon") || null;
}

function resetCategorySearchTree(tree) {
  tree.hidden = false;
  tree.querySelector(".category-search-empty")?.remove();
  tree.querySelectorAll(".category-node[hidden]").forEach((node) => {
    node.hidden = false;
  });
  tree.querySelectorAll(".category-search-match").forEach((item) => {
    item.classList.remove("category-search-match");
  });
  tree.querySelectorAll(".is-active-search-result").forEach((item) => {
    item.classList.remove("is-active-search-result");
    item.setAttribute("aria-selected", "false");
  });
  tree.querySelectorAll('[data-category-search-expanded="true"]').forEach((element) => {
    element.classList.remove("expanded");
    delete element.dataset.categorySearchExpanded;
  });
  expandCategoryAncestorPath(state.category);
}

function setCategorySearchExpanded(node) {
  const children = getDirectCategoryChildren(node);
  const expandIcon = getDirectCategoryExpandIcon(node);
  if (!children) return;
  if (!children.classList.contains("expanded")) {
    children.classList.add("expanded");
    children.dataset.categorySearchExpanded = "true";
  }
  if (expandIcon && !expandIcon.classList.contains("expanded")) {
    expandIcon.classList.add("expanded");
    expandIcon.dataset.categorySearchExpanded = "true";
  }
}

function setCategoryNodeExpanded(node) {
  const children = getDirectCategoryChildren(node);
  const expandIcon = getDirectCategoryExpandIcon(node);
  if (children) children.classList.add("expanded");
  if (expandIcon) expandIcon.classList.add("expanded");
}

function expandCategoryAncestorPath(categoryId) {
  if (!categoryId || typeof document === "undefined") return false;
  const selectedItem = document.querySelector(`.category-item[data-category-id="${cssEscape(categoryId)}"]`);
  let node = selectedItem?.closest(".category-node")?.parentElement?.closest(".category-node") || null;
  let didExpand = false;
  while (node) {
    setCategoryNodeExpanded(node);
    didExpand = true;
    node = node.parentElement?.closest(".category-node") || null;
  }
  const tree = selectedItem?.closest("#category-tree");
  if (tree && selectedItem) {
    const treeRect = tree.getBoundingClientRect();
    const itemRect = selectedItem.getBoundingClientRect();
    const itemTop = itemRect.top - treeRect.top;
    const itemBottom = itemRect.bottom - treeRect.top;
    if (tree.clientHeight > 0 && itemRect.height > 0) {
      if (itemTop < 0) tree.scrollTop += itemTop;
      else if (itemBottom > tree.clientHeight) tree.scrollTop += itemBottom - tree.clientHeight;
      else if (typeof selectedItem.scrollIntoView === "function")
        selectedItem.scrollIntoView({ block: "nearest", inline: "nearest" });
    } else if (typeof selectedItem.scrollIntoView === "function") {
      selectedItem.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  } else if (typeof selectedItem?.scrollIntoView === "function") {
    selectedItem.scrollIntoView({ block: "nearest", inline: "nearest" });
  }
  return didExpand;
}

function nodeHasVisibleDescendant(node) {
  const children = getDirectCategoryChildren(node);
  if (!children) return false;
  return Array.from(children.querySelectorAll(".category-node")).some((childNode) => !childNode.hidden);
}

function renderCategorySearchResults(query) {
  const { tree } = getCategorySearchElements();
  if (!tree) return;
  const trimmedQuery = String(query || "").trim();
  categorySearchState.lastQuery = trimmedQuery;
  updateCategorySearchInputState(document.getElementById("category-search-input"));

  if (!trimmedQuery) {
    resetCategorySearch();
    return;
  }

  const treeState = getCategorySearchTreeState(categorySearchState.index, trimmedQuery);
  categorySearchState.results = treeState.results;
  categorySearchState.activeIndex = -1;
  resetCategorySearchTree(tree);

  if (!categorySearchState.started) {
    categorySearchState.started = true;
    trackEvent("category_search_started", {
      query_length: trimmedQuery.length,
      result_count: categorySearchState.results.length,
      type: state.type,
    });
  }

  tree.querySelectorAll(".category-node:not(.category-all-option)").forEach((node) => {
    const item = node.querySelector(".category-item[data-category-id]");
    const categoryId = item?.dataset.categoryId;
    if (!categoryId) return;
    node.hidden = !treeState.visibleIds.has(categoryId);
    item.classList.toggle("category-search-match", treeState.matchingIds.has(categoryId));
  });

  tree.querySelectorAll(".category-node:not(.category-all-option)").forEach((node) => {
    if (!node.hidden && nodeHasVisibleDescendant(node)) setCategorySearchExpanded(node);
  });

  if (categorySearchState.results.length === 0) {
    const empty = document.createElement("div");
    empty.className = "category-search-empty";
    empty.textContent = "huh?";
    tree.querySelector(".category-search-wrap")?.insertAdjacentElement("afterend", empty);
    return;
  }
}

function trackCategorySearchSelected(entry) {
  if (!entry) return;
  trackEvent("category_search_selected", {
    query_length: categorySearchState.lastQuery.length,
    result_count: categorySearchState.results.length,
    selected_category_id: entry.id,
    selected_depth: entry.depth,
    type: state.type,
  });
}

function isCategorySelectionDisabled(categoryId) {
  return state.type === "expert" && countCategoryItems(categoryId) === 0;
}

function selectCategorySearchEntry(entry) {
  if (!entry) return;
  if (isCategorySelectionDisabled(entry.id)) return;
  trackCategorySearchSelected(entry);
  handleCategorySelect(entry.id, entry.name);
}

function handleCategoryTreeItemSelect(categoryId, categoryName) {
  const entry = categorySearchState.lastQuery
    ? categorySearchState.index.find((candidate) => candidate.id === categoryId)
    : null;
  if (isCategorySelectionDisabled(categoryId)) return;
  if (entry) trackCategorySearchSelected(entry);
  handleCategorySelect(categoryId, categoryName);
}

function bindCategorySearchControls() {
  const { input, clearButton } = getCategorySearchElements();
  if (input && !input.dataset.categorySearchBound) {
    input.dataset.categorySearchBound = "true";
    input.addEventListener("input", () => renderCategorySearchResults(input.value));
    input.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (input.value.trim()) {
          resetCategorySearch({ track: true });
        } else {
          closeCategoryDropdown("escape");
        }
        return;
      }

      const resultCount = categorySearchState.results.length;
      if (event.key === "ArrowDown" && resultCount > 0) {
        event.preventDefault();
        categorySearchState.activeIndex = (categorySearchState.activeIndex + 1) % resultCount;
        updateActiveCategorySearchResult();
      } else if (event.key === "ArrowUp" && resultCount > 0) {
        event.preventDefault();
        categorySearchState.activeIndex =
          categorySearchState.activeIndex <= 0 ? resultCount - 1 : categorySearchState.activeIndex - 1;
        updateActiveCategorySearchResult();
      } else if (event.key === "Enter" && resultCount > 0) {
        event.preventDefault();
        const index = categorySearchState.activeIndex >= 0 ? categorySearchState.activeIndex : 0;
        selectCategorySearchEntry(categorySearchState.results[index]);
      }
    });
  }
  if (input && clearButton && !clearButton.dataset.categorySearchBound) {
    clearButton.dataset.categorySearchBound = "true";
    clearButton.addEventListener("click", () => {
      resetCategorySearch({ track: true });
      input.focus();
    });
  }
}

function closeCategoryDropdown() {
  const panel = document.getElementById("category-dropdown-panel");
  if (panel) panel.classList.remove("open");
  resetCategorySearch({ track: true });
}

function shouldCloseCategoryDropdownOnCategorySelect() {
  return false;
}

// ============================================
// CATEGORY TREE
// ============================================

/**
 * Sort category tree alphabetically at each level independently
 * Preserves all category properties (id, url, pageUrl, etc.)
 */
function getCategoryTreeOrderMap(type = state.type) {
  if (type !== "template") return null;
  const order = new Map();
  let nextOrder = 0;
  for (const group of CURATED_TEMPLATE_CATEGORY_NAV) {
    order.set(group.id, nextOrder++);
    for (const [, childId] of group.children || []) order.set(childId, nextOrder++);
  }
  return order;
}

function sortCategoryTree(categories, orderMap = null) {
  // Sort this level by name (case-insensitive)
  const sorted = [...categories].sort((a, b) => {
    const aOrder = orderMap?.get(String(a.id));
    const bOrder = orderMap?.get(String(b.id));
    if (aOrder !== undefined || bOrder !== undefined) {
      if (aOrder === undefined) return 1;
      if (bOrder === undefined) return -1;
      if (aOrder !== bOrder) return aOrder - bOrder;
    }
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

  // Recursively sort children at each level
  return sorted.map((cat) => ({
    ...cat,
    children: cat.children && cat.children.length > 0 ? sortCategoryTree(cat.children, orderMap) : cat.children,
  }));
}

function renderCategoryTree() {
  const container = document.getElementById("category-tree");
  if (!container) return;
  buildActiveCategorySearchIndex();
  const typeKey = buildApiTypeKey();
  const lastUpdated = state.api.typeLastUpdated.get(typeKey) || state.api.lastUpdated || null;
  const cacheKey = buildCategoryTreeCacheKey(typeKey, lastUpdated);
  const canUseCache = state.api.activeTypeKey === typeKey;
  const cachedHtml = canUseCache ? state.api.categoryTreeCache.get(cacheKey) : null;
  if (typeof cachedHtml === "string") {
    container.innerHTML = cachedHtml;
    attachCategoryTreeEventHandlers(container);
    updateCategorySelectedState(state.category);
    bindCategorySearchControls();
    resetCategorySearch();
    if (document.getElementById("category-dropdown-panel")?.classList.contains("open")) {
      expandCategoryAncestorPath(state.category);
    }
    startPerfMeasure("ranks:category-tree-cache", { typeKey, hit: true }).end();
    return;
  }

  container.innerHTML = "";

  // "All Categories" option
  const allOption = document.createElement("div");
  allOption.className = "category-node category-all-option";
  allOption.innerHTML = `
    <div class="category-item category-item-all">
      <span class="category-label-text category-label-clickable">${getAllCategoriesOptionLabel()}</span>
    </div>
  `;
  allOption.querySelector(".category-label-text").addEventListener("click", (e) => {
    e.stopPropagation();
    handleCategoryClear();
    closeCategoryDropdown();
  });
  container.appendChild(allOption);
  container.appendChild(createCategorySearchControls());

  // Render tree - sort alphabetically at each level
  const activeTree = getActiveCategoryTree();
  if (activeTree.length > 0) {
    const sortedTree = sortCategoryTree(activeTree, getCategoryTreeOrderMap());
    container.appendChild(renderCategoryLevel(sortedTree, 1));
  }
  updateCategorySelectedState(state.category);
  if (document.getElementById("category-dropdown-panel")?.classList.contains("open")) {
    expandCategoryAncestorPath(state.category);
  }
  if (canUseCache) state.api.categoryTreeCache.set(cacheKey, container.innerHTML);
  bindCategorySearchControls();
  startPerfMeasure("ranks:category-tree-cache", { typeKey, hit: false }).end();
}

function attachCategoryTreeEventHandlers(container) {
  container.querySelector(".category-all-option .category-label-text")?.addEventListener("click", (e) => {
    e.stopPropagation();
    handleCategoryClear();
    closeCategoryDropdown();
  });

  container.querySelectorAll(".category-expand-icon").forEach((expandIcon) => {
    expandIcon.addEventListener("click", (e) => {
      e.stopPropagation();
      const node = expandIcon.closest(".category-node");
      const children = node?.querySelector(".category-children");
      if (children) {
        children.classList.toggle("expanded");
        expandIcon.classList.toggle("expanded");
        trackEvent(children.classList.contains("expanded") ? "category_tree_expanded" : "category_tree_collapsed", {
          categoryId: node?.querySelector(".category-item")?.getAttribute("data-category-id") || null,
        });
      }
    });
  });

  container.querySelectorAll(".category-item[data-category-id]").forEach((item) => {
    item.addEventListener("click", () => {
      const categoryId = item.dataset.categoryId;
      if (categoryId && isCategorySelectionDisabled(categoryId)) return;
      const categoryName =
        categoryId && getActiveCategoryMeta()[categoryId]?.name
          ? getActiveCategoryMeta()[categoryId].name
          : item.querySelector(".category-label-text")?.childNodes?.[0]?.textContent?.trim() || categoryId;
      if (categoryId) handleCategoryTreeItemSelect(categoryId, categoryName);
    });
  });
}

function renderCategoryLevel(categories, level) {
  const fragment = document.createDocumentFragment();
  const isTopLevel = level === 1;

  for (const cat of categories) {
    const node = document.createElement("div");
    node.className = `category-node category-level-${level}`;

    const hasChildren = cat.children && cat.children.length > 0;
    const count = countCategoryItems(cat.id);
    const isDisabled = state.type === "expert" && count === 0;

    const item = document.createElement("div");
    item.className = `category-item${isDisabled ? " category-item-disabled" : ""}`;
    item.dataset.categoryId = cat.id;
    item.setAttribute("aria-disabled", String(isDisabled));

    // Expand icon
    const expandIcon = document.createElement("span");
    expandIcon.className = `category-expand-icon ${hasChildren ? "" : "hidden"}`;
    expandIcon.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    item.appendChild(expandIcon);

    const displayName = isTopLevel ? normalizeCategoryDisplayName(cat.name) : normalizeCategoryDisplayName(cat.name);

    const label = document.createElement("span");
    label.className = "category-label-text";
    const labelName = document.createElement("span");
    labelName.className = "category-label-name";
    labelName.textContent = displayName;
    const labelCount = document.createElement("span");
    labelCount.className = "category-item-count";
    labelCount.textContent = `(${count})`;
    label.append(labelName, labelCount);
    item.appendChild(label);

    // Click handlers
    expandIcon.addEventListener("click", (e) => {
      e.stopPropagation();
      const children = node.querySelector(".category-children");
      if (children) {
        children.classList.toggle("expanded");
        expandIcon.classList.toggle("expanded");
        trackEvent(children.classList.contains("expanded") ? "category_tree_expanded" : "category_tree_collapsed", {
          categoryId: cat.id,
        });
      }
    });

    // Whole row triggers category selection (except expand icon which stops propagation)
    item.addEventListener("click", (_e) => {
      if (isDisabled) return;
      handleCategoryTreeItemSelect(cat.id, cat.name);
    });

    node.appendChild(item);

    // Children
    if (hasChildren) {
      const childrenContainer = document.createElement("div");
      childrenContainer.className = "category-children expanded";
      expandIcon.classList.add("expanded");
      childrenContainer.appendChild(renderCategoryLevel(cat.children, level + 1));
      node.appendChild(childrenContainer);
    }

    fragment.appendChild(node);
  }

  return fragment;
}

function countCategoryItems(categoryId) {
  return state.allItems.filter((item) => {
    if (item.type !== state.type) return false;
    const context = state.type === "expert" && state.expertType
      ? `expert-type-${state.expertType}-${categoryId}-all`
      : `${categoryId}-all`;
    return item.ranks?.[context] != null;
  }).length;
}

// ============================================
// ANALYTICS
// ============================================

function getSimpleAnalyticsAuthState() {
  if (typeof document === "undefined") return AUTH_BOOT_STATE_PENDING;
  const authState = document.body?.dataset?.authState || authClientState.status || AUTH_BOOT_STATE_PENDING;
  if (authState === AUTH_BOOT_STATE_AUTHENTICATED) return AUTH_BOOT_STATE_AUTHENTICATED;
  if (authState === AUTH_BOOT_STATE_LOGGED_OUT) return AUTH_BOOT_STATE_LOGGED_OUT;
  return AUTH_BOOT_STATE_PENDING;
}

function getAnalyticsEnvironment() {
  if (typeof window === "undefined") return "unknown";
  const hostname = window.location?.hostname || "";
  if (hostname === "framer-ranks.com" || hostname === "www.framer-ranks.com") return "production";
  if (hostname === "localhost" || hostname === "127.0.0.1") return "local";
  if (hostname.endsWith(".vercel.app")) return "preview";
  return "unknown";
}

function classifyHref(href) {
  if (!href) return "none";
  try {
    const parsed = new URL(href, window.location.origin);
    return parsed.origin === window.location.origin ? "internal" : "external";
  } catch (_error) {
    return "unknown";
  }
}

function getSafeLinkMetadata(link) {
  const rawHref = link?.getAttribute?.("href") || "";
  const parsedHref = rawHref ? new URL(rawHref, window.location.origin) : null;
  const text = String(link?.textContent || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 80);
  const row = link?.closest?.("[data-id]");
  return {
    linkId: link?.id || null,
    linkText: text || null,
    hrefKind: classifyHref(rawHref),
    hrefHost: parsedHref?.hostname || null,
    rowItemId: row?.getAttribute("data-id") || null,
  };
}

function trackEvent(eventName, metadata = {}) {
  if (typeof window.sa_event === "function") {
    window.sa_event(eventName, {
      ...metadata,
      type: state.type,
      auth_state: getSimpleAnalyticsAuthState(),
      analytics_environment: getAnalyticsEnvironment(),
      page_hostname: window.location?.hostname || null,
    });
  }
}

async function postClientAuthEvent(eventType, metadata = {}) {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  const baseUrl = getBaseUrl();
  const payload = {
    eventType,
    route: window.location?.pathname || "/ranks.html",
    statusCode: metadata.statusCode,
    errorCode: metadata.errorCode,
    latencyMs: metadata.latencyMs,
  };

  try {
    const body = JSON.stringify(payload);
    const url = `${baseUrl}/api/auth/events`;
    if (typeof window.navigator?.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (window.navigator.sendBeacon(url, blob)) return;
    }
    await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      credentials: "include",
      keepalive: true,
      body,
    });
  } catch {
    // Auth observability must never block the UI path.
  }
}

export function getCreatorMergeKey(name, url) {
  const textUrl = typeof url === "string" ? url.trim() : "";
  const slugMatch = textUrl.match(/\/@([^/?#]+)/i);
  if (slugMatch?.[1]) return slugMatch[1].toLowerCase();
  const textName = typeof name === "string" ? name.trim().toLowerCase() : "";
  return textName || "";
}

export function buildCreatorItemsFromData(data = {}) {
  const creators = Array.isArray(data.creators) ? data.creators : [];
  return creators.map((creator, index) => {
    const slug = String(creator.creatorSlug || "").trim();
    const creatorUrl =
      typeof creator.creatorUrl === "string" && creator.creatorUrl.length > 0
        ? creator.creatorUrl
        : slug
          ? `https://framer.com/@${slug}`
          : null;
    const totalTemplates = Number(creator.totalTemplates || 0);
    const totalComponents = Number(creator.totalComponents || 0);
    const totalPlugins = Number(creator.totalPlugins || 0);
    const totalVectors = Number(creator.totalVectors || 0);
    const totalTutorials = Number(creator.totalTutorials || 0);
    const totalItems = totalTemplates + totalComponents + totalPlugins + totalVectors + totalTutorials;

    return {
      id: `creator-${slug || index}`,
      type: "creator",
      name: creator.creatorName || slug || "Unknown creator",
      creatorSlug: slug,
      creatorUrl,
      url: creatorUrl || "https://framer.com/marketplace/",
      thumbnail: creator.creatorAvatar || null,
      price: "-",
      authorName: slug ? `@${slug}` : "-",
      authorUrl: creatorUrl,
      ranks: {},
      categories: [],
      badges: { crowns: [], medals: [], medalScore: 0 },
      creatorStats: {
        totalItems,
        totalTemplates,
        totalComponents,
        totalPlugins,
        totalVectors,
        totalTutorials,
        totalPluginsDelta1d:
          creator.totalPluginsDelta1d === null || creator.totalPluginsDelta1d === undefined
            ? null
            : Number(creator.totalPluginsDelta1d),
        totalVectorsDelta1d:
          creator.totalVectorsDelta1d === null || creator.totalVectorsDelta1d === undefined
            ? null
            : Number(creator.totalVectorsDelta1d),
        totalTutorialsDelta1d:
          creator.totalTutorialsDelta1d === null || creator.totalTutorialsDelta1d === undefined
            ? null
            : Number(creator.totalTutorialsDelta1d),
        totalItemsDelta1d:
          creator.totalItemsDelta1d === null || creator.totalItemsDelta1d === undefined
            ? null
            : Number(creator.totalItemsDelta1d),
        totalTemplatesDelta1d:
          creator.totalTemplatesDelta1d === null || creator.totalTemplatesDelta1d === undefined
            ? null
            : Number(creator.totalTemplatesDelta1d),
        totalComponentsDelta1d:
          creator.totalComponentsDelta1d === null || creator.totalComponentsDelta1d === undefined
            ? null
            : Number(creator.totalComponentsDelta1d),
      },
    };
  });
}

// ============================================
// INITIALIZATION
// ============================================

async function loadData(forceApiContext = false) {
  log("[INIT] Loading data...");
  const startTime = performance.now();

  state.api.enabled = isApiModeEnabled();

  if (!state.api.enabled) {
    throw new Error("Static ranks-data.json loading disabled. Enable API mode.");
  }

  await loadApiContextForCurrentState(forceApiContext);
  const duration = Math.round(performance.now() - startTime);
  log(`[INIT] Loaded API context '${state.api.activeTypeKey || "-"}' in ${duration}ms`);
  return { currentDateStr: state.api.lastUpdated || "-", loadedFrom: "api" };
}

function renderRows() {
  if (!acquireLock("rendering")) return;

  cancelProgressiveRowRender();
  log("[INIT] Rendering all rows...");
  const startTime = performance.now();

  if (
    isProgressiveRowsEnabled() &&
    !state.clusterize &&
    state.type !== "creator" &&
    state.type !== "expert" &&
    state.allItems.length > 0
  ) {
    progressiveRenderState.generation += 1;
    progressiveRenderState.active = true;
    progressiveRenderState.typeKey = buildApiTypeKey();
    progressiveRenderState.lastUpdated = state.api.lastUpdated || null;
    state.allRows = state.allItems.map((item, index) => ({
      html: "",
      itemId: String(item.id || index),
      categoryCellHtml: "",
      index,
    }));
    state.rowLookup = new Map(state.allRows.map((row) => [row.itemId, row]));
    log(`[RENDER] Prepared ${state.allRows.length} progressive row shells`);
    releaseLock("rendering");
    return;
  }

  const renderSpan = startPerfMeasure("ranks:rows-render", { itemCount: state.allItems.length });
  state.allRows = renderAllRows(state.allItems, getRenderCategoryOptions());
  renderSpan.end({ rowCount: state.allRows.length });
  state.rowLookup = new Map(state.allRows.map((row) => [row.itemId, row]));

  const duration = Math.round(performance.now() - startTime);
  log(`[INIT] Rendered ${state.allRows.length} rows in ${duration}ms`);

  releaseLock("rendering");
}

function mergeEngagementIntoItems(map) {
  const items = state.allItems || [];
  for (const item of items) {
    const engagement = map?.get(String(item.id));
    item.likes = engagement ? engagement.likes : null;
    item.comments = engagement ? engagement.comments : null;
  }
}

function clearEngagementFromItems() {
  const items = state.allItems || [];
  for (const item of items) {
    item.likes = null;
    item.comments = null;
  }
}

function applyEngagementData(map) {
  const applySpan = startPerfMeasure("ranks:engagement-apply", {});
  state.engagementData = map;
  mergeEngagementIntoItems(map);
  clearFilterCache();
  state.filteredRows = filterRows();
  sortFilteredRows();
  const htmlRowsResult = buildHtmlRowsForCurrentView();
  state.api.assembledViewCache.clear();
  saveAssembledViewCache(buildViewCacheKey(), htmlRowsResult.htmlRows);
  updateVirtualizer(htmlRowsResult.htmlRows);
  applySpan.end();
}

function scheduleEngagementLoad() {
  const run = () => {
    if (getAuthBootState() !== AUTH_BOOT_STATE_AUTHENTICATED) return;
    if (
      document.body?.classList.contains("ranks-booting") ||
      !state.clusterize ||
      !document.querySelector("#clusterize-content tr[data-id]")
    ) {
      window.setTimeout(scheduleEngagementLoad, 50);
      return;
    }
    loadEngagementForCurrentType().catch(() => {
      // ignore engagement load failures
    });
  };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 1000 });
  } else if (typeof window !== "undefined") {
    window.setTimeout(run, 0);
  }
}

async function loadEngagementForCurrentType() {
  const loadSpan = startPerfMeasure("ranks:engagement-load", {});
  try {
    const typeKey = buildApiTypeKey();
    if (!typeKey) return;

    const lastUpdateMs = state.api.lastUpdateMs || (state.api.lastUpdated ? Date.parse(state.api.lastUpdated) : 0);
    if (!lastUpdateMs || !Number.isFinite(lastUpdateMs)) {
      clearEngagementFromItems();
      state.engagementData = null;
      return;
    }

    const typeParam = `${state.type}s`;
    const cacheKey = buildEngagementCacheKey(1, lastUpdateMs, typeParam);

    // Check in-memory cache first (instant same-session hits)
    if (engagementMemoryCache.has(cacheKey)) {
      const map = engagementMemoryCache.get(cacheKey);
      applyEngagementData(map);
      if (perfState.enabled) {
        startPerfMeasure("ranks:engagement-cache", { source: "memory", cacheKey }).end();
      }
      return;
    }

    // Deduplicate in-flight requests per cache key
    if (engagementInFlightByKey.has(cacheKey)) {
      await engagementInFlightByKey.get(cacheKey);
      return;
    }

    state.engagementLoading = true;

    const loadPromise = (async () => {
      try {
        // Try IndexedDB cache
        const cached = await readEngagementPayload(cacheKey);
        if (cached) {
          const items = normalizeEngagementItems(cached);
          const map = new Map();
          for (const [id, likes, comments] of items) {
            map.set(id, { likes, comments });
          }
          engagementMemoryCache.set(cacheKey, map);
          trimEngagementMemoryCache();
          applyEngagementData(map);
          if (perfState.enabled) {
            startPerfMeasure("ranks:engagement-cache", { source: "indexeddb", cacheKey }).end();
          }
          return;
        }

        const pageAuth = getPageAuthParam();
        const engagementUrl = `/api/ranks-engagement-url?dataset=gated-data&type=${encodeURIComponent(typeParam)}&lastUpdate=${lastUpdateMs}${pageAuth ? `&auth=${encodeURIComponent(pageAuth)}` : ""}`;
        const res = await fetch(engagementUrl, {
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 401) {
            clearEngagementFromItems();
            state.engagementData = null;
            return;
          }
          throw new Error(`Engagement URL fetch failed: ${res.status}`);
        }

        const { url } = await res.json();
        if (!url) throw new Error("Missing signed URL");

        const dataRes = await fetch(url);
        if (!dataRes.ok) throw new Error(`Engagement data fetch failed: ${dataRes.status}`);

        const payload = await dataRes.json();
        const items = normalizeEngagementItems(payload);

        const map = new Map();
        for (const [id, likes, comments] of items) {
          map.set(id, { likes, comments });
        }

        // Write to in-memory cache
        engagementMemoryCache.set(cacheKey, map);
        trimEngagementMemoryCache();

        // Persist to IndexedDB
        writeEngagementPayload(cacheKey, payload).catch(() => {
          // ignore IndexedDB write failures
        });

        if (buildApiTypeKey() !== typeKey) return;
        applyEngagementData(map);
        if (perfState.enabled) {
          startPerfMeasure("ranks:engagement-cache", { source: "network", cacheKey }).end();
        }
      } catch (error) {
        console.error("[ENGAGEMENT] Load error:", error);
        clearEngagementFromItems();
        state.engagementData = new Map();
      } finally {
        state.engagementLoading = false;
      }
    })();

    engagementInFlightByKey.set(cacheKey, loadPromise);
    try {
      await loadPromise;
    } finally {
      engagementInFlightByKey.delete(cacheKey);
    }
  } finally {
    loadSpan.end();
  }
}

function trimEngagementMemoryCache() {
  while (engagementMemoryCache.size > ENGAGEMENT_MEMORY_CACHE_MAX_SIZE) {
    const oldestKey = engagementMemoryCache.keys().next().value;
    if (!oldestKey) break;
    engagementMemoryCache.delete(oldestKey);
  }
}

function initClusterize() {
  log("[INIT] Initializing Clusterize...");

  // Initial filter and sort
  state.filteredRows = filterRows();
  sortFilteredRows();

  if (progressiveRenderState.active) {
    renderInitialProgressiveRows();
  }

  const htmlRowsResult = buildHtmlRowsForCurrentView();
  const htmlRows = htmlRowsResult.htmlRows;
  const bootstrapContainer = document.getElementById("clusterize-content");
  const bootstrapDomRows =
    bootstrapContainer?.dataset.bootstrapRows === "1"
      ? Array.from(bootstrapContainer.querySelectorAll("tr[data-id]"))
      : [];
  const viewCacheKey = buildViewCacheKey();
  saveCurrentViewCache();
  saveAssembledViewCache(viewCacheKey, htmlRows);

  // Clusterize's constructor normally calls insertToDOM immediately. That
  // replaces the bootstrap rows and causes a visible image/avatar blink.
  // Let it measure the existing rows, but defer that first DOM write.
  const ClusterizeCtor = globalThis.Clusterize;
  const clusterizePrototype = ClusterizeCtor.prototype;
  const originalInsertToDOM = clusterizePrototype.insertToDOM;
  const deferInitialInsert = bootstrapDomRows.length > 0;
  if (deferInitialInsert) {
    clusterizePrototype.insertToDOM = function deferredInitialInsert(rows, cache) {
      if (!this.__ranksInitialInsertDeferred) {
        this.__ranksInitialInsertDeferred = true;
        this.options.cluster_height || this.exploreEnvironment(rows, cache);
        return;
      }
      return originalInsertToDOM.call(this, rows, cache);
    };
  }
  state.clusterize = new ClusterizeCtor({
    scrollId: "clusterize-scroll",
    contentId: "clusterize-content",
    rows: htmlRows,
    tag: "tr",
    // Keep larger blocks to avoid scroll jitter/jumps with heavy table rows.
    rows_in_block: 15,
    blocks_in_cluster: 2,
    show_no_data_row: false,
  });
  if (deferInitialInsert) clusterizePrototype.insertToDOM = originalInsertToDOM;
  if (progressiveRenderState.active) {
    progressiveRenderState.virtualizerVersion = progressiveRenderState.renderVersion;
  }

  // Clusterize replaces tbody.innerHTML during construction. Restore the
  // already-painted bootstrap nodes immediately in the same task so the
  // browser never sees a remove/reinsert cycle for their images and avatars.
  // The deferred Clusterize insert leaves the existing bootstrap nodes in
  // place; do not walk/replace every row during the first handoff.
  if (progressiveRenderState.active && bootstrapContainer) {
    bootstrapContainer.dataset.progressiveRows = "1";
    // The full row count is already known, even though only the first rows
    // have HTML. Reserve that height now so the scrollbar never grows through
    // an intermediate partial-list size while background rendering runs.
    setProgressiveFullHeightSpacer();
  }

  if (shouldUsePostHydrateContextCells()) {
    hydrateVisibleContextCells();
  }
  if (progressiveRenderState.active) {
    scheduleProgressiveRowRender();
  }
  scheduleClusterizeLayoutRefresh("init");
  const thumbnailSpan = startPerfMeasure("ranks:init-thumbnail-schedule", {});
  scheduleVisibleThumbnailHydration(1500);
  thumbnailSpan.end();

  // Initialize sparklines for initial rows
  const sparklineSpan = startPerfMeasure("ranks:init-sparklines", {});
  if (!progressiveRenderState.active) initSparklines();
  sparklineSpan.end();
  const csvSpan = startPerfMeasure("ranks:init-csv-state", {});
  updateCsvDownloadButtonState();
  csvSpan.end();

  log(`[INIT] Clusterize initialized with ${htmlRows.length} rows`);
}

function setupEventListeners() {
  bindCategoryNav();
  // Type buttons
  ITEM_TYPE_OPTIONS.forEach(({ buttonId, type }) => {
    document.getElementById(buttonId)?.addEventListener("click", () => handleTypeSwitch(type));
  });
  renderDataTypeDropdownOptions();
  renderPricingDropdownOptions();
  renderRankingModeDropdownOptions();
  document.getElementById("data-type-dropdown-button")?.addEventListener("click", (e) => {
    e.stopPropagation();
    const panel = document.getElementById("data-type-dropdown-panel");
    const button = document.getElementById("data-type-dropdown-button");
    if (!panel) return;
    const willOpen = !panel.classList.contains("open");
    panel.classList.toggle("open", willOpen);
    button?.setAttribute("aria-expanded", String(willOpen));
    trackEvent(willOpen ? "data_type_dropdown_opened" : "data_type_dropdown_closed");
  });
  document.getElementById("data-type-dropdown-content")?.addEventListener("click", (e) => {
    const option = e.target.closest(".data-type-option[data-type]");
    if (!option) return;
    e.stopPropagation();
    handleTypeSwitch(option.dataset.type);
    closeDataTypeDropdown("select");
  });
  renderExpertTypeDropdown();
  document.getElementById("expert-type-dropdown-button")?.addEventListener("click", (event) => {
    event.stopPropagation();
    const panel = document.getElementById("expert-type-dropdown-panel");
    const button = document.getElementById("expert-type-dropdown-button");
    if (!panel) return;
    const open = !panel.classList.contains("open");
    if (open) {
      panel.classList.add("open");
      button?.setAttribute("aria-expanded", "true");
      renderExpertTypeDropdown();
      trackEvent("expert_type_dropdown_opened");
    } else {
      closeExpertTypeDropdown("toggle");
    }
  });
  document.getElementById("pricing-dropdown-button")?.addEventListener("click", (e) => {
    e.stopPropagation();
    const panel = document.getElementById("pricing-dropdown-panel");
    const button = document.getElementById("pricing-dropdown-button");
    if (!panel) return;
    const willOpen = !panel.classList.contains("open");
    panel.classList.toggle("open", willOpen);
    button?.setAttribute("aria-expanded", String(willOpen));
    trackEvent(willOpen ? "pricing_dropdown_opened" : "pricing_dropdown_closed");
  });
  document.getElementById("pricing-dropdown-content")?.addEventListener("click", (e) => {
    const option = e.target.closest(".pricing-dropdown-option[data-tab]");
    if (!option) return;
    e.stopPropagation();
    if (option.classList.contains("disabled")) return;
    handleTabSwitch(option.dataset.tab);
    closeControlDropdown("pricing", "select");
  });
  document.getElementById("ranking-mode-dropdown-button")?.addEventListener("click", (e) => {
    e.stopPropagation();
    const panel = document.getElementById("ranking-mode-dropdown-panel");
    const button = document.getElementById("ranking-mode-dropdown-button");
    if (!panel) return;
    const willOpen = !panel.classList.contains("open");
    panel.classList.toggle("open", willOpen);
    button?.setAttribute("aria-expanded", String(willOpen));
    trackEvent(willOpen ? "ranking_mode_dropdown_opened" : "ranking_mode_dropdown_closed", {
      reason: willOpen ? "toggle" : "toggle",
    });
  });
  document.getElementById("ranking-mode-dropdown-content")?.addEventListener("click", (e) => {
    const option = e.target.closest(".ranking-mode-dropdown-option[data-ranking-mode]");
    if (!option) return;
    e.stopPropagation();
    handleRankingModeSwitch(option.dataset.rankingMode);
    closeControlDropdown("ranking-mode", "select", "ranking_mode");
  });

  // Tab buttons
  document.querySelectorAll("#ranking-tabs .tab-button").forEach((btn) => {
    btn.addEventListener("click", () => handleTabSwitch(btn.dataset.tab));
  });
  document.querySelectorAll("#ranking-mode-tabs [data-ranking-mode]").forEach((btn) => {
    btn.addEventListener("click", () => handleRankingModeSwitch(btn.dataset.rankingMode));
  });

  // Sort headers
  bindSortHeaderListeners();

  // Search input
  const searchInput = document.getElementById("rankings-search");
  if (searchInput) {
    log("[INIT] Search input found, attaching listener");
    searchInput.addEventListener("input", (e) => {
      handleSearchInput(e.target.value);
      // Show checkboxes when search has value
      const checkboxes = document.getElementById("search-field-checkboxes");
      if (checkboxes) {
        if (state.type === "creator") {
          checkboxes.classList.remove("show");
        } else if (searchInput.value) checkboxes.classList.add("show");
        else if (document.activeElement !== searchInput) checkboxes.classList.remove("show");
      }
    });

    // Show/hide search field checkboxes on focus/blur
    searchInput.addEventListener("focus", () => {
      const checkboxes = document.getElementById("search-field-checkboxes");
      if (checkboxes && state.type !== "creator") checkboxes.classList.add("show");
      setSearchCommandHintVisible(true);
    });
    const searchCommandHint = document.getElementById("search-command-hint");
    searchCommandHint?.addEventListener("mousedown", (event) => {
      if (event.target.closest("[data-search-command-append]")) event.preventDefault();
    });
    searchInput.addEventListener("blur", () => {
      // Delay to allow checkbox clicks
      setTimeout(() => {
        const checkboxes = document.getElementById("search-field-checkboxes");
        if (checkboxes && !searchInput.value) checkboxes.classList.remove("show");
        if (document.activeElement !== searchInput) setSearchCommandHintVisible(false);
      }, 200);
    });
  } else {
    console.warn("[INIT] Search input NOT found!");
  }
  const searchClearButton = document.getElementById("rankings-search-clear");
  if (searchClearButton && searchInput && !searchClearButton.dataset.searchClearBound) {
    searchClearButton.dataset.searchClearBound = "true";
    searchClearButton.addEventListener("click", () => {
      if (!searchInput.value) return;
      searchInput.value = "";
      searchInput.dispatchEvent(new Event("input", { bubbles: true }));
      searchInput.focus();
    });
  }

  const clusterizeScroll = document.getElementById("clusterize-scroll");
  if (clusterizeScroll && !clusterizeScroll.dataset.contextHydrateBound) {
    clusterizeScroll.dataset.contextHydrateBound = "true";
    let hydrationFrame = 0;
    let progressiveHydrationFrame = 0;
    let scrollTrackedForContext = "";
    clusterizeScroll.addEventListener(
      "scroll",
      () => {
        // Clusterize's own scroll listener runs in the bubble phase. Hydrate the
        // target window first so it never receives an empty/placeholder cluster.
        hydrateProgressiveScrollWindow();
        // Wheel scrolling can deliver this event before the browser commits the
        // new scrollTop. Re-run after layout so Clusterize paints the window for
        // the actual position, not the previous one.
        if (progressiveRenderState.active && !progressiveHydrationFrame) {
          progressiveHydrationFrame = window.requestAnimationFrame(() => {
            progressiveHydrationFrame = 0;
            hydrateProgressiveScrollWindow({ force: true });
          });
        }
        flushDeferredBootstrapClusterizeUpdate();
        // Clusterize may replace the tbody during its own scroll handler. While
        // progressive hydration is active, restore the full-list spacer after
        // that replacement so the scrollbar and empty lower area never collapse
        // to only the currently mounted block.
        if (progressiveRenderState.active || clusterizeScroll.dataset.progressiveRows === "1") {
          setProgressiveFullHeightSpacer();
        }
        scheduleVisibleThumbnailHydration(300);
        const contextKey = getCurrentContext();
        if (scrollTrackedForContext !== contextKey) {
          scrollTrackedForContext = contextKey;
          trackEvent("table_scroll_started", { context: contextKey });
        }
        if (!shouldUsePostHydrateContextCells()) return;
        if (hydrationFrame) return;
        hydrationFrame = window.requestAnimationFrame(() => {
          hydrationFrame = 0;
          hydrateVisibleContextCells();
        });
      },
      { capture: true },
    );
  }

  // Search field checkboxes
  const fieldCheckboxes = {
    "search-field-rank": "searchRank",
    "search-field-change": "searchChange",
    "search-field-price": "searchPrice",
    "search-field-name": "searchName",
    "search-field-author": "searchAuthor",
  };
  for (const [id, field] of Object.entries(fieldCheckboxes)) {
    const checkbox = document.getElementById(id);
    if (checkbox) {
      checkbox.addEventListener("change", (e) => {
        state.searchFields[field] = e.target.checked;
        log(`[SEARCH] Field ${field} = ${e.target.checked}`);
        trackEvent("search_field_toggled", { field, checked: Boolean(e.target.checked) });
        if (state.search) {
          refresh();
          refreshGatedMetricsForVisibleList();
        }
      });
    }
  }

  // Category dropdown
  document.getElementById("category-dropdown-button")?.addEventListener("click", (e) => {
    e.stopPropagation();
    const panel = document.getElementById("category-dropdown-panel");
    if (panel) {
      if (!document.getElementById("category-tree")?.children.length) renderCategoryTree();
      panel.classList.toggle("open");
      if (panel.classList.contains("open")) {
        expandCategoryAncestorPath(state.category);
        // Restore the old category-context affordance: opening the dropdown
        // brings the active category into view after the tree is painted.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            expandCategoryAncestorPath(state.category);
            document.getElementById("category-search-input")?.focus();
          });
        });
        window.setTimeout(() => expandCategoryAncestorPath(state.category), 250);
        bindCategorySearchControls();
      } else {
        resetCategorySearch({ track: true });
      }
      trackEvent(panel.classList.contains("open") ? "category_dropdown_opened" : "category_dropdown_closed");
    }
  });

  // Close dropdown on outside click
  bindDocumentListener("dropdown-outside-click", "click", (e) => {
    const panel = document.getElementById("category-dropdown-panel");
    const button = document.getElementById("category-dropdown-button");
    if (panel && !panel.contains(e.target) && button && !button.contains(e.target)) {
      const wasOpen = panel.classList.contains("open");
      if (wasOpen) closeCategoryDropdown();
      if (wasOpen) trackEvent("category_dropdown_closed", { reason: "outside_click" });
    }
    const typePanel = document.getElementById("data-type-dropdown-panel");
    const typeButton = document.getElementById("data-type-dropdown-button");
    if (typePanel && !typePanel.contains(e.target) && typeButton && !typeButton.contains(e.target)) {
      closeDataTypeDropdown("outside_click");
    }
    const pricingPanel = document.getElementById("pricing-dropdown-panel");
    const pricingButton = document.getElementById("pricing-dropdown-button");
    if (pricingPanel && !pricingPanel.contains(e.target) && pricingButton && !pricingButton.contains(e.target)) {
      closeControlDropdown("pricing", "outside_click");
    }
    const expertTypePanel = document.getElementById("expert-type-dropdown-panel");
    const expertTypeButton = document.getElementById("expert-type-dropdown-button");
    if (
      expertTypePanel &&
      !expertTypePanel.contains(e.target) &&
      expertTypeButton &&
      !expertTypeButton.contains(e.target)
    ) {
      closeExpertTypeDropdown("outside_click");
    }
    const rankingModePanel = document.getElementById("ranking-mode-dropdown-panel");
    const rankingModeButton = document.getElementById("ranking-mode-dropdown-button");
    if (
      rankingModePanel &&
      !rankingModePanel.contains(e.target) &&
      rankingModeButton &&
      !rankingModeButton.contains(e.target)
    ) {
      closeControlDropdown("ranking-mode", "outside_click", "ranking_mode");
    }
  });

  bindDocumentListener("context-jump-and-search-click", "click", (event) => {
    const expertTypeBadge = event.target.closest(".expert-type-context-jump-btn[data-expert-type]");
    if (expertTypeBadge) {
      event.preventDefault();
      event.stopPropagation();
      handleExpertTypeSelect(expertTypeBadge.dataset.expertType, expertTypeBadge.dataset.itemId, "row_badge");
      return;
    }
    const appendCommandTarget = event.target.closest("[data-search-command-append]");
    if (appendCommandTarget) {
      event.preventDefault();
      appendSearchCommand(appendCommandTarget.dataset.searchCommand);
      return;
    }
    const highlightTarget = event.target.closest("[data-rank-highlight-count]");
    if (highlightTarget) {
      event.preventDefault();
      const searchCommand = highlightTarget.dataset.searchCommand;
      if (searchCommand) {
        applySearchCommand(searchCommand, {
          afterRefresh: () =>
            applyRankHighlightMetric(highlightTarget.dataset.rankHighlightCount, { refreshView: false }),
        });
      } else {
        applyRankHighlightMetric(highlightTarget.dataset.rankHighlightCount);
      }
      return;
    }
    const commandTarget = event.target.closest("[data-search-command]");
    if (!commandTarget) return;
    event.preventDefault();
    applySearchCommand(commandTarget.dataset.searchCommand, {
      afterRefresh: shouldHighlightAllVisibleChangeCellsForCommandTarget(commandTarget)
        ? () => highlightVisibleChangeCells()
        : null,
    });
  });

  bindDocumentListener("search-command-keydown", "keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const appendCommandTarget = event.target.closest("[data-search-command-append]");
    if (appendCommandTarget) {
      event.preventDefault();
      appendSearchCommand(appendCommandTarget.dataset.searchCommand);
      return;
    }
    const highlightTarget = event.target.closest("[data-rank-highlight-count]");
    if (highlightTarget) {
      event.preventDefault();
      const searchCommand = highlightTarget.dataset.searchCommand;
      if (searchCommand) {
        applySearchCommand(searchCommand, {
          afterRefresh: () =>
            applyRankHighlightMetric(highlightTarget.dataset.rankHighlightCount, { refreshView: false }),
        });
      } else {
        applyRankHighlightMetric(highlightTarget.dataset.rankHighlightCount);
      }
      return;
    }
    const commandTarget = event.target.closest("[data-search-command]");
    if (!commandTarget) return;
    event.preventDefault();
    applySearchCommand(commandTarget.dataset.searchCommand, {
      afterRefresh: shouldHighlightAllVisibleChangeCellsForCommandTarget(commandTarget)
        ? () => highlightVisibleChangeCells()
        : null,
    });
  });

  const isMobileTooltipMode = () => window.matchMedia("(max-width: 900px)").matches;
  const closeMobileTableTooltips = (exceptEl = null) => {
    if (!isMobileTooltipMode()) return;
    document
      .querySelectorAll(
        ".medals-cell .with-tooltip.tooltip-open, .categories-cell .with-tooltip.tooltip-open, .trophy-with-tooltip.tooltip-open",
      )
      .forEach((el) => {
        if (exceptEl && el === exceptEl) return;
        el.classList.remove("tooltip-open");
        el.closest(".categories-cell, .medals-cell")?.classList.remove("tooltip-open-cell");
      });
  };

  const clusterizeContent = document.getElementById("clusterize-content");
  clusterizeContent?.addEventListener("mouseover", hydrateLazyCategoryTooltipFromEvent);
  clusterizeContent?.addEventListener("focusin", hydrateLazyCategoryTooltipFromEvent);
  clusterizeContent?.addEventListener("click", hydrateLazyCategoryTooltipFromEvent, true);
  clusterizeContent?.addEventListener("mouseout", dehydrateLazyCategoryTooltipFromEvent);
  clusterizeContent?.addEventListener("focusout", dehydrateLazyCategoryTooltipFromEvent);
  clusterizeContent?.addEventListener("click", (e) => {
    const targetEl = e.target instanceof Element ? e.target : null;
    if (!targetEl) return;
    if (targetEl.closest("a, button, .medal-jump-btn, .category-jump-btn, .trophy-jump-btn")) return;
    const row = targetEl.closest("[data-id]");
    if (!row) return;
    trackEvent("table_row_clicked", {
      rowItemId: row.getAttribute("data-id"),
      column: targetEl.closest("td")?.className || null,
    });
  });

  if (!document.body.dataset.ranksLinkTrackingBound) {
    document.body.dataset.ranksLinkTrackingBound = "true";
    bindDocumentListener("link-click", "click", (e) => {
      const link = e.target instanceof Element ? e.target.closest("a[href]") : null;
      if (!link || link.classList.contains("js-feedback-trigger")) return;
      trackEvent("link_clicked", getSafeLinkMetadata(link));
    });
  }

  bindDocumentListener(
    "mobile-tooltip-click",
    "click",
    (e) => {
      if (!isMobileTooltipMode()) return;
      const targetEl = e.target instanceof Element ? e.target : null;
      if (!targetEl) return;

      const jumpBtn = targetEl.closest(".medal-jump-btn");
      const categoryJumpBtn = targetEl.closest(".category-jump-btn");
      const trophyJumpBtn = targetEl.closest(".trophy-jump-btn");
      if (jumpBtn || categoryJumpBtn || trophyJumpBtn) return;

      const maybeTrigger = targetEl.closest(".with-tooltip");
      const trigger =
        maybeTrigger?.closest(".medals-cell, .categories-cell") ||
        maybeTrigger?.classList.contains("trophy-with-tooltip")
          ? maybeTrigger
          : null;
      if (trigger) {
        const willOpen = !trigger.classList.contains("tooltip-open");
        closeMobileTableTooltips();
        if (willOpen) {
          trigger.classList.add("tooltip-open");
          trigger.closest(".categories-cell, .medals-cell")?.classList.add("tooltip-open-cell");
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      closeMobileTableTooltips();
    },
    true,
  );

  // Medal tooltip jump buttons
  bindDocumentListener("medal-jump-click", "click", (e) => {
    const btn = e.target.closest(".medal-jump-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const categoryId = btn.getAttribute("data-category-id");
    const rank = Number(btn.getAttribute("data-rank") || "0");
    const market = btn.getAttribute("data-market") || "all";
    const itemType = btn.getAttribute("data-item-type");
    const sourceRow = btn.closest("[data-id]");
    const sourceItemId = sourceRow?.getAttribute("data-id") || null;
    handleMedalJump(categoryId, rank, market, itemType, sourceItemId);
    closeMobileTableTooltips();
  });

  // Category tooltip jump buttons
  bindDocumentListener("category-jump-click", "click", (e) => {
    const btn = e.target.closest(".category-jump-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const categoryId = btn.getAttribute("data-category-id");
    const itemType = btn.getAttribute("data-item-type");
    const itemId = btn.getAttribute("data-item-id");
    handleCategoryJump(categoryId, itemType, itemId);
    closeMobileTableTooltips();
  });

  // Crown tooltip jump buttons
  bindDocumentListener("trophy-jump-click", "click", (e) => {
    const btn = e.target.closest(".trophy-jump-btn");
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const trophyMarket = btn.getAttribute("data-trophy-market");
    const itemType = btn.getAttribute("data-item-type");
    const sourceRow = btn.closest("[data-id]");
    const sourceItemId = sourceRow?.getAttribute("data-id") || null;
    handleTrophyJump(trophyMarket, itemType, sourceItemId);
    closeMobileTableTooltips();
  });

  document.body.classList.add("category-placement-ready");
  window.addEventListener("popstate", () => {
    isApplyingUrlState = true;
    try {
      const resolved = resolveUrlStateFromLocation(
        {
          pathname: window.location.pathname,
          searchParams: new URLSearchParams(window.location.search),
        },
        getUrlResolutionOptions(state),
      );
      state.type = resolved.type;
      state.tab = resolved.tab;
      state.rankingMode = resolved.rankingMode;
      state.category = resolved.category;
      state.search = resolved.search;
      state.sortColumn = resolved.sortColumn;
      state.sortDirection = resolved.sortDirection;
      applyCurrentStateToUi({ closeDropdown: true, scrollTop: true });
      syncUrlWithState();
    } finally {
      isApplyingUrlState = false;
    }
  });

  // Pictures are always-on; toggle UI is hidden.
}

import { createClient } from "@supabase/supabase-js";

if (typeof window !== "undefined") {
  window.Supabase = { createClient };
}

// ============================================
// FEEDBACK
// ============================================

/**
 * Initialize feedback modal and direct-to-Supabase submit logic.
 * No-op when feedback elements are not present.
 */
function initFeedback() {
  const triggers = Array.from(document.querySelectorAll(".js-feedback-trigger"));
  const overlay = document.getElementById("feedback-modal-overlay");
  const closeBtn = document.getElementById("feedback-close");
  const form = document.getElementById("feedback-form");
  const toast = document.getElementById("feedback-toast");

  if (triggers.length === 0 || !overlay || !closeBtn || !form || !toast) return;
  form.setAttribute("data-feedback-ready", "1");

  const fallbackConfig = {
    endpoint: "https://alaluqoxjttmnjkwagqt.supabase.co/functions/v1/feedback-submit",
    publishableKey:
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFsYWx1cW94anR0bW5qa3dhZ3F0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzU3NDEwNzYsImV4cCI6MjA5MTMxNzA3Nn0.7yoDEQJta9whtHSnIks8oC88e5bn8QNrNeqDiWYETTE",
  };

  const getFeedbackConfig = () => {
    // Optional runtime override from ranks.html without changing bundled JS.
    const runtimeConfig = window.FR_RANKS_FEEDBACK_CONFIG || {};
    const endpoint = (runtimeConfig.endpoint || fallbackConfig.endpoint || "").trim();
    const publishableKey = (runtimeConfig.publishableKey || fallbackConfig.publishableKey || "").trim();
    if (!endpoint || !publishableKey) return null;
    return { endpoint, publishableKey };
  };

  const closeModal = () => {
    overlay.classList.remove("open");
    document.body.style.overflow = "";
    form.reset();
  };

  for (const trigger of triggers) {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      trackEvent("feedback_opened", { triggerId: trigger.id || null });
      overlay.classList.add("open");
      document.body.style.overflow = "hidden";
    });
  }

  closeBtn.addEventListener("click", () => {
    trackEvent("feedback_closed", { reason: "close_button" });
    closeModal();
  });
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) {
      trackEvent("feedback_closed", { reason: "overlay" });
      closeModal();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && overlay.classList.contains("open")) {
      trackEvent("feedback_closed", { reason: "escape" });
      closeModal();
    }
  });

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const hp = document.getElementById("feedback-hp");
    if (hp?.value) return;

    const submitBtn = document.getElementById("feedback-submit");
    if (!submitBtn) return;
    const originalText = submitBtn.textContent;

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";

      const message = (document.getElementById("feedback-message")?.value || "").trim();
      const email = (document.getElementById("feedback-email")?.value || "").trim();
      if (!message) throw new Error("Message is required");
      if (message.length > 2000) throw new Error("Message exceeds max length");

      const payload = {
        message,
        email,
        page: "ranks",
        type: "suggestion",
      };

      const config = getFeedbackConfig();
      if (!config) throw new Error("Feedback endpoint config is missing");
      trackEvent("feedback_submit_attempted", {
        messageLength: message.length,
        hasEmail: Boolean(email),
      });

      const response = await fetch(config.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: config.publishableKey,
          Authorization: `Bearer ${config.publishableKey}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`Feedback submit failed (${response.status}): ${responseText}`);
      }

      closeModal();
      toast.classList.add("show");
      trackEvent("feedback_submit_succeeded", {
        messageLength: message.length,
        hasEmail: Boolean(email),
      });
      setTimeout(() => toast.classList.remove("show"), 4000);
    } catch (error) {
      console.error("[FEEDBACK] Error:", error);
      trackEvent("feedback_submit_failed", {
        reason: error instanceof Error ? error.message.slice(0, 80) : "unknown",
      });
      alert("Failed to send feedback. Please try again later.");
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
    }
  });
}

// ============================================
// AUTH
// ============================================

const AUTH_WIDGET_HTML = `<div id="auth-widget" class="auth-widget">
  <button id="auth-login-btn" class="auth-login-btn">Login</button>
  <p id="auth-inline-note" class="auth-inline-note" style="display:none"></p>
  <div id="auth-avatar-wrapper" class="auth-avatar-wrapper" style="display:none">
    <button id="auth-avatar-btn" class="auth-avatar-btn" aria-label="Account menu">
      <img id="auth-avatar-image" class="auth-avatar-image" alt="" loading="eager" decoding="async" style="display:none" />
      <span id="auth-avatar-letter"></span>
    </button>
    <div id="auth-dropdown" class="auth-dropdown">
      <div id="auth-email" class="auth-email"></div>
      <div class="auth-dropdown-divider"></div>
      <button id="auth-settings-btn" class="auth-dropdown-item">My Account</button>
      <button id="auth-logout-btn" class="auth-dropdown-item">Log Out</button>
    </div>
  </div>
</div>`;

function isPublicLoginEnabled() {
  if (typeof window === "undefined") return false;
  return window.FR_RANKS_SHOW_LOGIN === true || window.FR_RANKS_SHOW_LOGIN === "true";
}

function applyPublicLoginVisibility(loginBtn) {
  if (!loginBtn) return;
  if (isPublicLoginEnabled()) {
    loginBtn.style.display = "";
    loginBtn.removeAttribute("aria-hidden");
    loginBtn.removeAttribute("tabindex");
    return;
  }
  loginBtn.style.display = "none";
  loginBtn.setAttribute("aria-hidden", "true");
  loginBtn.setAttribute("tabindex", "-1");
}

const AUTH_MODAL_HTML = `<div id="auth-modal-overlay" class="auth-overlay">
  <div class="auth-modal">
    <div class="auth-modal-header">
      <span id="auth-modal-title" class="auth-modal-title">Login</span>
      <button id="auth-close" class="auth-close-btn" aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
    <div id="auth-modal-signin-view" class="auth-modal-view">
      <button id="auth-google-btn" class="auth-google-btn">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>
    </div>
    <div id="auth-modal-settings-view" class="auth-modal-view" style="display:none">
      <div class="auth-settings-card">
        <div class="auth-settings-row auth-settings-row-avatar">
          <span class="auth-settings-label">Avatar</span>
          <div class="auth-settings-avatar-slot">
            <img id="auth-settings-avatar-image" class="auth-settings-avatar-image" alt="" loading="eager" decoding="async" style="display:none" />
            <span id="auth-settings-avatar-letter" class="auth-settings-avatar-letter"></span>
          </div>
        </div>
        <div class="auth-settings-row">
          <span class="auth-settings-label">Email</span>
          <span id="auth-settings-email" class="auth-settings-value"></span>
        </div>
        <div class="auth-settings-row">
          <span class="auth-settings-label">Sign-in</span>
          <span id="auth-settings-provider" class="auth-settings-value">Google</span>
        </div>
        <div class="auth-settings-row">
          <span class="auth-settings-label">Name</span>
          <span id="auth-settings-name" class="auth-settings-value"></span>
        </div>
        <div class="auth-settings-row">
          <span class="auth-settings-label">Created</span>
          <span id="auth-settings-created" class="auth-settings-value"></span>
        </div>
      </div>
      <div id="auth-settings-delete-default" class="auth-settings-delete-state">
        <div class="auth-settings-links">
          <a id="auth-settings-privacy-link" class="auth-settings-link" href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <a id="auth-settings-terms-link" class="auth-settings-link" href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a>
        </div>
        <div class="auth-settings-danger">
          <button id="auth-delete-account-btn" class="auth-delete-account-btn">Delete Account</button>
        </div>
      </div>
      <div id="auth-settings-delete-confirm" class="auth-settings-delete-state" style="display:none">
        <p class="auth-settings-danger-copy">Are you sure?<br>This permanently deletes your Framer Ranks account and signs you out.</p>
        <div class="auth-settings-confirm-actions">
          <button id="auth-delete-cancel-btn" class="auth-settings-secondary-btn">Cancel</button>
          <button id="auth-delete-confirm-btn" class="auth-delete-account-btn">Confirm Delete</button>
        </div>
      </div>
    </div>
    <p id="auth-status" class="auth-status" role="status" aria-live="polite"></p>
  </div>
</div>`;

const GATED_BLOCK_HTML = `<div id="gated-block" class="gated-block" style="display:none">
  <div id="gated-locked" class="gated-locked" style="display:none"></div>
  <div id="gated-error" class="gated-error" style="display:none">
    <svg class="gated-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>
    <span id="gated-error-text" class="gated-error-text">Failed to load metrics</span>
    <button id="gated-retry-btn" class="gated-retry-btn">Retry</button>
  </div>
  <div id="gated-metrics" class="gated-metrics" style="display:none">
    <span class="gated-download-csv-tooltip with-tooltip" style="display:none" aria-hidden="true">
      <button id="gated-download-csv-btn" class="gated-download-csv-btn" type="button" disabled aria-describedby="gated-download-csv-tooltip-text">CSV</button>
      <div class="tooltip top"><div id="gated-download-csv-tooltip-text" class="tooltip-item">No rows to download</div></div>
    </span>
  </div>
</div>`;

function renderGatedChurnSubtitleSlot() {
  return `<span id="gated-churn-subtitle" class="gated-churn-subtitle" style="display:none"><span id="gated-churn-metrics" class="churn-metrics"><span class="churn-metrics__placeholder">--</span></span><span class="gated-churn-login-prompt" aria-hidden="true"><button id="gated-signin-btn" class="gated-churn-login-btn" type="button">Log In</button><span> to see rank churn metrics. It's free</span></span></span>`;
}

function getAuthConfig() {
  const runtimeConfig = window.FR_RANKS_AUTH_CONFIG || {};
  const configuredBaseUrl = (runtimeConfig.baseUrl || "").trim();
  return {
    baseUrl: configuredBaseUrl || inferCanonicalPreviewAuthBaseUrl(),
  };
}

function getGatedConfig() {
  const runtimeConfig = window.FR_RANKS_GATED_CONFIG || {};
  return {
    endpoint: (runtimeConfig.endpoint || "/api/gated-metrics").trim(),
  };
}

function getBaseUrl() {
  const config = getAuthConfig();
  return config.baseUrl || "";
}

function inferCanonicalPreviewAuthBaseUrl(hostname = window.location.hostname) {
  const normalized = String(hostname || "").toLowerCase();
  const w7BranchHost = "framer-ranks-static-w7-git-w7-preview-maxpalmiras-projects.vercel.app";
  if (normalized === w7BranchHost || !normalized.endsWith(".vercel.app")) return "";
  if (!normalized.startsWith("framer-ranks-static-w7-")) return "";
  if (normalized.includes("-git-")) return "";
  return `https://${w7BranchHost}`;
}

function buildAuthReturnUrl(baseUrl = getBaseUrl()) {
  const targetOrigin = baseUrl || window.location.origin;
  const target = new URL(
    `${window.location.pathname || "/ranks.html"}${window.location.search}${window.location.hash}`,
    targetOrigin,
  );
  target.searchParams.delete("auth");
  return target.toString();
}

function buildCanonicalAuthNavigationTarget(baseUrl = getBaseUrl(), currentOrigin = window.location.origin) {
  if (!baseUrl) return "";
  let base;
  let current;
  try {
    base = new URL(baseUrl);
    current = new URL(currentOrigin);
  } catch {
    return "";
  }
  if (base.origin === current.origin) return "";
  const target = new URL(buildAuthReturnUrl(base.origin));
  target.searchParams.set("auth", "signin");
  return target.toString();
}

const AUTH_BOOT_STATE_PENDING = "pending";
const AUTH_BOOT_STATE_AUTHENTICATED = "authenticated";
const AUTH_BOOT_STATE_LOGGED_OUT = "logged_out";
const AUTH_MODAL_MODE_SIGNIN = "signin";
const AUTH_MODAL_MODE_SETTINGS = "settings";
const AUTH_BOOT_RETRY_DELAY_MS = 500;
const AUTH_BOOTSTRAP_TIMEOUT_MS = 4000;
const AUTH_GATED_ERROR_RETRY_DELAY_MS = 500;
const GATED_BLOCK_SEARCH_ROW_MEDIA = "(max-width: 500px)";
const DELETE_REAUTH_PENDING_STORAGE_KEY = "ranks_delete_reauth_pending_v1";
const DELETE_REAUTH_PENDING_MAX_AGE_MS = 15 * 60 * 1000;
const DELETE_REAUTH_ERROR_CODE = "SESSION_NOT_FRESH";
const CHURN_METRICS_PLACEHOLDER_HTML = '<span class="churn-metrics__placeholder">--</span>';
const CHURN_METRICS_PENDING_HTML = '<span class="churn-metrics__pending">Updating...</span>';
const LOCKED_CHURN_METRIC_LABELS = ["Items Up", "Up Sum", "Top 1 Up", "Top 5 Up Sum", "Avg/Mid"];
const LOCKED_CHURN_METRICS_HTML = LOCKED_CHURN_METRIC_LABELS.map(
  (label) =>
    `<span class="churn-metrics__metric churn-metrics__metric--locked"><span class="churn-metrics__label">${label}</span><span class="churn-metrics__metric-values"><span class="churn-metrics__value churn-metrics__value--locked">--</span></span></span>`,
).join('<span class="churn-metrics__metric-sep" aria-hidden="true">·</span>');
const GATED_METRICS_PENDING_CLASS = "gated-metrics-pending";
const authClientState = {
  status: AUTH_BOOT_STATE_PENDING,
  session: null,
  inlineNoteTimer: null,
};

function getBaseRowsAuthState() {
  const authState =
    (typeof document !== "undefined" ? document.body?.dataset?.authState : null) ||
    authClientState.status ||
    AUTH_BOOT_STATE_PENDING;
  if (authState === AUTH_BOOT_STATE_AUTHENTICATED) return AUTH_BOOT_STATE_AUTHENTICATED;
  if (authState === AUTH_BOOT_STATE_LOGGED_OUT) return AUTH_BOOT_STATE_LOGGED_OUT;
  return AUTH_BOOT_STATE_PENDING;
}

function canSeeGatedMetrics() {
  return authClientState.status === AUTH_BOOT_STATE_AUTHENTICATED && Boolean(authClientState.session?.user);
}

function setAuthBootState(authState, isReady = authState !== AUTH_BOOT_STATE_PENDING) {
  if (typeof document === "undefined") return;
  authClientState.status = authState;
  document.body.dataset.authState = authState;
  document.body.dataset.authReady = isReady ? "true" : "false";
}

function getAuthBootState() {
  if (typeof document === "undefined") return AUTH_BOOT_STATE_PENDING;
  return document.body.dataset.authState || AUTH_BOOT_STATE_PENDING;
}

function renderPendingAuthState() {
  const loginBtn = document.getElementById("auth-login-btn");
  const avatarWrapper = document.getElementById("auth-avatar-wrapper");
  const dropdown = document.getElementById("auth-dropdown");
  const blockEl = document.getElementById("gated-block");
  const lockedEl = document.getElementById("gated-locked");
  const errorEl = document.getElementById("gated-error");
  const metricsEl = document.getElementById("gated-metrics");

  placeThemeSwitchInGlobalControls();
  if (loginBtn) loginBtn.style.display = "none";
  if (avatarWrapper) avatarWrapper.style.display = "none";
  if (blockEl) blockEl.style.display = "none";
  if (lockedEl) lockedEl.style.display = "none";
  if (errorEl) errorEl.style.display = "none";
  if (metricsEl) metricsEl.style.display = supportsGatedMetricsForType() ? "flex" : "none";
  updateCsvDownloadButtonState();
  dropdown?.classList.remove("open");
  clearAuthInlineNote();
}

function clearAuthInlineNote() {
  const noteEl = document.getElementById("auth-inline-note");
  if (authClientState.inlineNoteTimer) {
    window.clearTimeout(authClientState.inlineNoteTimer);
    authClientState.inlineNoteTimer = null;
  }
  if (noteEl) {
    noteEl.textContent = "";
    noteEl.style.display = "none";
  }
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function persistPendingDeleteReauth() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DELETE_REAUTH_PENDING_STORAGE_KEY, JSON.stringify({ requestedAt: Date.now() }));
  } catch {
    // ignore storage failures
  }
}

function clearPendingDeleteReauth() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DELETE_REAUTH_PENDING_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

function hasPendingDeleteReauth() {
  if (typeof window === "undefined") return false;
  try {
    const rawValue = window.localStorage.getItem(DELETE_REAUTH_PENDING_STORAGE_KEY);
    if (!rawValue) return false;
    const parsed = JSON.parse(rawValue);
    const requestedAt = Number(parsed?.requestedAt || 0);
    if (!Number.isFinite(requestedAt) || requestedAt <= 0) {
      clearPendingDeleteReauth();
      return false;
    }
    if (Date.now() - requestedAt > DELETE_REAUTH_PENDING_MAX_AGE_MS) {
      clearPendingDeleteReauth();
      return false;
    }
    return true;
  } catch {
    clearPendingDeleteReauth();
    return false;
  }
}

async function getBetterAuthSession() {
  if (authClientState.status === AUTH_BOOT_STATE_AUTHENTICATED && authClientState.session?.user) {
    return authClientState.session;
  }
  const sessionResult = await requestBetterAuthSession();
  authClientState.session = sessionResult.session;
  return sessionResult.session;
}

function getPageAuthParam() {
  try {
    const host = window.location.hostname;
    if (host !== "localhost" && host !== "127.0.0.1") return "";
    return new URL(window.location.href).searchParams.get("auth") || "";
  } catch {
    return "";
  }
}

async function requestAuthBootstrap() {
  const baseUrl = getBaseUrl();
  const url = new URL("/api/auth/bootstrap", baseUrl || window.location.origin);
  const pageAuth = getPageAuthParam();
  if (pageAuth) url.searchParams.set("auth", pageAuth);
  const contextInfo = getGatedContextInfo();
  const contextKey = supportsGatedMetricsForType(contextInfo.type) ? buildGatedContextKey(contextInfo) : null;
  if (supportsGatedMetricsForType(contextInfo.type)) {
    if (contextInfo.type) url.searchParams.set("type", contextInfo.type);
    if (contextInfo.tab) url.searchParams.set("tab", contextInfo.tab);
    if (contextInfo.category) url.searchParams.set("category", contextInfo.category);
    if (contextInfo.search) url.searchParams.set("search", contextInfo.search);
    for (const [field, enabled] of Object.entries(contextInfo.searchFields || {})) {
      if (!enabled) url.searchParams.set(field, "0");
    }
  } else {
    url.searchParams.set("metrics", "0");
  }

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  let timeoutId = null;

  try {
    const timeoutResult = {
      status: "error",
      session: null,
      metrics: null,
      metricsState: "locked",
      contextKey,
    };
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = window.setTimeout(() => {
        controller?.abort();
        resolve(timeoutResult);
      }, AUTH_BOOTSTRAP_TIMEOUT_MS);
    });
    const fetchPromise = fetch(url.toString(), {
      credentials: "include",
      signal: controller?.signal,
    }).catch(() => timeoutResult);
    const res = await Promise.race([fetchPromise, timeoutPromise]);
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (res === timeoutResult) return timeoutResult;
    if (!res.ok) {
      return {
        status: "error",
        session: null,
        metrics: null,
        metricsState: "locked",
        contextKey,
      };
    }

    const payload = await res.json();
    const status =
      payload?.authState === AUTH_BOOT_STATE_AUTHENTICATED ? AUTH_BOOT_STATE_AUTHENTICATED : AUTH_BOOT_STATE_LOGGED_OUT;
    return {
      status,
      session: payload?.user ? { user: payload.user } : null,
      metrics: payload?.metrics ?? null,
      metricsState:
        payload?.metricsState === "ready" ? "ready" : payload?.metricsState === "error" ? "error" : "locked",
      contextKey,
    };
  } catch {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    return {
      status: "error",
      session: null,
      metrics: null,
      metricsState: "locked",
      contextKey,
    };
  }
}

async function requestBetterAuthSession() {
  const baseUrl = getBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/auth/get-session`, {
      credentials: "include",
    });
    if (res.status === 401) {
      return {
        status: AUTH_BOOT_STATE_LOGGED_OUT,
        session: null,
      };
    }
    if (res.ok) {
      const session = await res.json();
      return {
        status: session?.user ? AUTH_BOOT_STATE_AUTHENTICATED : AUTH_BOOT_STATE_LOGGED_OUT,
        session: session?.user ? session : null,
      };
    }
    return {
      status: "error",
      session: null,
    };
  } catch {
    return {
      status: "error",
      session: null,
    };
  }
}

async function signInWithGoogle(options = {}) {
  const canonicalTarget = buildCanonicalAuthNavigationTarget();
  if (canonicalTarget) {
    window.location.href = canonicalTarget;
    return;
  }

  const request = buildGoogleSignInRequest(options);
  const res = await fetch(request.url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(request.body),
  });

  if (!res.ok) {
    throw new Error(`Google sign-in failed with status ${res.status}`);
  }

  const data = await res.json();
  if (data?.url) {
    await postClientAuthEvent("google_redirect_started", { statusCode: 200 });
    window.location.href = data.url;
    return;
  }

  throw new Error("Better Auth did not return a Google redirect URL");
}

async function signOut() {
  const baseUrl = getBaseUrl();
  try {
    await fetch(`${baseUrl}/api/auth/sign-out`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({}),
    });
  } catch {
    // ignore
  }
}

async function deleteAccount() {
  const baseUrl = getBaseUrl();
  const res = await fetch(`${baseUrl}/api/account/delete`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({}),
  });

  const payload = await res.json().catch(() => null);
  if (res.status === 403 && payload?.code === DELETE_REAUTH_ERROR_CODE) {
    return {
      success: false,
      reauthRequired: true,
      freshAgeSeconds: Number(payload?.freshAgeSeconds || 0),
    };
  }

  if (!res.ok) {
    throw new Error(`Delete account failed with status ${res.status}`);
  }

  return payload;
}

function getGatedEndpoint() {
  return getGatedConfig().endpoint;
}

function buildGoogleSignInRequest(options = {}) {
  const baseUrl = getBaseUrl();
  const callbackURL =
    typeof options.callbackURL === "string" && options.callbackURL.trim()
      ? options.callbackURL.trim()
      : buildAuthReturnUrl(baseUrl);
  return {
    url: `${baseUrl}/api/auth/sign-in/social`,
    body: {
      provider: "google",
      callbackURL,
      disableRedirect: true,
    },
  };
}

function getGatedContextInfo() {
  return {
    type: state.type,
    tab: state.tab,
    category: state.category,
    search: state.search,
    searchFields: normalizeGatedSearchFields(state.searchFields),
  };
}

export function supportsGatedMetricsForType(type = state.type) {
  const normalizedType = URL_TYPE_TO_STATE[type] || type;
  return normalizedType !== "creator";
}

function buildGatedCacheKey() {
  return buildGatedContextKey(getGatedContextInfo());
}

function getGatedMetricsDataFromEnvelope(envelope) {
  return {
    positiveSum: envelope.positiveSum,
    negativeSum: envelope.negativeSum,
    categoryChurn: envelope.categoryChurn,
    lastUpdated: envelope.lastUpdated,
  };
}

function readGatedMemoryCache(contextKey) {
  const entry = state.gated.cache.get(contextKey);
  if (!entry) return null;
  if (!isGatedCacheEnvelopeValid(entry, contextKey)) {
    state.gated.cache.delete(contextKey);
    return null;
  }
  const pageLastUpdated = state.api.lastUpdated;
  if (pageLastUpdated && entry.lastUpdated !== pageLastUpdated) return null;
  state.gated.cache.delete(contextKey);
  state.gated.cache.set(contextKey, entry);
  touchGatedSessionStorage(contextKey);
  return entry;
}

function getGatedSessionStorageEntries() {
  const entries = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const storageKey = sessionStorage.key(i);
    if (!storageKey?.startsWith("ranks_gated_")) continue;
    let cachedAt = 0;
    try {
      const parsed = JSON.parse(sessionStorage.getItem(storageKey) || "null");
      cachedAt = Number(parsed?.cachedAt || 0);
    } catch {
      cachedAt = 0;
    }
    entries.push({ storageKey, cachedAt, index: i });
  }
  return entries;
}

function enforceGatedMemoryCacheLimit() {
  while (state.gated.cache.size > GATED_CACHE_MAX_ENTRIES) {
    const oldestKey = state.gated.cache.keys().next().value;
    if (!oldestKey) break;
    state.gated.cache.delete(oldestKey);
  }
}

function enforceGatedSessionStorageLimit(protectedStorageKey = null) {
  try {
    const entries = getGatedSessionStorageEntries().sort((a, b) => a.cachedAt - b.cachedAt || a.index - b.index);
    while (entries.length > GATED_CACHE_MAX_ENTRIES) {
      const oldest = entries.shift();
      if (!oldest) break;
      if (oldest.storageKey === protectedStorageKey && entries.length > 0) {
        entries.push(oldest);
        continue;
      }
      sessionStorage.removeItem(oldest.storageKey);
    }
  } catch {
    // ignore storage failures
  }
}

function touchGatedSessionStorage(contextKey) {
  try {
    const storageKey = getGatedSessionStorageKey(contextKey);
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;
    const envelope = JSON.parse(raw);
    if (!isGatedCacheEnvelopeValid(envelope, contextKey)) return;
    sessionStorage.setItem(storageKey, JSON.stringify({ ...envelope, cachedAt: Date.now() }));
  } catch {
    // ignore storage failures
  }
}

function readGatedSessionStorage(contextKey) {
  try {
    const storageKey = getGatedSessionStorageKey(contextKey);
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const envelope = JSON.parse(raw);
    if (!isGatedCacheEnvelopeValid(envelope, contextKey)) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    const pageLastUpdated = state.api.lastUpdated;
    if (pageLastUpdated && envelope.lastUpdated !== pageLastUpdated) return null;
    sessionStorage.setItem(storageKey, JSON.stringify({ ...envelope, cachedAt: Date.now() }));
    return envelope;
  } catch {
    return null;
  }
}

function writeGatedMemoryCache(contextKey, envelope) {
  state.gated.cache.set(contextKey, envelope);
  enforceGatedMemoryCacheLimit();
}

function writeGatedSessionStorage(contextKey, envelope) {
  const storageKey = getGatedSessionStorageKey(contextKey);
  const cacheEnvelope = { ...envelope, cachedAt: Date.now() };
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(cacheEnvelope));
    enforceGatedSessionStorageLimit(storageKey);
  } catch {
    try {
      enforceGatedSessionStorageLimit(storageKey);
      sessionStorage.setItem(storageKey, JSON.stringify(cacheEnvelope));
      enforceGatedSessionStorageLimit(storageKey);
    } catch {
      // ignore storage failures
    }
  }
}

function seedGatedCache(envelope) {
  state.gated.lastUpdated = envelope.lastUpdated;
  writeGatedMemoryCache(envelope.contextKey, envelope);
  writeGatedSessionStorage(envelope.contextKey, envelope);
}

async function clearGatedCache() {
  state.gated.cache.clear();
  state.gated.lastUpdated = null;
  state.gated.currentMetrics = null;
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("ranks_gated_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key);
    });
  } catch {
    // ignore storage failures
  }
}

function resetGatedChurnMetricsUi() {
  setGatedMetricsPending(false);
  const churnEl = document.getElementById("gated-churn-metrics");
  const churnSubtitleEl = document.getElementById("gated-churn-subtitle");
  churnSubtitleEl?.classList.remove("gated-churn-subtitle--locked");
  if (churnEl) {
    churnEl.innerHTML = CHURN_METRICS_PLACEHOLDER_HTML;
    churnEl.removeAttribute("aria-label");
  }
  if (churnSubtitleEl) churnSubtitleEl.style.display = "none";
}

function calculateMedianRankGain(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
}

function calculateVisibleGatedMetrics(rows = [], contextKey = getCurrentContext()) {
  let positiveSum = 0;
  let negativeSum = 0;
  let totalRankedCount = 0;
  const positiveGains = [];

  for (const row of rows) {
    const item = getRowItem(row);
    const entry = item?.ranks?.[contextKey];
    if (!entry || typeof entry[0] !== "number") continue;
    totalRankedCount += 1;

    const change = entry[1];
    if (typeof change !== "number") continue;
    if (change > 0) {
      positiveSum += change;
      positiveGains.push(change);
    } else if (change < 0) {
      negativeSum += change;
    }
  }

  positiveGains.sort((a, b) => b - a);
  const movedUpCount = positiveGains.length;
  const totalRankGain = positiveGains.reduce((sum, gain) => sum + gain, 0);
  const topItemRankGain = positiveGains[0] ?? 0;
  const topFiveRankGain = positiveGains.slice(0, 5).reduce((sum, gain) => sum + gain, 0);

  return {
    positiveSum,
    negativeSum,
    categoryChurn: {
      movedUpCount,
      totalRankedCount,
      movedUpPercent: totalRankedCount > 0 ? (movedUpCount / totalRankedCount) * 100 : 0,
      medianRankGain: calculateMedianRankGain(positiveGains),
      avgRankGain: movedUpCount > 0 ? totalRankGain / movedUpCount : null,
      totalRankGain,
      topItemRankGain,
      topItemRankGainShare: totalRankGain > 0 ? (topItemRankGain / totalRankGain) * 100 : null,
      topFiveRankGain,
      topFiveRankGainShare: totalRankGain > 0 ? (topFiveRankGain / totalRankGain) * 100 : null,
    },
  };
}

function renderVisibleGatedMetrics() {
  if (!supportsGatedMetricsForType()) {
    clearGatedMetricsForUnsupportedContext();
    return { ok: true, reason: "unsupported-context", data: null };
  }
  if (!canSeeGatedMetrics()) {
    renderLockedGatedState();
    return { ok: false, reason: "unauthenticated", data: null };
  }
  const metrics = calculateVisibleGatedMetrics(state.filteredRows, getCurrentContext());
  renderMetricsGatedState(metrics);
  return { ok: true, reason: "local", data: metrics };
}

function hasRenderableGatedChurnMetrics(churnEl) {
  if (!churnEl) return false;
  if (churnEl.querySelector(".churn-metrics__metric")) return true;
  const text = churnEl.textContent.trim();
  return Boolean(text && text !== "--" && text !== "Updating...");
}

function renderPendingGatedChurnMetrics() {
  const churnSubtitleEl = document.getElementById("gated-churn-subtitle");
  let churnEl = document.getElementById("gated-churn-metrics");
  if (!churnSubtitleEl) return;
  if (!churnEl) {
    churnEl = document.createElement("span");
    churnEl.id = "gated-churn-metrics";
    churnEl.className = "churn-metrics";
    churnSubtitleEl.appendChild(churnEl);
  }
  churnSubtitleEl.style.display = "inline-flex";
  if (!hasRenderableGatedChurnMetrics(churnEl)) {
    churnEl.innerHTML = CHURN_METRICS_PENDING_HTML;
    churnEl.setAttribute("aria-label", "Metrics updating");
  }
}

function setGatedMetricsPending(isPending) {
  if (isPending) renderPendingGatedChurnMetrics();
  const targets = [document.getElementById("gated-churn-subtitle"), document.getElementById("gated-metrics")].filter(
    Boolean,
  );
  for (const target of targets) {
    target.classList.toggle(GATED_METRICS_PENDING_CLASS, Boolean(isPending));
    target.setAttribute("aria-busy", isPending ? "true" : "false");
  }
}

function clearGatedMetricsForUnsupportedContext() {
  state.gated.currentMetrics = null;
  resetGatedChurnMetricsUi();
  updateCsvDownloadButtonState();
}

function renderLockedGatedState() {
  const blockEl = document.getElementById("gated-block");
  const lockedEl = document.getElementById("gated-locked");
  const errorEl = document.getElementById("gated-error");
  const metricsEl = document.getElementById("gated-metrics");
  let churnEl = document.getElementById("gated-churn-metrics");
  const churnSubtitleEl = document.getElementById("gated-churn-subtitle");

  if (blockEl) blockEl.style.display = "";
  if (lockedEl) lockedEl.style.display = "none";
  if (errorEl) errorEl.style.display = "none";
  if (metricsEl) metricsEl.style.display = supportsGatedMetricsForType() ? "flex" : "none";
  if (churnSubtitleEl) {
    churnSubtitleEl.style.display = supportsGatedMetricsForType() ? "inline-flex" : "none";
    churnSubtitleEl.classList.toggle("gated-churn-subtitle--locked", supportsGatedMetricsForType());
  }
  if (!churnEl && churnSubtitleEl) {
    churnEl = document.createElement("span");
    churnEl.id = "gated-churn-metrics";
    churnEl.className = "churn-metrics";
    churnSubtitleEl.appendChild(churnEl);
  }
  if (churnEl && supportsGatedMetricsForType()) {
    churnEl.innerHTML = LOCKED_CHURN_METRICS_HTML;
    churnEl.setAttribute("aria-label", "Log in to see gated metrics");
  }
  state.gated.currentMetrics = null;
  updateCsvDownloadButtonState();
}

function renderErrorGatedState(message) {
  const blockEl = document.getElementById("gated-block");
  const lockedEl = document.getElementById("gated-locked");
  const errorEl = document.getElementById("gated-error");
  const metricsEl = document.getElementById("gated-metrics");
  const errorTextEl = document.getElementById("gated-error-text");

  if (blockEl) blockEl.style.display = "none";
  if (lockedEl) lockedEl.style.display = "none";
  if (errorEl) errorEl.style.display = "none";
  if (metricsEl) metricsEl.style.display = "none";
  if (errorTextEl) errorTextEl.textContent = message || "Failed to load metrics";
  state.gated.currentMetrics = null;
  resetGatedChurnMetricsUi();
  updateCsvDownloadButtonState();
}

async function retryGatedMetricsBootstrap() {
  const bootstrapResult = await requestAuthBootstrap();
  if (bootstrapResult.status === "error") {
    if (authClientState.session?.user) {
      renderAuthenticatedIdentity(authClientState.session.user);
      renderErrorGatedState("Failed to load metrics");
      setAuthBootState(AUTH_BOOT_STATE_AUTHENTICATED, true);
      return;
    }
    renderLoggedOutState();
    setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
    return;
  }
  if (bootstrapResult.status !== AUTH_BOOT_STATE_AUTHENTICATED || !bootstrapResult.session?.user) {
    renderLoggedOutState();
    setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
    return;
  }
  renderAuthenticatedIdentity(bootstrapResult.session.user);
  if (!supportsGatedMetricsForType()) {
    clearGatedMetricsForUnsupportedContext();
    return;
  }
  renderVisibleGatedMetrics();
}

function renderMetricsGatedState(metrics) {
  if (!supportsGatedMetricsForType()) {
    clearGatedMetricsForUnsupportedContext();
    return;
  }
  const blockEl = document.getElementById("gated-block");
  const lockedEl = document.getElementById("gated-locked");
  const errorEl = document.getElementById("gated-error");
  const metricsEl = document.getElementById("gated-metrics");
  let churnEl = document.getElementById("gated-churn-metrics");
  const churnSubtitleEl = document.getElementById("gated-churn-subtitle");
  const positiveEl = document.getElementById("gated-positive-value");
  const negativeEl = document.getElementById("gated-negative-value");
  const renderableMetrics = normalizeRenderableGatedMetrics(metrics);
  const categoryChurn = renderableMetrics?.categoryChurn;
  state.gated.currentMetrics = renderableMetrics;
  setGatedMetricsPending(false);
  churnSubtitleEl?.classList.remove("gated-churn-subtitle--locked");

  if (blockEl) blockEl.style.display = "";
  if (lockedEl) lockedEl.style.display = "none";
  if (errorEl) errorEl.style.display = "none";
  if (metricsEl) metricsEl.style.display = "flex";
  if (churnSubtitleEl) churnSubtitleEl.style.display = "inline-flex";
  if (!churnEl && churnSubtitleEl) {
    churnEl = document.createElement("span");
    churnEl.id = "gated-churn-metrics";
    churnEl.className = "churn-metrics";
    churnSubtitleEl.appendChild(churnEl);
  }
  if (churnEl) {
    churnEl.innerHTML = formatCategoryChurnMetricsHtml(categoryChurn);
    churnEl.setAttribute("aria-label", formatCategoryChurnMetrics(categoryChurn));
  }
  if (positiveEl) positiveEl.textContent = `+${Number(renderableMetrics?.positiveSum || 0).toLocaleString()}`;
  if (negativeEl) negativeEl.textContent = `-${Math.abs(Number(renderableMetrics?.negativeSum || 0)).toLocaleString()}`;
  updateCsvDownloadButtonState();
}

function renderLoggedOutState() {
  const loginBtn = document.getElementById("auth-login-btn");
  const avatarWrapper = document.getElementById("auth-avatar-wrapper");
  const avatarImage = document.getElementById("auth-avatar-image");
  const avatarLetter = document.getElementById("auth-avatar-letter");
  const settingsAvatarImage = document.getElementById("auth-settings-avatar-image");
  const settingsAvatarLetter = document.getElementById("auth-settings-avatar-letter");
  const dropdown = document.getElementById("auth-dropdown");
  const blockEl = document.getElementById("gated-block");
  const lockedEl = document.getElementById("gated-locked");
  const errorEl = document.getElementById("gated-error");
  const metricsEl = document.getElementById("gated-metrics");

  placeThemeSwitchInGlobalControls();
  applyPublicLoginVisibility(loginBtn);
  if (avatarWrapper) avatarWrapper.style.display = "none";
  if (avatarImage) {
    avatarImage.removeAttribute("src");
    avatarImage.style.display = "none";
  }
  if (avatarLetter) avatarLetter.style.display = "";
  if (settingsAvatarImage) {
    settingsAvatarImage.removeAttribute("src");
    settingsAvatarImage.style.display = "none";
  }
  if (settingsAvatarLetter) settingsAvatarLetter.style.display = "";
  if (blockEl) blockEl.style.display = "";
  if (lockedEl) lockedEl.style.display = "none";
  if (errorEl) errorEl.style.display = "none";
  if (metricsEl) metricsEl.style.display = "none";
  state.gated.currentMetrics = null;
  renderLockedGatedState();
  updateCsvDownloadButtonState();
  dropdown?.classList.remove("open");
}

function setAuthStatus(message = "", tone = "") {
  const statusEl = document.getElementById("auth-status");
  if (!statusEl) return;
  statusEl.textContent = message;
  if (tone) {
    statusEl.dataset.tone = tone;
  } else {
    delete statusEl.dataset.tone;
  }
}

function setDeleteConfirmationVisible(isVisible) {
  const defaultEl = document.getElementById("auth-settings-delete-default");
  const confirmEl = document.getElementById("auth-settings-delete-confirm");
  const dangerEl = defaultEl?.querySelector(".auth-settings-danger");
  if (dangerEl) dangerEl.style.display = isVisible ? "none" : "";
  if (confirmEl) confirmEl.style.display = isVisible ? "" : "none";
}

function setAuthModalMode(mode = AUTH_MODAL_MODE_SIGNIN) {
  const titleEl = document.getElementById("auth-modal-title");
  const signinView = document.getElementById("auth-modal-signin-view");
  const settingsView = document.getElementById("auth-modal-settings-view");
  const resolvedMode = mode === AUTH_MODAL_MODE_SETTINGS ? AUTH_MODAL_MODE_SETTINGS : AUTH_MODAL_MODE_SIGNIN;

  if (titleEl) titleEl.textContent = resolvedMode === AUTH_MODAL_MODE_SETTINGS ? "My Account" : "Login";
  if (signinView) signinView.style.display = resolvedMode === AUTH_MODAL_MODE_SIGNIN ? "" : "none";
  if (settingsView) settingsView.style.display = resolvedMode === AUTH_MODAL_MODE_SETTINGS ? "" : "none";
  if (resolvedMode !== AUTH_MODAL_MODE_SETTINGS) {
    setDeleteConfirmationVisible(false);
  }
}

function closeAuthModal() {
  const modalOverlay = document.getElementById("auth-modal-overlay");
  setAuthStatus();
  setAuthModalMode(AUTH_MODAL_MODE_SIGNIN);
  modalOverlay?.classList.remove("open");
}

function openSignInModal() {
  const modalOverlay = document.getElementById("auth-modal-overlay");
  setAuthStatus();
  setAuthModalMode(AUTH_MODAL_MODE_SIGNIN);
  modalOverlay?.classList.add("open");
}

function showAvatarLetterFallback(avatarImage, avatarLetter, email = "") {
  if (avatarImage) {
    avatarImage.onload = null;
    avatarImage.onerror = null;
    avatarImage.removeAttribute("src");
    avatarImage.style.display = "none";
  }
  if (avatarLetter) {
    avatarLetter.textContent = email ? email.charAt(0).toUpperCase() : "?";
    avatarLetter.style.display = "";
  }
}

function wireAvatarImage(avatarImage, avatarLetter, avatarUrl, email = "") {
  showAvatarLetterFallback(avatarImage, avatarLetter, email);
  if (!avatarImage || !avatarUrl) return;

  const handleLoad = () => {
    avatarImage.style.display = "block";
    if (avatarLetter) avatarLetter.style.display = "none";
  };
  const handleError = () => {
    showAvatarLetterFallback(avatarImage, avatarLetter, email);
  };
  avatarImage.onload = handleLoad;
  avatarImage.onerror = handleError;
  avatarImage.src = avatarUrl;
  if (avatarImage.complete) {
    if (avatarImage.naturalWidth > 0) {
      handleLoad();
    } else {
      handleError();
    }
  }
}

function getSessionAvatarUrl(user) {
  const candidates = [user?.image, user?.avatar, user?.picture];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      const value = candidate.trim();
      if (value.startsWith("data:")) {
        return value;
      }
      try {
        const parsed = new URL(value, window.location.origin);
        if (/^lh[3-6]\.googleusercontent\.com$/i.test(parsed.hostname)) {
          const proxied = new URL("/api/auth/avatar", window.location.origin);
          proxied.searchParams.set("url", parsed.toString());
          return proxied.toString();
        }
        return parsed.toString();
      } catch {
        return value;
      }
    }
  }
  return "";
}

function formatTimestampUTCForSettings(timestamp) {
  if (!timestamp) return "—";
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "—";
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
}

function populateSettingsView(user) {
  const email = typeof user?.email === "string" ? user.email : "";
  const name = typeof user?.name === "string" && user.name.trim() ? user.name.trim() : "Google account";
  const avatarUrl = getSessionAvatarUrl(user);
  const avatarImage = document.getElementById("auth-settings-avatar-image");
  const avatarLetter = document.getElementById("auth-settings-avatar-letter");
  const emailEl = document.getElementById("auth-settings-email");
  const nameEl = document.getElementById("auth-settings-name");
  const createdEl = document.getElementById("auth-settings-created");

  if (emailEl) emailEl.textContent = email;
  if (nameEl) nameEl.textContent = name;
  if (createdEl) createdEl.textContent = formatTimestampUTCForSettings(user?.createdAt);
  wireAvatarImage(avatarImage, avatarLetter, avatarUrl, email || name);
}

function renderAuthenticatedIdentity(user) {
  const email = typeof user?.email === "string" ? user.email : "";
  const avatarUrl = getSessionAvatarUrl(user);
  const loginBtn = document.getElementById("auth-login-btn");
  const avatarWrapper = document.getElementById("auth-avatar-wrapper");
  const avatarImage = document.getElementById("auth-avatar-image");
  const avatarLetter = document.getElementById("auth-avatar-letter");
  const avatarBtn = document.getElementById("auth-avatar-btn");
  const emailEl = document.getElementById("auth-email");

  if (loginBtn) loginBtn.style.display = "none";
  wireAvatarImage(avatarImage, avatarLetter, avatarUrl, email);
  if (emailEl) emailEl.textContent = email || "";

  if (!isPublicLoginEnabled()) {
    if (avatarWrapper) avatarWrapper.style.display = "none";
    document.getElementById("auth-dropdown")?.classList.remove("open");
    placeThemeSwitchInGlobalControls();
    return;
  }

  placeThemeSwitchInAuthDropdown();
  if (avatarWrapper) avatarWrapper.style.display = "block";
  if (avatarBtn) {
    avatarBtn.onclick = (e) => {
      e.stopPropagation();
      const dropdown = document.getElementById("auth-dropdown");
      if (dropdown) {
        dropdown.classList.toggle("open");
        trackEvent(dropdown.classList.contains("open") ? "auth_dropdown_opened" : "auth_dropdown_closed");
      }
    };
  }
}

function openSettingsModal() {
  const modalOverlay = document.getElementById("auth-modal-overlay");
  const sessionUser = authClientState.session?.user;
  if (!modalOverlay || !sessionUser) return;
  populateSettingsView(sessionUser);
  setDeleteConfirmationVisible(false);
  setAuthStatus();
  setAuthModalMode(AUTH_MODAL_MODE_SETTINGS);
  modalOverlay.classList.add("open");
}

async function forceLoggedOutState(options = {}) {
  const { doSignOut = false } = options;
  authClientState.session = null;
  await clearGatedCache();
  renderLoggedOutState();
  setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
  syncColumnFeatureFlags({ rerender: true });
  clearEngagementFromItems();
  engagementMemoryCache.clear();
  renderRowsForAuthState();
  if (doSignOut) {
    await signOut();
  }
}

async function fetchGatedMetrics(contextInfo = {}, options = {}) {
  // Current visible churn metrics are local-only via renderVisibleGatedMetrics().
  // Keep this fetch/cache path for future private metrics that need server-only data.
  const { applyUi = true, onUnauthorized = "logout", bypassCache = false } = options;
  const normalizedContextInfo = {
    ...contextInfo,
    searchFields: normalizeGatedSearchFields(contextInfo.searchFields),
  };
  if (!supportsGatedMetricsForType(normalizedContextInfo.type)) {
    if (applyUi) clearGatedMetricsForUnsupportedContext();
    return { ok: true, reason: "unsupported-context", data: null };
  }
  const contextKey = buildGatedContextKey(normalizedContextInfo);
  const shouldApplyUiForContext = () => applyUi && buildGatedCacheKey() === contextKey;
  const markPendingForContext = () => {
    if (shouldApplyUiForContext()) setGatedMetricsPending(true);
  };

  if (!bypassCache) {
    const memoryCached = readGatedMemoryCache(contextKey);
    if (memoryCached) {
      const cachedData = getGatedMetricsDataFromEnvelope(memoryCached);
      if (shouldApplyUiForContext()) renderMetricsGatedState(cachedData);
      return {
        ok: true,
        reason: "cache",
        data: cachedData,
      };
    }
    const sessionCached = readGatedSessionStorage(contextKey);
    if (sessionCached) {
      writeGatedMemoryCache(contextKey, sessionCached);
      const cachedData = getGatedMetricsDataFromEnvelope(sessionCached);
      if (shouldApplyUiForContext()) renderMetricsGatedState(cachedData);
      return {
        ok: true,
        reason: "cache",
        data: cachedData,
      };
    }
  }

  if (!bypassCache && state.gated.inFlightByContextKey.has(contextKey)) {
    markPendingForContext();
    await state.gated.inFlightByContextKey.get(contextKey);
    const memoryAfter = readGatedMemoryCache(contextKey);
    const cachedData = memoryAfter ? getGatedMetricsDataFromEnvelope(memoryAfter) : null;
    if (cachedData && shouldApplyUiForContext()) renderMetricsGatedState(cachedData);
    return memoryAfter
      ? {
          ok: true,
          reason: "inflight",
          data: cachedData,
        }
      : { ok: false, reason: "error", data: null };
  }

  const endpoint = getGatedEndpoint();
  const url = new URL(endpoint, window.location.origin);
  if (normalizedContextInfo.type) url.searchParams.set("type", normalizedContextInfo.type);
  if (normalizedContextInfo.tab) url.searchParams.set("tab", normalizedContextInfo.tab);
  if (normalizedContextInfo.category) url.searchParams.set("category", normalizedContextInfo.category);
  if (normalizedContextInfo.search) url.searchParams.set("search", normalizedContextInfo.search);
  for (const [field, enabled] of Object.entries(normalizedContextInfo.searchFields)) {
    if (!enabled) url.searchParams.set(field, "0");
  }

  const fetchPromise = (async () => {
    try {
      markPendingForContext();
      const res = await fetch(url.toString(), {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        if (applyUi) {
          if (onUnauthorized === "logout") {
            await forceLoggedOutState({ doSignOut: false });
          } else {
            renderLockedGatedState();
          }
        }
        return { ok: false, reason: "unauthorized", data: null };
      }
      if (!res.ok) {
        if (shouldApplyUiForContext()) renderErrorGatedState("Failed to load metrics");
        return { ok: false, reason: "error", data: null };
      }

      const data = await res.json();
      const envelope = buildGatedEnvelope(contextKey, data);
      if (envelope) {
        seedGatedCache(envelope);
        if (shouldApplyUiForContext()) renderMetricsGatedState(data);
        return { ok: true, reason: "success", data };
      }
      if (shouldApplyUiForContext()) renderErrorGatedState("Failed to load metrics");
      return { ok: false, reason: "error", data: null };
    } catch (_err) {
      if (shouldApplyUiForContext()) renderErrorGatedState("Failed to load metrics");
      return { ok: false, reason: "error", data: null };
    }
  })();

  state.gated.inFlightByContextKey.set(contextKey, fetchPromise);
  try {
    return await fetchPromise;
  } finally {
    state.gated.inFlightByContextKey.delete(contextKey);
  }
}

async function refreshGatedMetricsIfAuthenticated() {
  if (authClientState.status === AUTH_BOOT_STATE_LOGGED_OUT) {
    renderLoggedOutState();
    setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
    return;
  }

  if (authClientState.status !== AUTH_BOOT_STATE_AUTHENTICATED || !authClientState.session?.user) {
    const sessionResult = await requestBetterAuthSession();
    authClientState.session = sessionResult.session;
    if (sessionResult.status !== AUTH_BOOT_STATE_AUTHENTICATED || !sessionResult.session?.user) {
      renderLoggedOutState();
      setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
      return;
    }
  }

  renderAuthenticatedIdentity(authClientState.session.user);
  return renderVisibleGatedMetrics();
}

async function onSnapshotLastUpdatedChanged() {
  renderVisibleGatedMetrics();
}

async function updateAuthUI() {
  authClientState.session = null;
  setAuthBootState(AUTH_BOOT_STATE_PENDING, false);
  renderPendingAuthState();
  let bootstrapResult = await requestAuthBootstrap();
  if (bootstrapResult.status === "error") {
    await sleep(AUTH_BOOT_RETRY_DELAY_MS);
    bootstrapResult = await requestAuthBootstrap();
  }
  if (bootstrapResult.status === "error") {
    void postClientAuthEvent("auth_bootstrap_ui_error", { errorCode: "BOOTSTRAP_UNAVAILABLE" });
  }
  authClientState.session = bootstrapResult.session;

  if (bootstrapResult.status === AUTH_BOOT_STATE_LOGGED_OUT) {
    clearAuthInlineNote();
    renderLoggedOutState();
    setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
    syncColumnFeatureFlags({ rerender: true });
    renderRowsForAuthState();
    return { state: AUTH_BOOT_STATE_LOGGED_OUT, session: null };
  }

  if (bootstrapResult.status !== AUTH_BOOT_STATE_AUTHENTICATED || !bootstrapResult.session?.user) {
    renderLoggedOutState();
    clearAuthInlineNote();
    console.error("[AUTH] Session bootstrap failed; falling back to logged-out UI");
    setAuthBootState(AUTH_BOOT_STATE_LOGGED_OUT, true);
    syncColumnFeatureFlags({ rerender: true });
    renderRowsForAuthState();
    return { state: AUTH_BOOT_STATE_LOGGED_OUT, session: null };
  }

  clearAuthInlineNote();
  renderAuthenticatedIdentity(bootstrapResult.session.user);
  if (!supportsGatedMetricsForType()) {
    clearGatedMetricsForUnsupportedContext();
    setAuthBootState(AUTH_BOOT_STATE_AUTHENTICATED, true);
    updateCsvDownloadButtonState();
    syncColumnFeatureFlags({ rerender: true });
    return { state: AUTH_BOOT_STATE_AUTHENTICATED, session: bootstrapResult.session };
  }
  setAuthBootState(AUTH_BOOT_STATE_AUTHENTICATED, true);
  renderVisibleGatedMetrics();
  updateCsvDownloadButtonState();
  syncColumnFeatureFlags({ rerender: true });
  renderRowsForAuthState();
  scheduleEngagementLoad();
  return { state: AUTH_BOOT_STATE_AUTHENTICATED, session: bootstrapResult.session };
}

function hasAuthCallbackParams() {
  const searchParams = new URLSearchParams(window.location.search);
  return ["code", "state", "error", "error_description"].some((key) => searchParams.has(key));
}

function cleanupAuthCallbackUrl() {
  const cleanUrl = `${window.location.origin}${window.location.pathname}`;
  window.history.replaceState({}, "", cleanUrl);
}

async function handleAuthCallback() {
  const hasCallbackParams = hasAuthCallbackParams();
  setAuthStatus();
  const result = await updateAuthUI();
  if (hasCallbackParams && result.state !== AUTH_BOOT_STATE_PENDING) {
    cleanupAuthCallbackUrl();
  }
  if (hasCallbackParams && result.state === AUTH_BOOT_STATE_AUTHENTICATED && hasPendingDeleteReauth()) {
    clearPendingDeleteReauth();
    openSettingsModal();
    setDeleteConfirmationVisible(true);
    setAuthStatus("Confirm Delete to finish.", "pending");
  }
  return result;
}

async function initAuth() {
  const themeSwitch = document.getElementById("theme-switch");
  if (!themeSwitch) return;

  if (!document.getElementById("auth-widget")) {
    themeSwitch.insertAdjacentHTML("afterend", AUTH_WIDGET_HTML);
  }
  if (!document.getElementById("auth-modal-overlay")) {
    document.body.insertAdjacentHTML("beforeend", AUTH_MODAL_HTML);
  }
  if (!document.getElementById("gated-block")) {
    const titleSubtitle = document.getElementById("table-title-subtitle");
    const gatedSlot = document.getElementById("gated-block-slot");
    if (gatedSlot) {
      gatedSlot.insertAdjacentHTML("beforeend", GATED_BLOCK_HTML);
    } else if (titleSubtitle) {
      titleSubtitle.insertAdjacentHTML(
        "beforeend",
        `<span id="gated-block-slot" class="table-title-subtitle-gated-slot">${GATED_BLOCK_HTML}</span>`,
      );
    } else {
      document.body.insertAdjacentHTML("afterbegin", GATED_BLOCK_HTML);
    }
  }
  placeGatedBlockInTitleSubtitle();
  bindGatedBlockViewportPlacement();

  const loginBtn = document.getElementById("auth-login-btn");
  const modalOverlay = document.getElementById("auth-modal-overlay");
  const closeBtn = document.getElementById("auth-close");
  const googleBtn = document.getElementById("auth-google-btn");
  const settingsBtn = document.getElementById("auth-settings-btn");
  const logoutBtn = document.getElementById("auth-logout-btn");
  const deleteAccountBtn = document.getElementById("auth-delete-account-btn");
  const deleteCancelBtn = document.getElementById("auth-delete-cancel-btn");
  const deleteConfirmBtn = document.getElementById("auth-delete-confirm-btn");
  const dropdown = document.getElementById("auth-dropdown");

  if (!loginBtn || !modalOverlay || !closeBtn || !googleBtn) return;

  setAuthBootState(AUTH_BOOT_STATE_PENDING, false);
  renderPendingAuthState();
  setAuthStatus();

  if (!document.body.dataset.gatedSigninBound) {
    document.body.dataset.gatedSigninBound = "true";
    document.body.addEventListener("click", (event) => {
      const gatedSignInBtn = event.target?.closest?.(
        "#gated-signin-btn, .gated-churn-login-btn, .gated-churn-subtitle--locked, .gated-cell--logged-out",
      );
      if (!gatedSignInBtn) return;
      trackEvent("auth_modal_opened", { source: "gated_signin" });
      void postClientAuthEvent("auth_modal_opened");
      openSignInModal();
    });
  }

  const gatedRetryBtn = document.getElementById("gated-retry-btn");
  if (gatedRetryBtn && !gatedRetryBtn.dataset.authBound) {
    gatedRetryBtn.dataset.authBound = "true";
    gatedRetryBtn.addEventListener("click", async () => {
      trackEvent("gated_metrics_retry_clicked");
      if (gatedRetryBtn) gatedRetryBtn.disabled = true;
      await sleep(AUTH_GATED_ERROR_RETRY_DELAY_MS);
      await retryGatedMetricsBootstrap();
      if (gatedRetryBtn) gatedRetryBtn.disabled = false;
    });
  }

  bindCsvDownloadButton();

  if (!loginBtn.dataset.authBound) {
    loginBtn.dataset.authBound = "true";
    loginBtn.addEventListener("click", () => {
      trackEvent("auth_modal_opened", { source: "login_button" });
      void postClientAuthEvent("auth_modal_opened");
      openSignInModal();
    });
  }

  if (!closeBtn.dataset.authBound) {
    closeBtn.dataset.authBound = "true";
    closeBtn.addEventListener("click", () => {
      trackEvent("auth_modal_closed", { reason: "close_button" });
      closeAuthModal();
    });
  }

  if (!modalOverlay.dataset.authBound) {
    modalOverlay.dataset.authBound = "true";
    modalOverlay.addEventListener("click", (e) => {
      if (e.target === modalOverlay) {
        trackEvent("auth_modal_closed", { reason: "overlay" });
        closeAuthModal();
      }
    });
  }

  if (!googleBtn.dataset.authBound) {
    googleBtn.dataset.authBound = "true";
    googleBtn.addEventListener("click", async () => {
      trackEvent("auth_google_clicked");
      void postClientAuthEvent("google_signin_clicked");
      googleBtn.disabled = true;
      setAuthStatus("Redirecting to Google...", "pending");
      try {
        await signInWithGoogle();
      } catch (error) {
        console.error("[AUTH] Google sign-in failed", error);
        await postClientAuthEvent("google_redirect_failed", {
          errorCode: error instanceof Error ? error.message : "GOOGLE_SIGNIN_FAILED",
        });
        setAuthStatus("Sign-in failed. Please try again.", "error");
        googleBtn.disabled = false;
      }
    });
  }

  if (new URLSearchParams(window.location.search).get("auth") === "signin") {
    openSignInModal();
  }

  if (settingsBtn && !settingsBtn.dataset.authBound) {
    settingsBtn.dataset.authBound = "true";
    settingsBtn.addEventListener("click", () => {
      trackEvent("auth_settings_opened");
      dropdown?.classList.remove("open");
      openSettingsModal();
    });
  }

  if (logoutBtn && !logoutBtn.dataset.authBound) {
    logoutBtn.dataset.authBound = "true";
    logoutBtn.addEventListener("click", async () => {
      trackEvent("auth_logout_clicked");
      await forceLoggedOutState({ doSignOut: true });
    });
  }

  if (deleteAccountBtn && !deleteAccountBtn.dataset.authBound) {
    deleteAccountBtn.dataset.authBound = "true";
    deleteAccountBtn.addEventListener("click", () => {
      trackEvent("auth_delete_account_started");
      setDeleteConfirmationVisible(true);
      setAuthStatus();
    });
  }

  if (deleteCancelBtn && !deleteCancelBtn.dataset.authBound) {
    deleteCancelBtn.dataset.authBound = "true";
    deleteCancelBtn.addEventListener("click", () => {
      trackEvent("auth_delete_account_cancelled");
      clearPendingDeleteReauth();
      setDeleteConfirmationVisible(false);
      setAuthStatus();
    });
  }

  if (deleteConfirmBtn && !deleteConfirmBtn.dataset.authBound) {
    deleteConfirmBtn.dataset.authBound = "true";
    deleteConfirmBtn.addEventListener("click", async () => {
      trackEvent("auth_delete_account_confirmed");
      deleteConfirmBtn.disabled = true;
      setAuthStatus("Deleting account...", "pending");
      try {
        const deleteResult = await deleteAccount();
        if (deleteResult?.reauthRequired) {
          persistPendingDeleteReauth();
          setAuthStatus("Confirming your identity with Google...", "pending");
          try {
            await signInWithGoogle({
              callbackURL: `${window.location.origin}${window.location.pathname}`,
            });
            return;
          } catch (reauthError) {
            clearPendingDeleteReauth();
            throw reauthError;
          }
        }
        clearPendingDeleteReauth();
        closeAuthModal();
        await forceLoggedOutState({ doSignOut: false });
        window.location.replace(`${window.location.origin}${window.location.pathname}`);
      } catch (error) {
        console.error("[AUTH] Delete account failed", error);
        setAuthStatus("Delete failed. Please try again.", "error");
      } finally {
        deleteConfirmBtn.disabled = false;
      }
    });
  }

  if (!document.body.dataset.ranksAuthOutsideClickBound) {
    document.body.dataset.ranksAuthOutsideClickBound = "true";
    document.addEventListener("click", (e) => {
      if (dropdown && !dropdown.classList.contains("open")) return;
      const avatarWrapper = document.getElementById("auth-avatar-wrapper");
      if (avatarWrapper && !avatarWrapper.contains(e.target)) {
        trackEvent("auth_dropdown_closed", { reason: "outside_click" });
        dropdown?.classList.remove("open");
      }
    });
  }

  return await handleAuthCallback();
}

export async function init() {
  log("[INIT] Starting initialization...");
  const startTime = performance.now();
  if (typeof window !== "undefined") window.__RANKS_CONTEXT_INVARIANT_FAILURES__ = 0;
  setBootingState(true);
  initThemeController();
  let authBootPromise = null;
  const startAuthBoot = () => {
    if (!authBootPromise) {
      authBootPromise = Promise.resolve(initAuth()).catch((error) => {
        console.error("[AUTH] Boot error:", error);
        return null;
      });
    }
    return authBootPromise;
  };

  try {
    perfState.enabled = detectPerfEnabled();
    const preResolved = resolveUrlStateFromLocation(
      {
        pathname: window.location.pathname,
        searchParams: new URLSearchParams(window.location.search),
      },
      getUrlResolutionOptions(state),
    );
    state.type = preResolved.type;
    state.tab = preResolved.tab;
    state.rankingMode = preResolved.rankingMode;
    state.category = preResolved.category;
    state.expertType = preResolved.expertType;
    state.search = preResolved.search;
    state.sortColumn = preResolved.sortColumn;
    state.sortDirection = preResolved.sortDirection;
    if (renderBootstrapRowsIfEligible()) setBootingState(false);
    const snapshotValidatorState = await resolveSnapshotValidatorState();
    await loadData(snapshotValidatorState.forceFreshApiContext);

    isApplyingUrlState = true;
    const resolved = resolveUrlStateFromLocation(
      {
        pathname: window.location.pathname,
        searchParams: new URLSearchParams(window.location.search),
      },
      getUrlResolutionOptions(state),
    );
    state.type = resolved.type;
    state.tab = resolved.tab;
    state.rankingMode = resolved.rankingMode;
    state.category = resolved.category;
    state.expertType = resolved.expertType;
    state.search = resolved.search;
    state.sortColumn = resolved.sortColumn;
    state.sortDirection = resolved.sortDirection;
    if (state.api.enabled) {
      await loadApiContextForCurrentState(false);
    }
    isApplyingUrlState = false;
    const categoryCssSpan = startPerfMeasure("ranks:inject-category-css", {});
    injectCategoryCssRules(); // Inject CSS for category views
    categoryCssSpan.end();
    // The API loader already built and cached allRows for this payload.
    // Re-render only if a future loader path provides no rows.
    if (state.allRows.length === 0) renderRows();
    clearFilterCache();
    // Mount the complete table structure before Clusterize measures the first
    // body rows. Measuring with an empty thead makes auto-width columns (Name)
    // collapse until a later scroll/layout refresh.
    updateColumnHeadersForMode();
    const clusterizeInitSpan = startPerfMeasure("ranks:clusterize-init", {});
    initClusterize();
    clusterizeInitSpan.end();
    setupEventListeners();
    initFeedback();
    // The category menu is closed on boot. Build it lazily when opened so the
    // large tree does not block scrolling while the first table rows hydrate.
    if (document.querySelector("#category-dropdown.open")) renderCategoryTree();
    updateViewClass();
    disableFreePaidTabs(!typeSupportsPricing(state.type));
    updateTabButtonStyles();
    updateControlDisabling();
    updateSearchFieldControls();
    updateSortIndicators();
    updateTableTitle();
    updateTableFooter();
    updateCategoryDropdownVisibility();
    const activeMeta = getActiveCategoryMeta();
    const categoryName =
      state.category && activeMeta[state.category]
        ? activeMeta[state.category].name
        : getDefaultCategoryDropdownLabel();
    updateCategoryDropdownText(categoryName);
    updateCategorySelectedState(state.category);
    renderExpertTypeDropdown();
    updateExpertTypeDropdownText();
    const searchInput = document.getElementById("rankings-search");
    if (searchInput) {
      searchInput.value = state.search || "";
      searchInput.classList.toggle("search-active", Boolean(state.search));
    }
    syncUrlWithState();
    setBootingState(false);

    // Start auth after the first table paint. Auth UI/session work can be
    // expensive; it must not block scrolling while the ranking rows hydrate.
    window.setTimeout(() => {
      const authSpan = startPerfMeasure("ranks:auth-boot", {});
      void startAuthBoot().then(() => {
        authSpan.end({ state: getAuthBootState() });
        const authBootState = getAuthBootState();
        if (authBootState === AUTH_BOOT_STATE_AUTHENTICATED) {
          const authUiSpan = startPerfMeasure("ranks:auth-ui", { state: authBootState });
          renderVisibleGatedMetrics();
          updateCsvDownloadButtonState();
          syncColumnFeatureFlags({ rerender: false });
          authUiSpan.end();
          window.setTimeout(() => {
            const authRowsSpan = startPerfMeasure("ranks:auth-rows", { state: authBootState });
            syncColumnFeatureFlags({ rerender: true });
            renderRowsForAuthState();
            scheduleEngagementLoad();
            authRowsSpan.end();
          }, 250);
        } else if (authBootState === AUTH_BOOT_STATE_LOGGED_OUT) {
          const authUiSpan = startPerfMeasure("ranks:auth-ui", { state: authBootState });
          renderLockedGatedState();
          updateCsvDownloadButtonState();
          // Bootstrap rows already contain the logged-out gated cells. Do not
          // rebuild all rows again during the first interactive paint.
          if (document.getElementById("clusterize-content")?.dataset.bootstrapRows !== "1") {
            renderRowsForAuthState();
          }
          authUiSpan.end();
        }
      });
    }, 250);

    const duration = Math.round(performance.now() - startTime);
    log(`[INIT] Initialization complete in ${duration}ms`);
  } catch (error) {
    console.error("[INIT] Error:", error);
    setBootingState(true);
    const dateEl = document.getElementById("current-date");
    if (dateEl) dateEl.textContent = "Error loading data";
    const titleMain = document.getElementById("table-title-main");
    const titleSubtitle = document.getElementById("table-title-subtitle");
    if (titleMain) titleMain.textContent = "Failed to load rankings data";
    if (titleSubtitle) titleSubtitle.textContent = "Check JSON generation and refresh.";
    setBootingState(false);
  }
}

// Export for compatibility
export {
  state,
  STATE_TO_URL_TYPE,
  handleTypeSwitch,
  handleTabSwitch,
  handleRankingModeSwitch,
  handleSearchInput,
  applyRankHighlightMetric,
  handleCategorySelect,
  handleMedalJump,
  handleCategoryJump,
  shouldCloseCategoryDropdownOnCategorySelect,
  disableFreePaidTabs,
  updateTableTitle,
  updateTableFooter,
  applyApiPayloadAndRender,
  clearApiCache,
  bindCsvDownloadButton,
  buildNoResultsRow,
  buildFooterStretchRow,
  buildCsvFilenameForCurrentView,
  buildCsvForCurrentView,
  downloadCsvForCurrentView,
  escapeCsvCell,
  formatCsvMedals,
  getOrderedRowsForCurrentView,
  updateCsvDownloadButtonState,
  normalizeCategoryDisplayName,
  normalizeApiPayloadItems,
  getUrlResolutionOptions,
  initThemeController,
  handleThemeSwitchInteraction,
  setupEventListeners,
  initFeedback,
  initAuth,
  trackEvent,
  getSimpleAnalyticsAuthState,
  buildGatedCacheKey,
  isGatedCacheEnvelopeValid,
  isCategoryChurnMetricsValid,
  formatCategoryChurnMetrics,
  formatCategoryChurnMetricsHtml,
  calculateVisibleGatedMetrics,
  renderMetricsGatedState,
};

// Expose debug info on window for console access
if (typeof window !== "undefined") {
  async function runTabSwitchBenchmark(options = {}) {
    const iterations = Math.max(3, Number(options.iterations ?? 30));
    const sequence = ["all", "free", "paid"];
    const previousPerfFlag = perfState.enabled;
    perfState.enabled = true;
    perfState.history = [];
    perfState.measures = [];

    // Ensure stable baseline.
    state.type = "template";
    state.category = null;
    state.tab = "all";
    updateViewClass();
    refresh();

    const samples = [];
    for (let i = 0; i < iterations; i++) {
      const nextTab = sequence[i % sequence.length];
      if (state.tab === nextTab) {
        const fallbackTab = sequence[(i + 1) % sequence.length];
        handleTabSwitch(fallbackTab);
      }
      const start = performance.now();
      handleTabSwitch(nextTab);
      const elapsed = performance.now() - start;
      samples.push(elapsed);
    }

    const sorted = [...samples].sort((a, b) => a - b);
    const avg = samples.reduce((sum, ms) => sum + ms, 0) / samples.length;
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
    const summary = {
      iterations: samples.length,
      avgMs: Math.round(avg * 100) / 100,
      p95Ms: Math.round(p95 * 100) / 100,
      minMs: Math.round(sorted[0] * 100) / 100,
      maxMs: Math.round(sorted[sorted.length - 1] * 100) / 100,
    };
    console.table(summary);
    console.table(perfState.history.slice(-Math.min(20, perfState.history.length)));
    perfState.enabled = previousPerfFlag;
    return { summary, samples, history: [...perfState.history] };
  }

  window.RanksDebug = {
    get state() {
      return state;
    },
    get filteredRows() {
      return state.filteredRows;
    },
    get sortedIndices() {
      return state.sortedIndices;
    },
    get allItems() {
      return state.allItems;
    },
    get allRows() {
      return state.allRows;
    },
    get clusterize() {
      return state.clusterize;
    },
    get progressiveRenderState() {
      return progressiveRenderState;
    },
    get contextInvariantFailures() {
      return window.__RANKS_CONTEXT_INVARIANT_FAILURES__ || 0;
    },
    refresh,
    buildCsvForCurrentView,
    buildCsvFilenameForCurrentView,
    downloadCsvForCurrentView,
    clearSortCache,
    clearFilterCache,
    getFilterCacheStats,
  };
  window.RanksPerf = {
    enable() {
      perfState.enabled = true;
      window.__RANKS_PERF__ = true;
    },
    disable() {
      perfState.enabled = false;
      window.__RANKS_PERF__ = false;
    },
    clear() {
      perfState.history = [];
      perfState.measures = [];
      if (typeof performance !== "undefined" && typeof performance.clearMarks === "function") {
        try {
          performance.clearMarks();
        } catch {
          // ignore user timing failures
        }
      }
      if (typeof performance !== "undefined" && typeof performance.clearMeasures === "function") {
        try {
          performance.clearMeasures();
        } catch {
          // ignore user timing failures
        }
      }
    },
    get history() {
      return perfState.history;
    },
    get measures() {
      return perfState.measures;
    },
    get last() {
      return perfState.history[perfState.history.length - 1] || null;
    },
    runTabSwitchBenchmark,
  };
  window.RanksAuth = {
    getBetterAuthSession,
    getAuthBootState,
    requestAuthBootstrap,
    requestBetterAuthSession,
    updateAuthUI,
    fetchGatedMetrics,
    retryGatedMetricsBootstrap,
    handleAuthCallback,
    getAuthConfig,
    getGatedConfig,
    getGatedEndpoint,
    inferCanonicalPreviewAuthBaseUrl,
    buildAuthReturnUrl,
    buildCanonicalAuthNavigationTarget,
    buildGoogleSignInRequest,
    signInWithGoogle,
    signOut,
    deleteAccount,
    openSettingsModal,
    forceLoggedOutState,
  };
}
